import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import ReviewerManagementFull from './ReviewerManagementFull';
import LoadingState from '../components/LoadingState';
import EmptyState from '../components/EmptyState';
import TabBar from '../components/TabBar';
import AdminSidebar from '../components/AdminSidebar';
import useNotifications from '../hooks/useNotifications';
import NotificationBell from '../components/NotificationBell';
import DeliveryForm from '../components/DeliveryForm';
import { REQUIRED_ROLES } from '../components/CastCrewEditor';
import { Field } from '../components/FormPrimitives';

const ROLE_LABEL_MAP = Object.fromEntries(REQUIRED_ROLES.map(r => [r.key, r.label]));
import {
  LayoutDashboard, BarChart2, Megaphone, Calendar, FileText,
  Users, Download, Edit3, Trash2,
  ChevronDown, ChevronUp, Lock, AlertCircle, Eye, EyeOff, Clock, Image, CheckCircle, XCircle, Copy, Folder, Search, UserCheck, ListChecks, RefreshCw, Mail, X, Plus, Truck, Star, Home, ArrowRight
} from 'lucide-react';
import API from '../utils/api';

// Fallback until the admin-editable list loads — mirrors
// frontend/src/pages/Submit.js's DEFAULT_GENRES, so "Submit on Behalf"
// offers the same options a filmmaker would see (Settings → Submission
// Genres is the actual source of truth for both, fetched at runtime).
const SUBMIT_BEHALF_GENRES = ['Drama','Thriller','Comedy','Horror','Romance','Documentary','Animation','Experimental','Action','Sci-Fi','Fantasy','AI Film','Other'];
const SUBMIT_BEHALF_LANGUAGES = ['Tamil','Hindi','Telugu','Malayalam','Kannada','English','Bengali','Marathi','Other'];
const SUBMIT_BEHALF_ALLOWED_LINK_HOSTS = ['drive.google.com', 'docs.google.com', 'youtube.com', 'www.youtube.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com'];
function isAllowedSubmitLink(url) {
  try { return SUBMIT_BEHALF_ALLOWED_LINK_HOSTS.includes(new URL(url).hostname.toLowerCase()); } catch { return false; }
}
const BLANK_BEHALF_FORM = {
  title: '', genre: '', language: '', format: 'horizontal', episode_count: '', season_number: '', duration_hours: '', duration_mins: '', duration_secs: '',
  synopsis: '', film_link: '', film_link_password: '', trailer_link: '', notes_to_reviewers: '',
  director_name: '', director_phone: '', director_email: '', is_general_submission: false,
};

// Shared between the "?" quick-reference modal and the Settings page's
// Keyboard Shortcuts card, so the two can never drift out of sync.
const SHORTCUTS_LIST = [
  ['Focus search', '/'],
  ['Toggle sidebar', 'Alt B'],
  ['Close drawer / menu', 'Esc'],
  ['Move between rows', 'J  K'],
  ['Next / previous film (drawer open)', 'J  K'],
  ['Open highlighted row', 'Enter'],
  ['Archive highlighted row', 'A'],
  ['Save review (drawer open)', 'Ctrl S'],
  ['Show this list', '?'],
];

// Manual version tracking — bump by hand each deploy (Request 19: a simple
// read-only version + changelog in Settings, not an admin-editable/DB-backed
// system, per the user's own choice when this was scoped).
const APP_VERSION = '1.11.0';
const CHANGELOG = [
  { version: '1.11.0', date: '2026-09-22', notes: [
    'SF Console link: approved films are shared with SF Console, genre changes made here are pushed to it, and the delivery QC / publish status (including scheduled releases) comes back and shows on each delivery. A new switch in Advanced Settings turns the link off at any time without affecting the rest of Gateway',
    'Series (multi-episode) films can be submitted, with an episode count and a season number',
    'New Studio field on submissions, a fixed Age Rating list, and a genre list managed on the server instead of typed by hand',
    'New Deliveries list page; filmmakers with more than one delivery no longer hit a dead end in the menu; the filmmaker dashboard shows the licensing agreement signature status of each film',
    'The submission drawer shows a small indicator on each tab that has something new; the submissions list has an entries-per-page control; My Films shows 5 per page; the old Agreement tab was removed (agreements live in SF Console)',
    'The QC and publish steps moved to SF Console; the app is now called Shortflix Gateway',
    'Fixed: a custom score category added by a reviewer was being dropped before it reached the filmmaker',
    'Fixed: scheduled release times are now stored with their time zone, so a release can no longer fire hours early or late; agreement events coming back from SF Console are recorded correctly',
    'Security: the secret keys Gateway and SF Console use to trust each other are now refused outright when missing on the server (before, a missing key let any caller in), are compared in a way that cannot be guessed by timing, and a missing key is warned about at startup',
    'Deployed from git for the first time: the server now runs from a clone of the repository instead of hand-uploaded files',
  ]},
  { version: '1.10.0', date: '2026-08-16', notes: [
    'Fixed a real bug: the reviewer review page never actually detected an already-submitted review, so Submit/Save Draft stayed clickable even after the server rejected a resubmit',
    'The 3 written-feedback boxes (What Works Well / Areas to Improve / Summary) are now optional by default — new Settings checkboxes let a super admin require any of them individually',
    'Merged the Screenplay and Engaging score categories into one ("Screenplay/Engaging"), and added a new Editing category — reviewers now score 7 categories',
  ]},
  { version: '1.9.0', date: '2026-08-16', notes: [
    'New Settings page — submission cap, auto-open, exports, and archive access moved here off the Submissions top bar',
    'Reviewer assignment cap removed — no maximum per film, 2 is just the recommended minimum',
    'Fixed the reviewer NDA-signed badge never updating after a reviewer accepted',
    'Improved dark mode text contrast and sizing across dense tables',
  ]},
  { version: '1.8.0', date: '2026-08-14', notes: [
    'Duration is now Hours/Minutes/Seconds instead of free text, with a sortable and filterable Duration column',
    'Approve now requires choosing App vs YouTube, with matching filmmaker email copy for each',
    'Decision emails are now a separate, deliberate, super-admin-only step from setting the decision itself',
  ]},
  { version: '1.7.0', date: '2026-08-13', notes: [
    '"Submit on Behalf of Filmmaker" tool for filmmakers who emailed their film in',
    'Login background support for the general/no-season submission pool',
  ]},
];

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d);
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Small hand-rolled vertical bar chart — no charting library anywhere in
// this codebase, and a monthly-trend/score-histogram bar chart doesn't need
// one. `data` is [{label, count}]. Bar height is proportional to the max
// value in the set, with a small floor so a real 0 still renders a sliver
// rather than vanishing entirely.
function MiniBarChart({ data, color = 'var(--pink)' }) {
  const max = Math.max(1, ...data.map(d => d.count));
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:110, padding:'0 2px' }}>
      {data.map(d => (
        <div key={d.label} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6, height:'100%', justifyContent:'flex-end' }}>
          <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700 }}>{d.count > 0 ? d.count : ''}</span>
          <div title={`${d.label}: ${d.count}`} style={{ width:'100%', height: d.count>0 ? Math.max(4, (d.count/max)*78) : 2, background: d.count>0 ? color : 'var(--bg-elevated)', borderRadius:'3px 3px 0 0', transition:'height 0.2s' }} />
          <span style={{ fontSize:9.5, color:'var(--text-muted)', fontWeight:600, whiteSpace:'nowrap' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function describeAction(a) {
  const after = a.after_value || {};
  const before = a.before_value || {};
  switch (a.action) {
    case 'submission.review': return `updated a submission — ${before.status || '?'} → ${after.status || '?'}, ${before.review_status || '?'} → ${after.review_status || '?'}`;
    case 'submission.bulk_review': return `bulk-set ${after.count || 0} submission(s) to "${after.status}"${after.filterWide ? ' (all matching a filter)' : ''}`;
    case 'submission.archive': return `archived "${after.title || 'a submission'}"`;
    case 'submission.restore': return `restored "${after.title || 'a submission'}"`;
    case 'submission.allow_resubmission': return `allowed resubmission for "${after.title || 'a submission'}"`;
    case 'submission.flag_correction': return `flagged "${after.title || 'a submission'}" for correction — "${after.reason || ''}"`;
    case 'season.create': return `created season "${after.name}"`;
    case 'season.activate': return `activated season "${after.name}"`;
    case 'season.deactivate': return `deactivated season "${after.name}" — submissions now go to the general pool`;
    case 'season.delete': return `deleted season "${before.name}"`;
    case 'banner.create': return `created banner "${after.title}"`;
    case 'banner.activate': return `activated banner "${after.title}"`;
    case 'banner.deactivate': return `deactivated banner "${after.title}"`;
    case 'banner.delete': return `deleted banner "${before.title}"`;
    case 'login_background.upload': return `uploaded a login background for "${after.season_name || 'a season'}"`;
    case 'login_background.update': return `updated login background settings (overlay ${after.overlay_opacity}%, focal ${after.focal_point})`;
    case 'login_background.delete': return `removed the login background for "${before.season_name || 'a season'}"`;
    case 'guidelines.update': return 'updated the guidelines page';
    case 'settings.update': return `changed setting "${a.target_id}" from "${before.value ?? '—'}" to "${after.value ?? '—'}"`;
    case 'team.add': return `added ${after.email} as ${after.role}`;
    case 'team.role_change': return `changed a team member's role from ${before.role} to ${after.role}`;
    case 'team.remove': return `removed ${before.email} from the admin team`;
    case 'agreement.sent': return `sent the licensing agreement for "${after.title || 'a submission'}" via Zoho Sign`;
    case 'agreement.manual_signed': return `recorded a manually-signed licensing agreement`;
    case 'delivery.requested': return `unlocked delivery for "${after.title || 'a submission'}"`;
    case 'delivery.requested_agreement_skipped': return `unlocked delivery for "${after.title || 'a submission'}" — skipped the agreement requirement`;
    case 'delivery.manual_submit': return `filled in delivery manually for "${after.title || 'a submission'}" (${after.delivery_method})`;
    case 'delivery.qc_pass': return 'passed a delivery QC check';
    case 'delivery.qc_fail': return `failed a delivery QC check — "${after.qc_notes || ''}"`;
    case 'delivery.publish': return `marked "${after.title || 'a submission'}" as published`;
    default: return a.action;
  }
}

function StatusBadge({ status }) {
  const map = { pending:['badge-amber','Pending',Clock], approved:['badge-green','Approved',CheckCircle], rejected:['badge-red','Not Selected',XCircle] };
  const [cls, label, Icon] = map[status] || ['','',null];
  return <span className={`badge ${cls}`} style={{ whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4 }}>{Icon && <Icon size={11} />}{label}</span>;
}

function ScoreSlider({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--pink)' }}>{value || 0}/10</span>
        </div>
      )}
      <input type="range" min={0} max={10} step={0.5} value={value || 0}
        onChange={e => onChange(e.target.value)} style={{ width: '100%' }} />
    </div>
  );
}

// Drawer tab id → label, used for the "X updated" pills in the drawer header.
// Kept in sync with the tab list rendered further down.
const DRAWER_TAB_LABELS = { details: 'Details', notes: 'Notes', reviews: 'Reviews', delivery: 'Delivery' };

// Read-only strip at the top of the Delivery tab showing where the licensing
// agreement stands in SF Console (which owns it entirely now). Informational
// only — it does not block requesting delivery. `info` comes from
// GET /api/admin/agreements/:id/sf-console-status.
function SfAgreementStatus({ info }) {
  if (!info) return null;

  let tone, label;
  if (info.onHold) {
    tone = { bg: 'rgba(255,80,80,0.08)', border: 'rgba(255,80,80,0.28)', color: 'var(--red, #ff5050)' };
    label = `Film on Processing Hold in SF Console${info.holdReason ? ` — ${info.holdReason}` : ''}`;
  } else if (info.state === 'locked') {
    tone = { bg: 'rgba(0,200,150,0.08)', border: 'rgba(0,200,150,0.28)', color: 'var(--green)' };
    label = 'Agreement locked in SF Console';
  } else if (info.state === 'pending') {
    tone = { bg: 'rgba(255,181,71,0.08)', border: 'rgba(255,181,71,0.28)', color: 'var(--amber, #ffb547)' };
    label = 'Agreement in progress in SF Console';
  } else if (info.state === 'none') {
    tone = { bg: 'var(--bg-elevated)', border: 'var(--border)', color: 'var(--text-muted)' };
    label = 'No agreement in SF Console yet';
  } else {
    tone = { bg: 'var(--bg-elevated)', border: 'var(--border)', color: 'var(--text-muted)' };
    label = 'Agreement status unavailable (SF Console unreachable)';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: tone.bg, border: `1.5px solid ${tone.border}`, borderRadius: 'var(--r-md)', padding: '9px 14px', marginBottom: 16 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone.color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: tone.color }}>{label}</span>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: 'auto' }}>Agreement · SF Console</span>
    </div>
  );
}

// Read-only strip at the top of the Delivery tab: QC + publish now happen in
// SF Console (Movie Details, editor role). `info` from
// GET /api/admin/deliveries/:id/sf-console-status.
function SfDeliveryStatus({ info }) {
  if (!info) return null;
  let tone, label;
  const S = (bg, border, color) => ({ bg, border, color });
  const fmtD = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  if (info.state === 'published') {
    tone = S('rgba(0,200,150,0.08)', 'rgba(0,200,150,0.28)', 'var(--green)');
    label = `Published in SF Console${info.publishedAt ? ` · ${fmtD(info.publishedAt)}` : ''}`;
  } else if (info.state === 'scheduled') {
    tone = S('rgba(0,200,150,0.08)', 'rgba(0,200,150,0.28)', 'var(--green)');
    label = `Release scheduled in SF Console${info.scheduledReleaseAt ? ` · ${new Date(info.scheduledReleaseAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}`;
  } else if (info.state === 'on_hold') {
    tone = S('rgba(255,181,71,0.08)', 'rgba(255,181,71,0.28)', 'var(--amber, #ffb547)');
    label = 'Release on hold in SF Console';
  } else if (info.state === 'qc_passed') {
    tone = S('rgba(0,200,150,0.08)', 'rgba(0,200,150,0.28)', 'var(--green)');
    label = 'QC passed in SF Console — awaiting publish';
  } else if (info.state === 'qc_failed') {
    tone = S('rgba(255,80,80,0.08)', 'rgba(255,80,80,0.28)', 'var(--red, #ff5050)');
    label = `QC failed in SF Console${info.qcNotes ? ` — ${info.qcNotes}` : ''}`;
  } else if (info.state === 'awaiting_qc') {
    tone = S('rgba(255,181,71,0.08)', 'rgba(255,181,71,0.28)', 'var(--amber, #ffb547)');
    label = 'Awaiting QC in SF Console';
  } else if (info.state === 'not_synced') {
    tone = S('var(--bg-elevated)', 'var(--border)', 'var(--text-muted)');
    label = 'Not synced into SF Console yet';
  } else {
    tone = S('var(--bg-elevated)', 'var(--border)', 'var(--text-muted)');
    label = 'QC status unavailable (SF Console unreachable)';
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: tone.bg, border: `1.5px solid ${tone.border}`, borderRadius: 'var(--r-md)', padding: '9px 14px', marginBottom: 16 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone.color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: tone.color }}>{label}</span>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: 'auto' }}>QC · SF Console</span>
    </div>
  );
}

// ── REVIEW DRAWER ────────────────────────────────────────────────────────────
function ReviewDrawer({ sub, onClose, onSave, onUpdated, allReviewers, allGroups, initialTab, onFilterByFilmmaker, onTabChange, onTabViewed }) {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [form, setForm] = useState({
    status: sub.status || 'pending',
    review_status: sub.review_status || 'submitted',
    score_notes: sub.score_notes || '',
    admin_notes: sub.admin_notes || '',
    admin_notes_for_reviewers: sub.admin_notes_for_reviewers || '',
    // Null means "still using the auto-merged reviewer text" — only set once
    // admin actually confirms an edit in the decision-preview modal. Carried
    // through on every save (even unrelated ones) so a normal "Save Review"
    // never silently wipes a prior edit by omitting these keys.
    edited_feedback_strengths: sub.edited_feedback_strengths || null,
    edited_feedback_improvements: sub.edited_feedback_improvements || null,
    edited_feedback_summary: sub.edited_feedback_summary || null,
    // Carried on every save (like the edited_feedback_* fields above) so a
    // routine "Save Review" on an already-approved film doesn't accidentally
    // drop the platform choice by omitting it from the payload.
    approval_platform: sub.approval_platform || null,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resubmitSent, setResubmitSent] = useState(false);
  const [correctionSent, setCorrectionSent] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [holdBusy, setHoldBusy] = useState(false);
  const [drawerTab, setDrawerTab] = useState(initialTab || 'details');
  // Per-tab "updated since you last looked" markers — seeded from the row's
  // unread_tabs, cleared as the admin actually opens each tab (mirrors the
  // server-side per-tab mark-read fired via onTabViewed).
  const [unreadTabs, setUnreadTabs] = useState(() => Array.isArray(sub.unread_tabs) ? sub.unread_tabs : []);
  const viewTab = useCallback((id) => {
    if (!unreadTabs.includes(id)) return;
    setUnreadTabs(prev => prev.filter(t => t !== id));
    onTabViewed?.(sub.id, id);
  }, [unreadTabs, sub.id, onTabViewed]);
  const goToTab = useCallback((id) => { setDrawerTab(id); onTabChange?.(id); viewTab(id); }, [onTabChange, viewTab]);
  // Clear the marker for whichever tab the drawer first lands on.
  useEffect(() => { viewTab(initialTab || 'details'); }, [viewTab, initialTab]);
  const [decisionPreview, setDecisionPreview] = useState(null); // 'approved' | 'rejected' | null
  const [previewPlatform, setPreviewPlatform] = useState(''); // 'app' | 'youtube' | ''
  const [showSendDecisionEmail, setShowSendDecisionEmail] = useState(false);
  const [sendingDecisionEmail, setSendingDecisionEmail] = useState(false);
  const [previewDraft, setPreviewDraft] = useState({ strengths:'', improvements:'', summary:'' });
  // Any edit clears the "Saved" state — it should read as saved only while
  // the form still matches what was last persisted, not just for a fixed
  // moment after clicking Save (matches how most desktop software's
  // save-state indicator behaves).
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const STAGES = ['submitted','opened','under_review','shortlisted'];
  const STAGE_LABELS = { submitted:'Submitted', opened:'Film Opened', under_review:'Under Review', shortlisted:'Shortlisted', approved:'Approved' };

  const applyStageChange = (st) => {
    setSaved(false);
    setForm(f => {
      const willBeShortlisted = st === 'shortlisted' || st === 'approved';
      return {
        ...f,
        review_status: st,
        // Decision is only meaningful once Shortlisted+. Moving a film back
        // to an earlier stage invalidates any prior Approve/Reject decision,
        // so we reset it to Pending rather than silently keeping a stale
        // decision that no longer reflects the current review state.
        status: willBeShortlisted ? f.status : 'pending',
      };
    });
  };

  // Forward-only progression: once a film is past a stage, that stage's pill
  // locks — admins can still jump ahead to any later stage, just never back.
  // 'approved' review_status sits beyond 'shortlisted' (the last pill) so it
  // locks every pill in STAGES.
  const curStageIdx = form.review_status === 'approved' ? STAGES.length : STAGES.indexOf(form.review_status);
  const isStageLocked = (st) => curStageIdx >= 0 && STAGES.indexOf(st) < curStageIdx;

  const handleStageClick = async (st) => {
    if (isStageLocked(st)) return;
    if (sub.status === 'approved' && st !== form.review_status) {
      const label = { submitted:'Submitted', opened:'Film Opened', under_review:'Under Review', shortlisted:'Shortlisted', approved:'Approved' }[st] || st;
      const ok = await confirm(`This film is already approved. Changing its stage to "${label}" will require re-approving it. Continue?`, { title: 'Change stage on an approved film?', confirmText: 'Continue' });
      if (!ok) return;
    }
    applyStageChange(st);
  };


  const [consensus, setConsensus] = useState(null);
  const [filmmakerNotifs, setFilmmakerNotifs] = useState([]);
  const [filmmakerProfile, setFilmmakerProfile] = useState(null);
  const [filmmakerSubCount, setFilmmakerSubCount] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTaskText, setNewTaskText] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [assignedReviewerNames, setAssignedReviewerNames] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [assigningId, setAssigningId] = useState('');
  const [assignMsg, setAssignMsg] = useState('');

  // Delivery — only relevant once approved. The licensing agreement itself
  // lives entirely in SF Console now; `sfAgreement` / `sfDelivery` are
  // read-only status snapshots shown at the top of their tabs for context.
  const [sfAgreement, setSfAgreement] = useState(null);
  const [sfDelivery, setSfDelivery] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [requestingDelivery, setRequestingDelivery] = useState(false);
  const [showManualFill, setShowManualFill] = useState(false);
  const [manualFillSubmitting, setManualFillSubmitting] = useState(false);
  const [exportingDelivery, setExportingDelivery] = useState('');
  const [strongCandidateThreshold, setStrongCandidateThreshold] = useState(null);
  const [rejectionTemplatesForDrawer, setRejectionTemplatesForDrawer] = useState([]);
  const [minApprovalScore, setMinApprovalScore] = useState(4.5);

  const loadReviewData = () => {
    API.get(`/api/admin/reviewers/consensus/${sub.id}`).then(r => setConsensus(r.data)).catch(() => setConsensus(null));
    API.get(`/api/admin/notifications/for-submission/${sub.id}`).then(r => setFilmmakerNotifs(r.data.items || [])).catch(() => setFilmmakerNotifs([]));
    API.get(`/api/admin/submissions/${sub.id}/tasks`).then(r => setTasks(r.data.tasks || [])).catch(() => setTasks([]));
    if (sub.user_id) {
      API.get(`/api/admin/filmmakers/${sub.user_id}/profile`).then(r => {
        setFilmmakerProfile(r.data.profile);
        setFilmmakerSubCount(r.data.submissionCount);
      }).catch(() => { setFilmmakerProfile(null); setFilmmakerSubCount(null); });
    }
    API.get('/api/admin/reviewers/assignments').then(r => {
      const mine = (r.data.assignments || []).filter(a => a.submission_id === sub.id);
      setAssignedReviewerNames(mine.map(a => ({ name: a.reviewer_name, status: a.status })));
    }).catch(() => {});
    if (sub.status === 'approved') {
      API.get(`/api/admin/agreements/${sub.id}/sf-console-status`).then(r => setSfAgreement(r.data)).catch(() => setSfAgreement(null));
      API.get(`/api/admin/deliveries/${sub.id}`).then(r => setDelivery(r.data.delivery)).catch(() => setDelivery(null));
      API.get(`/api/admin/deliveries/${sub.id}/sf-console-status`).then(r => setSfDelivery(r.data)).catch(() => setSfDelivery(null));
    }
    API.get('/api/admin/settings').then(r => {
      const v = r.data.settings?.adv_strong_candidate_threshold;
      setStrongCandidateThreshold(v ? parseFloat(v) : 7.5);
      const m = r.data.settings?.adv_min_approval_score;
      setMinApprovalScore(m ? parseFloat(m) : 4.5);
    }).catch(() => { setStrongCandidateThreshold(7.5); setMinApprovalScore(4.5); });
    API.get('/api/admin/rejection-templates').then(r => setRejectionTemplatesForDrawer(r.data.templates || [])).catch(() => {});
  };

  useEffect(() => {
    loadReviewData();
    /* eslint-disable-next-line */
  }, [sub.id]);

  const handleRequestDelivery = async () => {
    setRequestingDelivery(true);
    try {
      const res = await API.post(`/api/admin/submissions/${sub.id}/request-delivery`, {});
      setDelivery(res.data.delivery);
      showToast('Delivery requested');
    } catch (err) { showToast(err.response?.data?.error || 'Request failed', 'error'); }
    finally { setRequestingDelivery(false); }
  };

  const handleManualFillSubmit = async (formData) => {
    setManualFillSubmitting(true);
    try {
      const res = await API.post(`/api/admin/deliveries/${sub.id}/manual-submit`, formData);
      setDelivery(res.data.delivery);
      setShowManualFill(false);
      showToast('Delivery recorded');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to save delivery', 'error'); }
    finally { setManualFillSubmitting(false); }
  };

  // QC / pre-publish / publish / schedule / hold / unpublish all moved to SF
  // Console (Movie Details tab). This tab now just shows the filmmaker's
  // submission + SF Console's QC/publish status strip.

  const handleExportDelivery = async (kind) => {
    setExportingDelivery(kind);
    try {
      const url = kind === 'cast-crew' ? `/api/admin/deliveries/${sub.id}/export-cast-crew-xlsx` : `/api/admin/deliveries/${sub.id}/export-xlsx`;
      const res = await API.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${sub.title || 'film'}-${kind === 'cast-crew' ? 'cast-crew' : 'movie-details'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch { showToast('Export failed', 'error'); }
    finally { setExportingDelivery(''); }
  };

  const handleAssignEntry = async (entry) => {
    const rowId = `${entry.type}:${entry.id}`;
    setAssigningId(rowId); setAssignMsg('');
    try {
      const payload = entry.type === 'group'
        ? { submission_ids: [sub.id], group_id: entry.id }
        : { submission_ids: [sub.id], reviewer_ids: [entry.id] };
      const res = await API.post('/api/admin/reviewers/assignments/bulk', payload);
      setAssignMsg(res.data.created > 0 ? `✓ Assigned ${res.data.created} reviewer(s)` : 'No new assignments (already assigned or film is full)');
      loadReviewData();
      if (res.data.created > 0 && form.review_status === 'submitted') set('review_status', 'opened');
    } catch (err) { setAssignMsg(err.response?.data?.error || 'Assign failed'); }
    finally { setAssigningId(''); }
  };

  // LOCKED: approve/reject only available after shortlisted
  const isShortlisted = form.review_status === 'shortlisted' || form.review_status === 'approved';
  const decisionLocked = !isShortlisted;

  // Safety gates added after a real incident: a film was approved (and its
  // email auto-sent) with zero reviewer input at all. Not Selected also
  // requires at least one completed review — a decision either way should
  // be grounded in an actual review, not just admin judgment alone. The
  // score floor only applies to Approve, since a low-scoring film can still
  // legitimately be rejected without needing to clear any bar.
  const completedReviewCount = (consensus?.scores || []).filter(s => s.assignment_status === 'completed').length;
  const avgOverall = consensus?.consensus?.avg_overall != null ? parseFloat(consensus.consensus.avg_overall) : null;
  const noCompletedReviews = completedReviewCount < 1;
  const scoreBelowApprovalFloor = avgOverall === null || avgOverall < minApprovalScore;

  const handleSave = async (overrides = {}) => {
    setSaving(true);
    const payload = { ...form, ...overrides };
    try {
      const res = await API.put(`/api/admin/submissions/${sub.id}/review`, payload);
      setForm(f => ({ ...f, ...overrides }));
      setSaved(true);
      // Saving updates the film in place — it does NOT close the drawer, so
      // admin can keep working on the same film (make a correction, check
      // another tab) without having to reopen it from the list every time.
      // Only the X button closes. Merging the fresh row back into the
      // parent's `selected`/`submissions` state (rather than just refetching
      // the list) keeps `sub` itself up to date too, since gates like the
      // Agreement/Delivery tab visibility read `sub.status` directly.
      onUpdated(res.data.submission);
    } catch { showToast('Save failed', 'error'); } finally { setSaving(false); }
  };

  // Ctrl/Cmd+S is captured globally by AdminPanel (so it works regardless of
  // which element has focus) and relayed here via a custom event, since
  // handleSave/the rest of this form's state lives inside this component.
  useEffect(() => {
    const onSaveShortcut = () => handleSave();
    window.addEventListener('sf-save-review', onSaveShortcut);
    return () => window.removeEventListener('sf-save-review', onSaveShortcut);
  });

  // Request 12: "review of reviews" — merges raw per-reviewer feedback the
  // same way the backend's STRING_AGG does for the filmmaker-facing query,
  // so the preview modal's placeholder text matches what would ship as-is.
  const mergeReviewerField = (field) => (consensus?.scores || [])
    .map(s => (s[field] || '').trim()).filter(Boolean).join('\n\n');

  const handleDecisionClick = (val) => {
    if (decisionLocked) return;
    if (val === 'approved' || val === 'rejected') {
      setPreviewDraft({
        strengths: form.edited_feedback_strengths ?? mergeReviewerField('feedback_strengths'),
        improvements: form.edited_feedback_improvements ?? mergeReviewerField('feedback_improvements'),
        summary: form.edited_feedback_summary ?? mergeReviewerField('feedback_summary'),
      });
      setPreviewPlatform(form.approval_platform || '');
      setDecisionPreview(val);
    } else {
      set('status', val);
    }
  };

  const confirmDecision = async () => {
    if (decisionPreview === 'approved' && previewPlatform !== 'app' && previewPlatform !== 'youtube') {
      return showToast('Choose whether this film is approved for the app or YouTube', 'error');
    }
    await handleSave({
      status: decisionPreview,
      edited_feedback_strengths: previewDraft.strengths.trim() || null,
      edited_feedback_improvements: previewDraft.improvements.trim() || null,
      edited_feedback_summary: previewDraft.summary.trim() || null,
      approval_platform: decisionPreview === 'approved' ? previewPlatform : form.approval_platform,
    });
    setDecisionPreview(null);
  };

  const handleSendDecisionEmail = async () => {
    setSendingDecisionEmail(true);
    try {
      const res = await API.post(`/api/admin/submissions/${sub.id}/send-decision-email`);
      onUpdated(res.data.submission);
      showToast('Email sent to filmmaker');
      setShowSendDecisionEmail(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send email', 'error');
    } finally { setSendingDecisionEmail(false); }
  };

  const handleAddTask = async () => {
    const text = newTaskText.trim();
    if (!text) return;
    setAddingTask(true);
    try {
      const res = await API.post(`/api/admin/submissions/${sub.id}/tasks`, { text });
      setTasks(t => [...t, res.data.task]);
      setNewTaskText('');
    } catch { showToast('Could not add task', 'error'); }
    finally { setAddingTask(false); }
  };

  const handleToggleTask = async (taskId) => {
    setTasks(t => t.map(x => x.id === taskId ? { ...x, is_done: !x.is_done } : x));
    try {
      const res = await API.put(`/api/admin/tasks/${taskId}/toggle`);
      setTasks(t => t.map(x => x.id === taskId ? res.data.task : x));
    } catch { showToast('Could not update task', 'error'); }
  };

  const handleDeleteTask = async (taskId) => {
    setTasks(t => t.filter(x => x.id !== taskId));
    try { await API.delete(`/api/admin/tasks/${taskId}`); } catch { showToast('Could not delete task', 'error'); }
  };

  const handleClearNotes = () => {
    set('admin_notes', '');
    handleSave({ admin_notes: '' });
  };

  const handleArchive = async () => {
    const ok = await confirm(`Archive "${sub.title}"? It will be hidden from the submissions list. A super admin can restore it later.`, { title: 'Archive submission?', confirmText: 'Archive' });
    if (!ok) return;
    try {
      await API.put(`/api/admin/submissions/${sub.id}/archive`);
      onSave();
    } catch { showToast('Archive failed', 'error'); }
  };

  return (
    <>
    <div style={{ position:'fixed', inset:0, background:'var(--overlay)', zIndex:200, display:'flex', justifyContent:'flex-end' }}>
      <div style={{ width:580, background:'var(--bg-card)', display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden', boxShadow:'-8px 0 40px rgba(0,0,0,0.3)', borderLeft:'1.5px solid var(--border)', animation:'slideIn 0.25s ease' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1.5px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <h2 style={{ fontWeight:800, fontSize:18, color:'var(--text)', marginBottom:3, letterSpacing:'-0.01em' }}>{sub.title}</h2>
            <p style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500 }}>{sub.filmmaker_name} · Submitted {timeAgo(sub.created_at)}</p>
            {unreadTabs.length > 0 && (
              <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                {unreadTabs.map(t => (
                  <button key={t} onClick={() => goToTab(t)}
                    title="Updated since you last looked — click to view"
                    style={{ display:'inline-flex', alignItems:'center', gap:5, background:'var(--pink-glow)', border:'1px solid var(--pink-glow-strong)', color:'var(--pink)', borderRadius:20, padding:'3px 9px', fontSize:10, fontWeight:800, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--pink)' }} />
                    {DRAWER_TAB_LABELS[t] || t} updated
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>

        {/* Drawer tabs: Details vs Reviews */}
        <div style={{ display:'flex', borderBottom:'1.5px solid var(--border)', padding:'0 24px' }}>
          {[['details','Details'],['notes',`Notes${(tasks.filter(t=>!t.is_done).length) ? ` (${tasks.filter(t=>!t.is_done).length})` : ''}`],['reviews',`Reviews${consensus?.scores?.length ? ` (${consensus.scores.length})` : ''}`], ...(sub.status==='approved' && sub.approval_platform !== 'youtube' ? [['delivery','Delivery']] : [])].map(([id,label]) => (
            <button key={id} onClick={() => goToTab(id)}
              style={{ position:'relative', padding:'11px 14px', background:'none', color: drawerTab===id ? 'var(--pink)' : 'var(--text-secondary)', fontSize:12, fontWeight:700, border:'none', cursor:'pointer', borderBottom: drawerTab===id ? '2px solid var(--pink)' : '2px solid transparent', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
              {label}
              {unreadTabs.includes(id) && (
                <span title="Updated since you last looked"
                  style={{ position:'absolute', top:6, right:4, width:7, height:7, borderRadius:'50%', background:'var(--pink)', boxShadow:'0 0 0 2px var(--bg-card)' }} />
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto' }}>

        {drawerTab==='details' && (<>

          {/* Film info */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:12 }}>
              {[['Director',sub.director_name],['Studio',sub.studio_name],['Genre',sub.genre],['Language',sub.language],['Duration',sub.duration],['Format',sub.format ? sub.format.charAt(0).toUpperCase()+sub.format.slice(1) : ''],['Episodes',(sub.format==='series'||sub.format==='vertical') ? sub.episode_count : ''],['Season',(sub.format==='series'||sub.format==='vertical') ? sub.season_number : ''],['Phone',sub.director_phone],['Email',sub.director_email]].map(([l,v]) => v ? (
                <div key={l}><div style={{ fontSize:9, color:'var(--text-muted)', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:2 }}>{l}</div><div style={{ fontSize:12, color:'var(--text)', fontWeight:500 }}>{v}</div></div>
              ) : null)}
            </div>
            {sub.synopsis && <p style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, borderLeft:'3px solid var(--pink)', paddingLeft:12, fontWeight:400 }}>{sub.synopsis}</p>}
            {sub.notes_to_reviewers && (
              <div style={{ marginTop:10, background:'rgba(59,130,246,0.06)', border:'1.5px solid rgba(59,130,246,0.2)', borderRadius:'var(--r-md)', padding:'10px 12px', display:'flex', gap:8 }}>
                <Eye size={13} color="var(--blue)" style={{ flexShrink:0, marginTop:1 }} />
                <div>
                  <div style={{ fontSize:10, fontWeight:800, color:'var(--blue)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:2 }}>Notes from Filmmaker</div>
                  <p style={{ fontSize:12, color:'var(--text-secondary)', fontWeight:400 }}>{sub.notes_to_reviewers}</p>
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
              {sub.film_link && <a href={sub.film_link} target="_blank" rel="noopener noreferrer" style={{ background:'var(--pink-glow)', border:'1.5px solid var(--pink-glow-strong)', color:'var(--pink)', borderRadius:'var(--r-sm)', padding:'7px 14px', fontSize:11, fontWeight:800, textDecoration:'none', textTransform:'uppercase', letterSpacing:'0.06em' }}>▶ Watch Film</a>}
              {sub.film_link_password
                ? (
                  <span style={{ marginLeft:10, display:'inline-flex', alignItems:'center', gap:6, background:'var(--bg-elevated)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 10px' }}>
                    <span style={{ fontSize:10, color:'var(--text-secondary)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Password</span>
                    <span style={{ fontWeight:800, color:'var(--text)', fontFamily:'monospace', fontSize:14 }}>{sub.film_link_password}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(sub.film_link_password);
                        showToast('Password copied to clipboard', 'success');
                      }}
                      title="Copy password"
                      style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', display:'flex', alignItems:'center', padding:2 }}
                    >
                      <Copy size={14} />
                    </button>
                  </span>
                )
                : <span style={{ marginLeft:10, fontSize:11, color:'var(--text-muted)', fontStyle:'italic' }}>No link password set</span>}
              {sub.trailer_link && <a href={sub.trailer_link} target="_blank" rel="noopener noreferrer" style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', padding:'7px 14px', fontSize:11, fontWeight:700, textDecoration:'none', textTransform:'uppercase', letterSpacing:'0.06em' }}>▶ Trailer</a>}
            </div>
          </div>

          {/* Filmmaker profile — bio/city/socials from their account (never
              shown to reviewers), plus a way to see every other film this
              same email has submitted. */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div className="section-label">Filmmaker</div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
              <div style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>{sub.filmmaker_name} <span style={{ color:'var(--text-muted)', fontWeight:400 }}>· {sub.filmmaker_email}</span></div>
              {filmmakerSubCount != null && (
                <button onClick={() => onFilterByFilmmaker(sub.filmmaker_email)}
                  style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--pink)', borderRadius:'var(--r-sm)', padding:'5px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>
                  {filmmakerSubCount} film{filmmakerSubCount === 1 ? '' : 's'} by them →
                </button>
              )}
            </div>
            {filmmakerProfile && (filmmakerProfile.bio || filmmakerProfile.city || filmmakerProfile.instagram || filmmakerProfile.youtube || filmmakerProfile.vimeo || filmmakerProfile.website) ? (
              <div style={{ marginTop:10, background:'var(--bg-elevated)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', padding:'10px 12px' }}>
                {filmmakerProfile.city && <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>📍 {filmmakerProfile.city}</div>}
                {filmmakerProfile.bio && <p style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.5, fontWeight:400, marginBottom: (filmmakerProfile.instagram||filmmakerProfile.youtube||filmmakerProfile.vimeo||filmmakerProfile.website) ? 8 : 0 }}>{filmmakerProfile.bio}</p>}
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {[['Instagram',filmmakerProfile.instagram],['YouTube',filmmakerProfile.youtube],['Vimeo',filmmakerProfile.vimeo],['Website',filmmakerProfile.website]].filter(([,v]) => v).map(([label,url]) => (
                    <a key={label} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:'var(--blue)', fontWeight:600, textDecoration:'none' }}>{label} ↗</a>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ fontSize:11, color:'var(--text-muted)', fontStyle:'italic', marginTop:6 }}>No profile bio filled in yet.</p>
            )}
          </div>

          {/* Filmmaker seen-status — whether the most recent status-change
              notification sent to the filmmaker has actually been opened,
              using the same is_read flag the bell dropdown uses. */}
          {filmmakerNotifs.length > 0 && (() => {
            const latest = filmmakerNotifs[0];
            return (
              <div style={{ padding:'10px 24px', borderBottom:'1.5px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.05em', color: latest.is_read ? 'var(--green)' : 'var(--amber)' }}>
                  {latest.is_read ? '✓ Seen by filmmaker' : '○ Not yet seen'}
                </span>
                <span style={{ fontSize:11, color:'var(--text-muted)' }}>
                  {latest.title} · {timeAgo(latest.created_at)}{latest.is_read && latest.read_at ? ` · seen ${timeAgo(latest.read_at)}` : ''}
                </span>
              </div>
            );
          })()}

          {/* Review Stage */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div className="section-label">Review Stage</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {STAGES.map(st => {
                const locked = isStageLocked(st);
                return (
                  <button key={st} onClick={() => handleStageClick(st)} disabled={locked}
                    title={locked ? 'Locked — film has already progressed past this stage' : undefined}
                    style={{ padding:'6px 12px', borderRadius:20, fontSize:11, fontWeight:700, cursor: locked ? 'not-allowed' : 'pointer', fontFamily:'var(--font)', border:'1.5px solid', textTransform:'uppercase', letterSpacing:'0.04em', opacity: locked ? 0.45 : 1, background: form.review_status===st ? 'var(--pink-glow)' : 'var(--bg-elevated)', color: form.review_status===st ? 'var(--pink)' : 'var(--text-secondary)', borderColor: form.review_status===st ? 'var(--pink-glow-strong)' : 'var(--border)' }}>
                    {STAGE_LABELS[st]}
                  </button>
                );
              })}
            </div>
            {form.status !== 'pending' && (form.review_status === 'shortlisted' || form.review_status === 'approved') && (
              <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:8, fontWeight:500 }}>
                Moving this out of Shortlisted will reset its decision back to Pending.
              </p>
            )}
          </div>

          {/* Final Decision — LOCKED until shortlisted */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <div className="section-label" style={{ margin:0 }}>Final Decision</div>
              {decisionLocked && (
                <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(255,181,71,0.1)', border:'1px solid rgba(255,181,71,0.25)', borderRadius:20, padding:'2px 8px' }}>
                  <Lock size={10} color="var(--amber)" />
                  <span style={{ fontSize:9, fontWeight:800, color:'var(--amber)', letterSpacing:'0.06em', textTransform:'uppercase' }}>Set Shortlisted first</span>
                </div>
              )}
            </div>

            {decisionLocked && (
              <div style={{ display:'flex', alignItems:'flex-start', gap:8, background:'rgba(255,181,71,0.06)', border:'1.5px solid rgba(255,181,71,0.15)', borderRadius:'var(--r-md)', padding:'10px 14px', marginBottom:12 }}>
                <AlertCircle size={13} color="var(--amber)" style={{ flexShrink:0, marginTop:1 }} />
                <p style={{ fontSize:12, color:'var(--amber)', lineHeight:1.5, fontWeight:500 }}>
                  Final decision is only available after the film has been marked as <strong>Shortlisted</strong>. This prevents accidental approvals or rejections during early review stages.
                </p>
              </div>
            )}

            <div style={{ display:'flex', gap:8 }}>
              {[
                ['pending','var(--amber)','⏳ Pending', false, null],
                ['approved','var(--green)','✓ Approve', noCompletedReviews || scoreBelowApprovalFloor, noCompletedReviews ? 'Needs at least 1 completed review' : `Score must be ${minApprovalScore}+ to approve — current: ${avgOverall === null ? 'none yet' : avgOverall}`],
                ['rejected','var(--red)','✕ Not Selected', noCompletedReviews, 'Needs at least 1 completed review'],
              ].map(([val,color,label,gated,reason]) => {
                const isDisabled = decisionLocked || gated;
                return (
                <button key={val} onClick={() => handleDecisionClick(val)}
                  disabled={isDisabled} title={!decisionLocked && gated ? reason : undefined}
                  style={{ flex:1, padding:'10px', borderRadius:'var(--r-md)', fontSize:12, fontWeight:700, cursor: isDisabled ? 'not-allowed' : 'pointer', fontFamily:'var(--font)', border:`1.5px solid`, background: !isDisabled && form.status===val ? `${color}22` : 'var(--bg-elevated)', color: isDisabled ? 'var(--text-muted)' : form.status===val ? color : 'var(--text-muted)', borderColor: !isDisabled && form.status===val ? color : 'var(--border)', opacity: isDisabled ? 0.5 : 1, transition:'all 0.2s', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                  {label}
                </button>
              );})}
            </div>
            {!decisionLocked && (noCompletedReviews || scoreBelowApprovalFloor) && (
              <div style={{ display:'flex', alignItems:'flex-start', gap:8, background:'rgba(255,68,68,0.06)', border:'1.5px solid rgba(255,68,68,0.15)', borderRadius:'var(--r-md)', padding:'10px 14px', marginTop:10 }}>
                <AlertCircle size={13} color="var(--red)" style={{ flexShrink:0, marginTop:1 }} />
                <p style={{ fontSize:12, color:'var(--red)', lineHeight:1.5, fontWeight:500 }}>
                  {noCompletedReviews
                    ? 'No completed reviewer review yet — Approve and Not Selected are both disabled until at least one reviewer submits their review.'
                    : `Approve is disabled — score (${avgOverall === null ? 'none yet' : avgOverall}) is below the ${minApprovalScore} floor. Not Selected is still available.`}
                </p>
              </div>
            )}

            {/* Approved-for platform — editable after the fact (e.g. a
                YouTube-only approval later gets upgraded to the app),
                independent of re-running the whole decision flow. Changing
                it here does NOT reopen the "notify filmmaker" step — if an
                email was already sent, it stays sent; this only affects
                which pipeline (Delivery/QC tab) is offered going forward. */}
            {sub.status === 'approved' && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:'1.5px solid var(--border)' }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text-muted)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>Approved for</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[['app','The App'],['youtube','YouTube Only']].map(([val,label]) => (
                    <button key={val} onClick={() => handleSave({ approval_platform: val })} disabled={saving}
                      style={{ flex:1, padding:'8px', borderRadius:'var(--r-md)', fontSize:11, fontWeight:700, border:`1.5px solid ${form.approval_platform===val?'var(--pink)':'var(--border)'}`, background: form.approval_platform===val?'var(--pink-glow)':'var(--bg-elevated)', color: form.approval_platform===val?'var(--pink)':'var(--text-secondary)', cursor: saving ? 'not-allowed' : 'pointer', fontFamily:'var(--font)' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Notify filmmaker — deliberately separate from setting the
                decision above (see backend route comment for why). Only a
                super admin can actually send it; other admins just see the
                pending/sent state so it's never ambiguous whether the
                filmmaker has been told. */}
            {(sub.status === 'approved' || sub.status === 'rejected') && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:'1.5px solid var(--border)' }}>
                {sub.decision_email_sent_at ? (
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--green)', fontWeight:700 }}>
                    <CheckCircle size={13} /> Filmmaker notified {timeAgo(sub.decision_email_sent_at)}
                  </div>
                ) : isSuperAdmin ? (
                  <button onClick={() => setShowSendDecisionEmail(true)} className="btn-primary" style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    <Mail size={13} /> Send {sub.status === 'approved' ? 'Approval' : 'Not Selected'} Email to Filmmaker
                  </button>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--amber)', fontWeight:700 }}>
                    <Clock size={13} /> Decision set — awaiting a super admin to send the notification
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assign Reviewers */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div className="section-label">Assign Reviewers</div>
            {assignedReviewerNames.length > 0 && (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                {assignedReviewerNames.map((a,idx) => (
                  <span key={idx} className={`badge ${a.status==='completed'?'badge-green':a.status==='in_progress'?'badge-amber':''}`}
                    style={!['completed','in_progress'].includes(a.status) ? { background:'var(--bg-elevated)', color:'var(--text-muted)', border:'1px solid var(--border)' } : {}}>
                    {a.name} · {a.status.replace('_',' ')}
                  </span>
                ))}
              </div>
            )}
            <div style={{ position:'relative', marginBottom:8 }}>
              <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }} />
              <input value={assignSearch} onChange={e => setAssignSearch(e.target.value)}
                placeholder="Search groups or reviewers to assign..."
                style={{ width:'100%', fontSize:12, padding:'8px 10px 8px 30px' }} />
            </div>
            {(() => {
              const q = assignSearch.trim().toLowerCase();
              const groups = (allGroups||[]).filter(g => g.name.toLowerCase().includes(q));
              const reviewers = (allReviewers||[]).filter(r => r.name.toLowerCase().includes(q));
              if (groups.length === 0 && reviewers.length === 0) {
                return <p style={{ fontSize:12, color:'var(--text-muted)', fontStyle:'italic', padding:'4px 2px' }}>No groups or reviewers match.</p>;
              }
              return (
                <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:220, overflowY:'auto' }}>
                  {groups.map(g => {
                    const rowId = `group:${g.id}`;
                    return (
                      <div key={rowId} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderRadius:'var(--r-sm)', background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>
                        <Folder size={13} color="var(--text-muted)" style={{ flexShrink:0 }} />
                        <span style={{ fontSize:12, color:'var(--text)', fontWeight:600, flex:1 }}>{g.name} <span style={{ color:'var(--text-muted)', fontWeight:400 }}>({(g.members||[]).length})</span></span>
                        <button onClick={() => handleAssignEntry({ type:'group', id:g.id })} disabled={assigningId === rowId}
                          className="btn-primary" style={{ padding:'4px 12px', fontSize:11 }}>
                          {assigningId === rowId ? '...' : 'Assign'}
                        </button>
                      </div>
                    );
                  })}
                  {reviewers.map(r => {
                    const rowId = `reviewer:${r.id}`;
                    const already = assignedReviewerNames.some(a => a.name === r.name);
                    return (
                      <div key={rowId} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderRadius:'var(--r-sm)', background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>
                        <UserCheck size={13} color="var(--text-muted)" style={{ flexShrink:0 }} />
                        <span style={{ fontSize:12, color:'var(--text)', fontWeight:600, flex:1 }}>{r.name}</span>
                        {already
                          ? <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>✓ Assigned</span>
                          : (
                            <button onClick={() => handleAssignEntry({ type:'reviewer', id:r.id })} disabled={assigningId === rowId}
                              className="btn-primary" style={{ padding:'4px 12px', fontSize:11 }}>
                              {assigningId === rowId ? '...' : 'Assign'}
                            </button>
                          )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {assignMsg && <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:8, fontWeight:600 }}>{assignMsg}</div>}
          </div>

        </>)}

        {drawerTab==='notes' && (<>
          {/* Feedback note */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div className="section-label">Feedback to Filmmaker <span style={{ color:'var(--text-muted)', fontSize:9, fontWeight:500, textTransform:'none', letterSpacing:0 }}>(visible on their dashboard)</span></div>
            <textarea value={form.score_notes} onChange={e => set('score_notes',e.target.value)}
              placeholder="Write honest, constructive, anonymous feedback for the filmmaker..." rows={4}
              style={{ background:'rgba(0,200,150,0.04)', borderColor:'rgba(0,200,150,0.2)', color:'var(--text)', resize:'vertical', fontSize:13 }} />
          </div>

          {/* Notes for reviewers */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div className="section-label">Notes for Reviewers <span style={{ color:'var(--text-muted)', fontSize:9, fontWeight:500, textTransform:'none', letterSpacing:0 }}>(shown only to assigned reviewers, never to the filmmaker)</span></div>
            <textarea value={form.admin_notes_for_reviewers} onChange={e => set('admin_notes_for_reviewers',e.target.value)}
              placeholder="Extra context for the reviewer team — e.g. what to focus on, known technical issues with the print..." rows={3}
              style={{ background:'rgba(59,130,246,0.04)', borderColor:'rgba(59,130,246,0.2)', color:'var(--text)', resize:'vertical', fontSize:13 }} />
          </div>

          {/* Internal notes & tasks */}
          <div style={{ padding:'16px 24px', borderBottom:'1.5px solid var(--border)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div className="section-label" style={{ margin:0 }}>Internal Notes <span style={{ color:'var(--text-muted)', fontSize:9, fontWeight:500, textTransform:'none', letterSpacing:0 }}>(admin only)</span></div>
              {form.admin_notes && (
                <button onClick={handleClearNotes} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>Clear</button>
              )}
            </div>
            <textarea value={form.admin_notes} onChange={e => set('admin_notes',e.target.value)}
              placeholder="Private notes for the admin team only..." rows={3}
              style={{ background:'rgba(255,181,71,0.04)', borderColor:'rgba(255,181,71,0.2)', color:'var(--text)', resize:'vertical', fontSize:13, marginTop:8 }} />

            <div className="section-label" style={{ marginTop:16 }}>Tasks <span style={{ color:'var(--text-muted)', fontSize:9, fontWeight:500, textTransform:'none', letterSpacing:0 }}>(internal checklist, e.g. "background check on director")</span></div>
            {tasks.length > 0 && (
              <div style={{ marginBottom:8 }}>
                {tasks.map(t => (
                  <div key={t.id} style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'5px 0' }}>
                    <input type="checkbox" checked={t.is_done} onChange={() => handleToggleTask(t.id)} style={{ accentColor:'var(--pink)', marginTop:2 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, color: t.is_done ? 'var(--text-muted)' : 'var(--text)', textDecoration: t.is_done ? 'line-through' : 'none', fontWeight:500 }}>{t.text}</div>
                      <div style={{ fontSize:10, color:'var(--text-muted)' }}>
                        {t.is_done ? `Done by ${t.done_by_name || '—'} · ${timeAgo(t.done_at)}` : `Added by ${t.created_by_name || '—'} · ${timeAgo(t.created_at)}`}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteTask(t.id)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:2 }} title="Delete task">
                      <XCircle size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              <input value={newTaskText} onChange={e => setNewTaskText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddTask()}
                placeholder="Add a task, e.g. background check on director..."
                style={{ flex:1, fontSize:12, padding:'7px 10px' }} />
              <button onClick={handleAddTask} disabled={addingTask || !newTaskText.trim()} className="btn-primary" style={{ padding:'7px 14px', fontSize:11 }}>
                {addingTask ? '...' : '+ Add'}
              </button>
            </div>
          </div>

          {sub.status === 'rejected' && (
            <div style={{ padding:'16px 24px' }}>
              {resubmitSent ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(0,200,150,0.08)', border:'1.5px solid rgba(0,200,150,0.25)', borderRadius:'var(--r-md)', padding:'10px 16px', color:'var(--green)', fontSize:13, fontWeight:600 }}>
                  ✓ Resubmission email sent to filmmaker
                </div>
              ) : (
                <button onClick={async () => {
                  try {
                    await API.post(`/api/admin/submissions/${sub.id}/allow-resubmission`);
                    setResubmitSent(true);
                    setTimeout(() => setResubmitSent(false), 5000);
                  } catch(e) { showToast('Failed to allow resubmission', 'error'); }
                }}
                  style={{ background:'rgba(155,93,229,0.08)', border:'1.5px solid rgba(155,93,229,0.25)', color:'var(--purple)', borderRadius:'var(--r-md)', padding:'9px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                  Allow Resubmission →
                </button>
              )}
            </div>
          )}

          {sub.status !== 'approved' && sub.status !== 'rejected' && sub.review_status !== 'needs_correction' && (
            <div style={{ padding:'16px 24px' }}>
              <div className="section-label">Flag for Correction <span style={{ color:'var(--text-muted)', fontSize:9, fontWeight:500, textTransform:'none', letterSpacing:0 }}>(e.g. broken/private link — lets the filmmaker fix just this instead of a full reject)</span></div>
              {correctionSent ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(0,200,150,0.08)', border:'1.5px solid rgba(0,200,150,0.25)', borderRadius:'var(--r-md)', padding:'10px 16px', color:'var(--green)', fontSize:13, fontWeight:600 }}>
                  ✓ Correction request sent to filmmaker
                </div>
              ) : (
                <>
                  <textarea value={correctionReason} onChange={e => setCorrectionReason(e.target.value)}
                    placeholder="What needs to be fixed? e.g. 'Your film link is set to private — please update sharing permissions.'" rows={2}
                    style={{ background:'rgba(255,181,71,0.04)', borderColor:'rgba(255,181,71,0.2)', color:'var(--text)', resize:'vertical', fontSize:13, width:'100%', marginBottom:8 }} />
                  <button onClick={async () => {
                    if (!correctionReason.trim()) return showToast('Please describe what needs to be fixed', 'error');
                    try {
                      await API.post(`/api/admin/submissions/${sub.id}/flag-correction`, { reason: correctionReason.trim() });
                      setCorrectionSent(true);
                      setTimeout(() => setCorrectionSent(false), 5000);
                    } catch(e) { showToast('Failed to flag for correction', 'error'); }
                  }}
                    style={{ background:'rgba(255,181,71,0.08)', border:'1.5px solid rgba(255,181,71,0.25)', color:'var(--amber, #ffb547)', borderRadius:'var(--r-md)', padding:'9px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                    Flag for Correction →
                  </button>
                </>
              )}
            </div>
          )}
        {sub.status === 'approved' && (
          <div style={{ padding:'16px 24px' }}>
            <div className="section-label">Processing Hold <span style={{ color:'var(--text-muted)', fontSize:9, fontWeight:500, textTransform:'none', letterSpacing:0 }}>(deal fell through after approval — stops this film syncing into SF Console)</span></div>
            {sub.processing_hold ? (
              <div>
                <div style={{ display:'flex', alignItems:'flex-start', gap:8, background:'rgba(255,80,80,0.08)', border:'1.5px solid rgba(255,80,80,0.25)', borderRadius:'var(--r-md)', padding:'10px 16px', color:'var(--red, #ff5050)', fontSize:13, fontWeight:600, marginBottom:8 }}>
                  ⏸ On Hold — {sub.processing_hold_reason || 'no reason given'}
                </div>
                <button disabled={holdBusy} onClick={async () => {
                  setHoldBusy(true);
                  try {
                    await API.post(`/api/admin/submissions/${sub.id}/processing-hold/clear`);
                    sub.processing_hold = false; sub.processing_hold_reason = null;
                    showToast('Hold cleared', 'success');
                  } catch(e) { showToast('Failed to clear hold', 'error'); }
                  finally { setHoldBusy(false); }
                }}
                  style={{ background:'rgba(0,200,150,0.08)', border:'1.5px solid rgba(0,200,150,0.25)', color:'var(--green)', borderRadius:'var(--r-md)', padding:'9px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                  Clear Hold →
                </button>
              </div>
            ) : (
              <>
                <textarea value={holdReason} onChange={e => setHoldReason(e.target.value)}
                  placeholder="Why is this on hold? e.g. 'Filmmaker didn't agree to revenue-sharing terms.'" rows={2}
                  style={{ background:'rgba(255,80,80,0.04)', borderColor:'rgba(255,80,80,0.2)', color:'var(--text)', resize:'vertical', fontSize:13, width:'100%', marginBottom:8 }} />
                <button disabled={holdBusy} onClick={async () => {
                  if (!holdReason.trim()) return showToast('Please describe why this is on hold', 'error');
                  setHoldBusy(true);
                  try {
                    await API.post(`/api/admin/submissions/${sub.id}/processing-hold`, { reason: holdReason.trim() });
                    sub.processing_hold = true; sub.processing_hold_reason = holdReason.trim();
                    setHoldReason('');
                    showToast('Marked as Not Processing', 'success');
                  } catch(e) { showToast('Failed to mark as not processing', 'error'); }
                  finally { setHoldBusy(false); }
                }}
                  style={{ background:'rgba(255,80,80,0.08)', border:'1.5px solid rgba(255,80,80,0.25)', color:'var(--red, #ff5050)', borderRadius:'var(--r-md)', padding:'9px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                  Mark Not Processing →
                </button>
              </>
            )}
          </div>
        )}
        </>)}

        {drawerTab==='reviews' && (
          <div style={{ padding:'16px 24px' }}>
            {!consensus?.scores?.length ? (
              <p style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500 }}>No reviewer scores yet — assign reviewers on the Details tab to get started.</p>
            ) : (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                  <div style={{ fontWeight:900, fontSize:40, color:'var(--pink)', lineHeight:1 }}>
                    ★ {consensus.consensus?.avg_overall ? parseFloat(consensus.consensus.avg_overall).toFixed(1) : '—'}
                  </div>
                  <div>
                    <div style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500, marginBottom:4, display:'flex', alignItems:'center', gap:8 }}>
                      {consensus.consensus?.reviewer_count || consensus.scores.length} reviewer{(consensus.consensus?.reviewer_count || consensus.scores.length) !== 1 ? 's' : ''} scored this film
                      {strongCandidateThreshold != null && consensus.consensus?.avg_overall && parseFloat(consensus.consensus.avg_overall) >= strongCandidateThreshold && (
                        <span title={`Average score is at or above the Advanced Settings threshold (${strongCandidateThreshold})`} style={{ fontSize:9, fontWeight:800, color:'var(--amber)', background:'rgba(255,180,0,0.12)', border:'1px solid rgba(255,180,0,0.35)', borderRadius:20, padding:'2px 8px', textTransform:'uppercase' }}>⭐ Strong Candidate</span>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      {[['recommend','var(--green)',consensus.consensus?.recommend_count],['consider','var(--amber)',consensus.consensus?.consider_count],['pass','var(--red)',consensus.consensus?.pass_count]].map(([label,color,count]) => parseInt(count) > 0 && (
                        <span key={label} style={{ fontSize:10, fontWeight:800, color, background:`${color}22`, border:`1px solid ${color}55`, borderRadius:20, padding:'3px 8px', textTransform:'uppercase' }}>{label} {count}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {[['Cinematography','avg_cinematography'],['Screenplay/Engaging','avg_screenplay'],['Acting','avg_acting'],['Direction','avg_direction'],['Dialogue','avg_dialogue'],['Music','avg_music'],['Editing','avg_editing']].map(([label,key]) => {
                  const val = parseFloat(consensus.consensus?.[key]);
                  if (!val) return null;
                  return (
                    <div key={label} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:12, color:'var(--text-secondary)', fontWeight:500 }}>{label}</span>
                        <span style={{ fontSize:12, fontWeight:800, color: val>=7?'var(--green)':val>=4?'var(--amber)':'var(--red)' }}>{val}/10</span>
                      </div>
                      <div style={{ height:4, background:'var(--bg-elevated)', borderRadius:2, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${(val/10)*100}%`, background:'linear-gradient(90deg,var(--pink),var(--pink-light))', borderRadius:2 }} />
                      </div>
                    </div>
                  );
                })}

                <div className="section-label" style={{ marginTop:24, marginBottom:12 }}>Individual Reviews</div>
                {consensus.scores.map((score, i) => (
                  <div key={score.id} className="card" style={{ padding:16, marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>Reviewer {i+1}</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:400 }}>{score.submitted_at ? new Date(score.submitted_at).toLocaleDateString() : '—'}</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {score.recommendation && <span className={`badge ${score.recommendation==='recommend'?'badge-green':score.recommendation==='consider'?'badge-amber':'badge-red'}`}>{score.recommendation}</span>}
                        <span style={{ fontWeight:900, fontSize:16, color:'var(--pink)' }}>★ {parseFloat(score.score_overall).toFixed(1)}</span>
                      </div>
                    </div>
                    {score.feedback_summary && (
                      <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--r-md)', padding:'9px 12px', marginBottom:6 }}>
                        <div style={{ fontSize:9, fontWeight:800, color:'var(--text-muted)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:3 }}>Summary</div>
                        <p style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.5, fontWeight:400 }}>{score.feedback_summary}</p>
                      </div>
                    )}
                    {score.feedback_strengths && (
                      <div style={{ fontSize:12, color:'var(--green)', marginBottom:3 }}>
                        <strong>✓ Strengths:</strong> <span style={{ color:'var(--text-secondary)', fontWeight:400 }}>{score.feedback_strengths}</span>
                      </div>
                    )}
                    {score.feedback_improvements && (
                      <div style={{ fontSize:12, color:'var(--amber)' }}>
                        <strong>△ Improvements:</strong> <span style={{ color:'var(--text-secondary)', fontWeight:400 }}>{score.feedback_improvements}</span>
                      </div>
                    )}
                    {score.score_other_label && score.score_other != null && (
                      <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3 }}>
                        <strong style={{ color:'var(--text)' }}>+ {score.score_other_label}:</strong> {parseFloat(score.score_other).toFixed(1)}
                        <span style={{ fontSize:10, color:'var(--text-muted)', marginLeft:6 }}>(custom category — only the earliest reviewer's carries through to the final decision)</span>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {drawerTab==='delivery' && (
          <div style={{ padding:'16px 24px' }}>
            <SfAgreementStatus info={sfAgreement} />
            <SfDeliveryStatus info={sfDelivery} />
            {!delivery ? (
              <>
                <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>Unlock delivery so the filmmaker can submit final materials, or fill it in yourself (e.g. physical handoff, or helping them over a call).</p>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={() => handleRequestDelivery()} disabled={requestingDelivery} className="btn-primary" style={{ fontSize:11, padding:'8px 16px' }}>
                    {requestingDelivery ? 'Requesting...' : 'Request Delivery →'}
                  </button>
                  <button onClick={() => setShowManualFill(s => !s)} className="btn-ghost" style={{ fontSize:11, padding:'8px 16px' }}>
                    {showManualFill ? 'Cancel' : 'Fill In Manually →'}
                  </button>
                </div>
                {showManualFill && (
                  <div style={{ marginTop:20 }}>
                    <DeliveryForm defaultMethod="physical_media" submitting={manualFillSubmitting} submitLabel="Save & Submit →" onSubmit={handleManualFillSubmit} />
                  </div>
                )}
              </>
            ) : delivery.delivery_status==='requested' ? (
              <>
                <EmptyState icon={Clock} title="Waiting on filmmaker" subtitle={`Requested ${timeAgo(delivery.requested_at)}`} />
                <button onClick={() => setShowManualFill(s => !s)} className="btn-ghost" style={{ fontSize:11, padding:'8px 16px', marginTop:12 }}>
                  {showManualFill ? 'Cancel' : 'Fill In Manually →'}
                </button>
                {showManualFill && (
                  <div style={{ marginTop:20 }}>
                    <DeliveryForm defaultMethod="physical_media" submitting={manualFillSubmitting} submitLabel="Save & Submit →" onSubmit={handleManualFillSubmit} />
                  </div>
                )}
              </>
            ) : ['submitted','qc_failed','qc_passed','published'].includes(delivery.delivery_status) ? (
              <>
                <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                  <span className="badge" style={{ background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
                    {delivery.delivery_method==='drive_link' ? 'Drive Link' : 'Physical Media'}
                  </span>
                </div>

                <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                  <button onClick={() => handleExportDelivery('movie-details')} disabled={exportingDelivery==='movie-details'} className="btn-ghost" style={{ fontSize:11, padding:'6px 12px', display:'flex', alignItems:'center', gap:5 }}>
                    <Download size={12} /> {exportingDelivery==='movie-details' ? 'Exporting…' : 'Export Movie Details'}
                  </button>
                  <button onClick={() => handleExportDelivery('cast-crew')} disabled={exportingDelivery==='cast-crew'} className="btn-ghost" style={{ fontSize:11, padding:'6px 12px', display:'flex', alignItems:'center', gap:5 }}>
                    <Download size={12} /> {exportingDelivery==='cast-crew' ? 'Exporting…' : 'Export Cast & Crew'}
                  </button>
                </div>

                {delivery.delivery_method==='drive_link'
                  ? <p style={{ fontSize:12, marginBottom:14 }}><a href={delivery.delivery_folder_link} target="_blank" rel="noopener noreferrer" style={{ color:'var(--blue)' }}>Open Delivery Folder →</a></p>
                  : <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:14, whiteSpace:'pre-wrap' }}>{delivery.physical_delivery_notes}</p>}

                <div className="section-label" style={{ marginBottom:8 }}>What's Included</div>
                {[['Trailer File', delivery.trailer_file_declared, delivery.trailer_filename],
                  ['Movie File', delivery.movie_file_declared, delivery.movie_filename],
                  ['Poster PSD', delivery.poster_psd_declared, delivery.poster_psd_filename],
                  ['Poster PNGs', delivery.poster_png_declared, delivery.poster_png_filenames],
                  ['Banner Images', delivery.banner_declared, delivery.banner_filenames],
                  ['Screengrabs / Stills', delivery.screengrabs_declared, delivery.screengrabs_filenames],
                  ['SRT Subtitles', delivery.srt_declared, delivery.srt_filename]].map(([label, declared, filename]) => (
                  <div key={label} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, marginBottom:6 }}>
                    {declared ? <CheckCircle size={13} color="var(--green)" /> : <XCircle size={13} color="var(--text-muted)" />}
                    <span style={{ fontWeight:600, color:'var(--text)' }}>{label}</span>
                    {declared && filename && <span style={{ color:'var(--text-muted)' }}>— {filename}</span>}
                  </div>
                ))}

                <hr className="divider" style={{ margin:'16px 0' }} />
                <div className="section-label" style={{ marginBottom:8 }}>Cast & Crew</div>
                {(delivery.cast_crew_json || []).map((p, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:5, gap:8 }}>
                    <span><span style={{ fontWeight:700, color:'var(--text)' }}>{p.role==='other' ? (p.custom_role||'Other') : ROLE_LABEL_MAP[p.role] || p.role}</span> <span style={{ color:'var(--text-secondary)' }}>— {p.name}</span></span>
                    {p.social_handle && <span style={{ color:'var(--text-muted)' }}>{p.social_handle}</span>}
                  </div>
                ))}

                <hr className="divider" style={{ margin:'16px 0' }} />
                <div className="section-label" style={{ marginBottom:8 }}>Movie Details</div>
                {[['Runtime', delivery.runtime], ['Aspect Ratio', delivery.aspect_ratio],
                  ['Resolution', delivery.resolution], ['Frame Rate', delivery.frame_rate], ['Audio Format', delivery.audio_format],
                  ['Audio Channels', delivery.audio_channels], ['Content Warnings', delivery.content_warnings], ['Age Rating', delivery.age_rating],
                  ['Synopsis', delivery.delivery_synopsis], ['Audio Quality Notes', delivery.audio_quality_notes]].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label} style={{ marginBottom:8 }}>
                    <div style={{ fontSize:9, color:'var(--text-muted)', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</div>
                    <div style={{ fontSize:12, color:'var(--text)' }}>{val}</div>
                  </div>
                ))}

                {(delivery.teaser_link || delivery.trailer_link || delivery.promo_link) && (
                  <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                    {delivery.teaser_link && <a href={delivery.teaser_link} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ fontSize:11, padding:'6px 12px', textDecoration:'none' }}>Teaser</a>}
                    {delivery.trailer_link && <a href={delivery.trailer_link} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ fontSize:11, padding:'6px 12px', textDecoration:'none' }}>Trailer</a>}
                    {delivery.promo_link && <a href={delivery.promo_link} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ fontSize:11, padding:'6px 12px', textDecoration:'none' }}>Promo</a>}
                  </div>
                )}

                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:12 }}>
                  Submitted by: {delivery.submitted_by ? "Admin (on filmmaker's behalf)" : 'Filmmaker'}
                </div>

                {delivery.delivery_status==='submitted' && (
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:16 }}>
                    QC happens in SF Console (Movie Details tab) — see the status strip above.
                  </div>
                )}

                {delivery.delivery_status==='qc_failed' && (
                  <div style={{ marginTop:16 }}>
                    <button onClick={() => setShowManualFill(s => !s)} className="btn-ghost" style={{ fontSize:11, padding:'8px 16px' }}>
                      {showManualFill ? 'Cancel' : 'Fill In Manually →'}
                    </button>
                    {showManualFill && (
                      <div style={{ marginTop:16 }}>
                        <DeliveryForm initialData={delivery} defaultMethod={delivery.delivery_method} submitting={manualFillSubmitting}
                          submitLabel="Save & Resubmit →" onSubmit={handleManualFillSubmit} qcNotes={delivery.qc_notes} />
                      </div>
                    )}
                  </div>
                )}

                {delivery.delivery_status==='qc_passed' && (
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:16 }}>
                    QC passed. Release details &amp; publishing are handled in SF Console (Movie Details tab).
                  </div>
                )}

                {delivery.delivery_status==='published' && (
                  <>
                    <hr className="divider" style={{ margin:'16px 0' }} />
                    <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>Published {timeAgo(delivery.published_at)}</p>
                    <div>
                      {[['Territory', delivery.territory], ['Release Date', delivery.release_date ? new Date(delivery.release_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : ''], ['Server Folder', delivery.server_folder_name]].filter(([, v]) => v).map(([label, val]) => (
                        <div key={label} style={{ marginBottom:8 }}>
                          <div style={{ fontSize:9, color:'var(--text-muted)', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</div>
                          <div style={{ fontSize:12, color:'var(--text)' }}>{val}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:'1.5px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
          <button onClick={handleArchive} style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', padding:'9px 16px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            🗄 Archive
          </button>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={() => handleSave()} disabled={saving} className="btn-primary" style={{ minWidth:130, padding:'10px 28px', transition:'background 0.2s, border-color 0.2s, color 0.2s', ...(saved ? { background:'rgba(0,200,150,0.15)', border:'1.5px solid var(--green)', color:'var(--green)' } : {}) }}>
              {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {decisionPreview && (
      <div style={{ position:'fixed', inset:0, background:'var(--overlay)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div className="card" style={{ width:560, maxHeight:'88vh', overflowY:'auto', padding:24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <div>
              <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)' }}>Confirm decision</h3>
              <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                This sets the decision internally — nothing is sent to {sub.title}'s filmmaker yet. A super admin still needs to separately hit "Send Email" afterward to actually notify them.
                {isSuperAdmin ? ' Edit the text below if it needs cleanup.' : ' Only a super admin can edit this text.'}
              </p>
            </div>
            <button onClick={() => !saving && setDecisionPreview(null)} style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:14, flexShrink:0 }}>✕</button>
          </div>

          {decisionPreview === 'approved' && (
            <div style={{ marginTop:16 }}>
              <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>Approved for *</label>
              <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:8 }}>The email tells the filmmaker exactly which of these it is, so there's no confusion later about why an "approved" film isn't in the app.</p>
              <div style={{ display:'flex', gap:8 }}>
                {[['app','The App'],['youtube','YouTube Only']].map(([val,label]) => (
                  <button key={val} type="button" onClick={() => setPreviewPlatform(val)}
                    style={{ flex:1, padding:'10px', borderRadius:'var(--r-md)', fontSize:12, fontWeight:700, border:`1.5px solid ${previewPlatform===val?'var(--pink)':'var(--border)'}`, background: previewPlatform===val?'var(--pink-glow)':'var(--bg-elevated)', color: previewPlatform===val?'var(--pink)':'var(--text-secondary)', cursor:'pointer', fontFamily:'var(--font)' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {decisionPreview === 'rejected' && isSuperAdmin && rejectionTemplatesForDrawer.length > 0 && (
            <div style={{ marginTop:16 }}>
              <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>Use Template</label>
              <select defaultValue="" onChange={e => {
                const t = rejectionTemplatesForDrawer.find(t => t.id === e.target.value);
                if (t) setPreviewDraft(p => ({ ...p, summary: t.body }));
                e.target.value = '';
              }} style={{ fontSize:13 }}>
                <option value="" disabled>Fill Summary Note from a template…</option>
                {rejectionTemplatesForDrawer.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>Fills the Summary Note below — still fully editable before you confirm. Manage templates under Content → Rejection Templates.</p>
            </div>
          )}

          {[['Strengths','strengths'],['Improvements','improvements'],['Summary','summary']].map(([label,key]) => (
            <div key={key} style={{ marginTop:16 }}>
              <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>{label}</label>
              <textarea value={previewDraft[key]} onChange={e => isSuperAdmin && setPreviewDraft(p => ({ ...p, [key]: e.target.value }))}
                readOnly={!isSuperAdmin}
                rows={3} placeholder={`No ${label.toLowerCase()} from reviewers — leave blank or add your own`}
                style={{ width:'100%', fontSize:13, padding:'9px 12px', fontFamily:'var(--font)', resize:'vertical', ...(isSuperAdmin ? {} : { background:'var(--bg-elevated)', color:'var(--text-muted)', cursor:'not-allowed' }) }} />
            </div>
          ))}

          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
            <button onClick={() => setDecisionPreview(null)} disabled={saving} className="btn-ghost">Cancel</button>
            <button onClick={confirmDecision} disabled={saving || (decisionPreview === 'approved' && !previewPlatform)} className="btn-primary" style={{ padding:'9px 20px' }}>
              {saving ? 'Saving...' : decisionPreview === 'approved' ? '✓ Confirm & Approve' : '✕ Confirm & Not Selected'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showSendDecisionEmail && (
      <div style={{ position:'fixed', inset:0, background:'var(--overlay)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div className="card" style={{ width:520, maxHeight:'88vh', overflowY:'auto', padding:24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <div>
              <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)' }}>Send {sub.status === 'approved' ? 'Approval' : 'Not Selected'} email to {sub.director_name || sub.title}?</h3>
              <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                This is the last checkpoint — once sent, it cannot be recalled. Double-check the feedback text and the decision itself before confirming.
              </p>
            </div>
            <button onClick={() => !sendingDecisionEmail && setShowSendDecisionEmail(false)} style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:14, flexShrink:0 }}>✕</button>
          </div>

          <div style={{ marginTop:14, padding:14, background:'var(--bg-elevated)', borderRadius:'var(--r-md)', border:'1.5px solid var(--border)' }}>
            <div style={{ fontSize:11, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Decision</div>
            <div style={{ fontSize:14, fontWeight:700, color: sub.status === 'approved' ? 'var(--green)' : 'var(--red)', marginBottom:14 }}>
              {sub.status === 'approved' ? '✓ Approved' : '✕ Not Selected'} — sending to {sub.director_email}
            </div>
            {[['Strengths', sub.edited_feedback_strengths || mergeReviewerField('feedback_strengths')], ['Improvements', sub.edited_feedback_improvements || mergeReviewerField('feedback_improvements')], ['Summary', sub.edited_feedback_summary || mergeReviewerField('feedback_summary')]].filter(([,v]) => v).map(([label,val]) => (
              <div key={label} style={{ marginBottom:10 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{label}</div>
                <div style={{ fontSize:12, color:'var(--text-secondary)', whiteSpace:'pre-wrap' }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
            <button onClick={() => setShowSendDecisionEmail(false)} disabled={sendingDecisionEmail} className="btn-ghost">Cancel</button>
            <button onClick={handleSendDecisionEmail} disabled={sendingDecisionEmail} className="btn-primary" style={{ padding:'9px 20px' }}>
              {sendingDecisionEmail ? 'Sending...' : 'Yes, Send Email →'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ── REVIEWER PLACEHOLDER PAGE ────────────────────────────────────────────────
function ReviewerManagement() {
  const [reviewers, setReviewers] = useState([]);
  const [newR, setNewR] = useState({ name:'', email:'', specialty:'' });

  useEffect(() => {
    API.get('/api/admin/team').then(r => {
      // Filter for reviewer role (future — show admins for now with note)
      setReviewers(r.data.admins || []);
    }).catch(() => {});
  }, []);

  return (
    <div style={{ maxWidth: 700 }}>
      <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Reviewer Management</h2>
      <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:24, fontWeight:400 }}>
        Reviewers see only the film, title, genre, language, and synopsis — never filmmaker names, phone numbers, or emails.
      </p>

      {/* How reviewers work */}
      <div className="card" style={{ padding:20, marginBottom:24, background:'rgba(59,130,246,0.04)', borderColor:'rgba(59,130,246,0.2)' }}>
        <div style={{ fontSize:10, fontWeight:800, color:'var(--blue)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>How the Reviewer System Works</div>
        {[
          ['🎬','Blind Review','Reviewers see film link, title, genre, language, and synopsis only. Director name, phone, and email are hidden.'],
          ['⭐','Score 7 Categories','Cinematography, Screenplay/Engaging, Acting, Direction, Dialogue, Music, Editing — plus an optional custom category.'],
          ['💬','Anonymous Feedback','Written feedback is compiled anonymously. The filmmaker never knows which reviewer wrote which note.'],
          ['🔒','No Final Decision','Reviewers cannot approve or reject. Only admin can make the final call after reviewing scores.'],
        ].map(([icon, title, desc]) => (
          <div key={title} style={{ display:'flex', gap:12, marginBottom:12, alignItems:'flex-start' }}>
            <span style={{ fontSize:18, flexShrink:0 }}>{icon}</span>
            <div>
              <div style={{ fontWeight:700, fontSize:13, color:'var(--text)', marginBottom:2 }}>{title}</div>
              <div style={{ fontSize:12, color:'var(--text-muted)', fontWeight:400 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Coming soon notice */}
      <div className="card" style={{ padding:20, marginBottom:24, background:'rgba(255,45,139,0.04)', borderColor:'var(--pink-glow-strong)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <AlertCircle size={16} color="var(--pink)" />
          <span style={{ fontWeight:700, fontSize:13, color:'var(--pink)' }}>Reviewer Portal — Coming Next Phase</span>
        </div>
        <p style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, fontWeight:400 }}>
          The dedicated reviewer login and blind review interface is planned for the next development phase. Once live, you'll be able to:
        </p>
        <ul style={{ paddingLeft:20, marginTop:8, display:'flex', flexDirection:'column', gap:6 }}>
          {['Invite reviewers by email — they get a separate login','Assign specific films to specific reviewers','Set review deadlines per film','See individual vs consensus scores','Generate anonymous feedback reports for filmmakers'].map(item => (
            <li key={item} style={{ fontSize:12, color:'var(--text-muted)', fontWeight:400 }}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Add reviewer form (future-ready) */}
      <div className="card" style={{ padding:24 }}>
        <div style={{ fontWeight:800, fontSize:16, color:'var(--text)', marginBottom:4 }}>Add Reviewer (Preview)</div>
        <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:20, fontWeight:400 }}>This will send an invite when the reviewer portal launches.</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.06em' }}>Name</label>
            <input value={newR.name} onChange={e => setNewR(f => ({...f,name:e.target.value}))} placeholder="Reviewer's name" />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.06em' }}>Email</label>
            <input value={newR.email} onChange={e => setNewR(f => ({...f,email:e.target.value}))} placeholder="reviewer@email.com" type="email" />
          </div>
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', fontSize:11, fontWeight:700, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.06em' }}>Specialty (optional)</label>
          <input value={newR.specialty} onChange={e => setNewR(f => ({...f,specialty:e.target.value}))} placeholder="e.g. Cinematography expert, Drama specialist" />
        </div>
        <button className="btn-primary" style={{ opacity:0.6, cursor:'not-allowed' }} disabled>
          Invite Reviewer (Coming Soon)
        </button>
      </div>
    </div>
  );
}

// ── BAR CHART ────────────────────────────────────────────────────────────────
function BarChart({ title, data, keyField }) {
  const max = Math.max(...(data||[]).map(d => parseInt(d.count)), 1);
  return (
    <div className="card" style={{ padding:24 }}>
      <div className="section-label" style={{ marginBottom:16 }}>{title}</div>
      {(data||[]).slice(0,8).map(d => (
        <div key={d[keyField]} style={{ marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
            <span style={{ color:'var(--text)', fontWeight:500 }}>{d[keyField]}</span>
            <span style={{ fontWeight:800, color:'var(--pink)' }}>{d.count}</span>
          </div>
          <div style={{ height:4, background:'var(--bg-elevated)', borderRadius:2, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${(parseInt(d.count)/max)*100}%`, background:'linear-gradient(90deg,var(--pink),var(--pink-light))', borderRadius:2, transition:'width 0.6s ease' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MAIN PANEL ───────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const { user } = useAuth();
  const { highContrast, toggleHighContrast } = useTheme();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState('dashboard');
  const [contentSubTab, setContentSubTab] = useState('banners');
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(null);
  const [pendingDrawerTab, setPendingDrawerTab] = useState('details');
  const adminNotifications = useNotifications({ client: API, basePath: '/api/admin/notifications' });
  const [filters, setFilters] = useState({ search:'', genre:'', language:'', status:'', dateFrom:'', dateTo:'', filmmakerDeleted:'', season:'', duration:'' });
  const [bulkIds, setBulkIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('approved');
  const [bulkApprovalPlatform, setBulkApprovalPlatform] = useState('app');
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  // Per-row checkboxes are hidden by default (Gmail-style) — only the header
  // checkbox is always visible; clicking it enters "selection mode" and
  // reveals individual row checkboxes so admin can fine-tune the selection.
  const [selectionMode, setSelectionMode] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
  const [analytics, setAnalytics] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loginBackgrounds, setLoginBackgrounds] = useState([]);
  const [guidelines, setGuidelines] = useState('');
  const [rejectionTemplates, setRejectionTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ label:'', body:'' });
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateDraft, setEditingTemplateDraft] = useState({ label:'', body:'' });
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [team, setTeam] = useState([]);
  const [limit, setLimit] = useState('200');
  const [editLimit, setEditLimit] = useState(false);
  const [exportRange, setExportRange] = useState('all');
  const [exportCustomFrom, setExportCustomFrom] = useState('');
  const [exportCustomTo, setExportCustomTo] = useState('');
  const [limitEnabled, setLimitEnabled] = useState(true);
  const [autoOpenHours, setAutoOpenHours] = useState('1');
  const [autoOpenEnabled, setAutoOpenEnabled] = useState(true);
  // Submission Genres — admin-editable list (settings.submission_genres,
  // JSON array), used both by the filmmaker Submit form and "Submit on
  // Behalf" below. newGenre is the text box for adding one more.
  const [genreOptions, setGenreOptions] = useState(SUBMIT_BEHALF_GENRES);
  const [newGenre, setNewGenre] = useState('');
  // Which of the 3 reviewer written-feedback boxes are required — default
  // unchecked/optional until an admin turns one on (Settings page).
  const [requireStrengths, setRequireStrengths] = useState(false);
  const [requireImprovements, setRequireImprovements] = useState(false);
  const [requireSummary, setRequireSummary] = useState(false);
  // Advanced Settings (Request 21, first batch) — all reuse the generic
  // settings table; defaults here match the code fallback used wherever
  // each value is actually consumed, so an unset key looks identical to
  // its default until an admin explicitly changes it.
  const [advScoreThreshold, setAdvScoreThreshold] = useState('7.5');
  const [advMinApprovalScore, setAdvMinApprovalScore] = useState('4.5');
  const [advNotifySubmission, setAdvNotifySubmission] = useState(true);
  const [advNotifyCorrection, setAdvNotifyCorrection] = useState(true);
  const [advNotifyDeliveryQc, setAdvNotifyDeliveryQc] = useState(true);
  const [advNotifyRelease, setAdvNotifyRelease] = useState(true);
  const [advSfConsoleSyncEnabled, setAdvSfConsoleSyncEnabled] = useState(true);
  const [advLogRetentionMonths, setAdvLogRetentionMonths] = useState('24');
  const [advSessionExpiryDays, setAdvSessionExpiryDays] = useState('7');
  const [advMagicLinkMinutes, setAdvMagicLinkMinutes] = useState('15');
  const [advLockoutAttempts, setAdvLockoutAttempts] = useState('5');
  const [advLockoutMinutes, setAdvLockoutMinutes] = useState('15');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [sort, setSort] = useState({ by: 'date', dir: 'desc' });
  const [showSubmitBehalf, setShowSubmitBehalf] = useState(false);
  const [behalfForm, setBehalfForm] = useState(BLANK_BEHALF_FORM);
  const [behalfSubmitting, setBehalfSubmitting] = useState(false);
  const setBehalf = (k, v) => setBehalfForm(f => ({ ...f, [k]: v }));
  const [showHiddenStats, setShowHiddenStats] = useState(false);
  const [newSeason, setNewSeason] = useState({ name:'', start_date:'', end_date:'' });
  const [newBanner, setNewBanner] = useState({ title:'', subtitle:'', cta_text:'', cta_link:'', bg_color:'#FF2D8B', text_color:'#ffffff' });
  const [newBg, setNewBg] = useState({ season_id:'', overlay_opacity:50, focal_point:'center', overlay_style:'gradient', file:null, previewUrl:null });
  const [bgModalSeasonId, setBgModalSeasonId] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);
  const [newMember, setNewMember] = useState({ name:'', email:'' });
  const [quickAssignReviewers, setQuickAssignReviewers] = useState([]);
  const [quickAssignGroups, setQuickAssignGroups] = useState([]);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, submissionId }
  const [quickAssignMsg, setQuickAssignMsg] = useState('');
  const [contextMenuAddingTask, setContextMenuAddingTask] = useState(false);
  const [contextMenuTaskText, setContextMenuTaskText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [refreshing, setRefreshing] = useState(false);
  const prevUnreadCountRef = useRef(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const searchInputRef = useRef(null);
  const [topSearchOpen, setTopSearchOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [teamsSubTab, setTeamsSubTab] = useState('reviewer');
  const [settingsSubTab, setSettingsSubTab] = useState('settings');
  const [dashboardData, setDashboardData] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [reviewsFilms, setReviewsFilms] = useState([]);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsPagination, setReviewsPagination] = useState({ total: 0, totalPages: 1 });
  const [reviewsSearch, setReviewsSearch] = useState('');
  const [reviewsReadyCount, setReviewsReadyCount] = useState(0);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [deliveriesPagination, setDeliveriesPagination] = useState({ total: 0 });
  const [deliveriesStatusFilter, setDeliveriesStatusFilter] = useState('');
  const [deliveriesSearch, setDeliveriesSearch] = useState('');
  const [deliveriesNeedingAttention, setDeliveriesNeedingAttention] = useState(0);
  const [activity, setActivity] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityCategory, setActivityCategory] = useState('');
  const [clearOldDays, setClearOldDays] = useState('90');
  const [allTasks, setAllTasks] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [tasksFilter, setTasksFilter] = useState('open');
  const [notesLoading, setNotesLoading] = useState(false);
  const [clearingOld, setClearingOld] = useState(false);
  const [activityPagination, setActivityPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
  const isSuperAdmin = user?.role === 'super_admin';

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.search) p.append('search', filters.search);
      if (filters.genre) p.append('genre', filters.genre);
      if (filters.language) p.append('language', filters.language);
      if (filters.status) p.append('status', filters.status);
      if (filters.dateFrom) p.append('date_from', filters.dateFrom);
      if (filters.dateTo) p.append('date_to', filters.dateTo);
      if (filters.filmmakerDeleted) p.append('filmmaker_deleted', filters.filmmakerDeleted);
      if (filters.season) p.append('season', filters.season);
      if (filters.duration) p.append('duration_bucket', filters.duration);
      if (sort.by === 'duration') { p.append('sort', 'duration'); p.append('sort_dir', sort.dir); }
      p.append('page', page);
      p.append('limit', pageSize);
      const endpoint = showArchived ? '/api/admin/submissions/archived' : '/api/admin/submissions';
      const r = await API.get(`${endpoint}?${p}`);
      setSubmissions(r.data.submissions);
      if (r.data.stats) setStats(r.data.stats);
      if (r.data.pagination) setPagination(r.data.pagination);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [filters, showArchived, page, pageSize, sort]);

  // Reset to page 1 whenever filters, the archived toggle, or the page size
  // change, and drop any bulk selection since the underlying result set (or
  // its page boundaries) just changed.
  useEffect(() => { setPage(1); setBulkIds([]); setSelectAllMatching(false); }, [filters, showArchived, pageSize]);
  useEffect(() => { setHighlightedIndex(-1); }, [submissions]);

  const handleSubmitBehalf = async (e) => {
    e.preventDefault();
    const required = { title: 'Film title', genre: 'Genre', language: 'Language', synopsis: 'Synopsis', film_link: 'Film link', director_name: 'Director name', director_phone: 'Phone number', director_email: 'Contact email' };
    const missing = Object.entries(required).filter(([k]) => !behalfForm[k] || !String(behalfForm[k]).trim()).map(([, label]) => label);
    const totalSeconds = (parseInt(behalfForm.duration_hours,10)||0)*3600 + (parseInt(behalfForm.duration_mins,10)||0)*60 + (parseInt(behalfForm.duration_secs,10)||0);
    if (totalSeconds <= 0) missing.push('Duration');
    if ((behalfForm.format === 'series' || behalfForm.format === 'vertical') && !(parseInt(behalfForm.episode_count, 10) > 0)) missing.push('Number of Episodes');
    if (missing.length > 0) return showToast(`Missing required fields: ${missing.join(', ')}`, 'error');
    if (!isAllowedSubmitLink(behalfForm.film_link)) return showToast('Film link must be a Google Drive, YouTube, or Vimeo link', 'error');
    if (behalfForm.trailer_link && !isAllowedSubmitLink(behalfForm.trailer_link)) return showToast('Trailer link must be a Google Drive, YouTube, or Vimeo link', 'error');
    setBehalfSubmitting(true);
    try {
      await API.post('/api/admin/submissions/submit-behalf', behalfForm);
      showToast('Submission entered and confirmation email sent');
      setShowSubmitBehalf(false);
      setBehalfForm(BLANK_BEHALF_FORM);
      loadSubmissions();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to enter submission', 'error');
    } finally { setBehalfSubmitting(false); }
  };

  const handleManualRefresh = () => {
    setRefreshing(true);
    loadSubmissions().finally(() => setTimeout(() => setRefreshing(false), 400));
  };

  // No live push (websockets) yet — this piggybacks on the notification
  // bell's existing 30s poll instead: whenever the admin's unread count goes
  // UP (a new submission/delivery notification just landed), silently
  // re-fetch the list so a new film shows up without the admin having to
  // reload the browser. Only acts on increases, never on drops (which just
  // mean something got marked read, not that the list is stale).
  useEffect(() => {
    if (tab !== 'submissions' || showArchived) { prevUnreadCountRef.current = adminNotifications.unreadCount; return; }
    if (adminNotifications.unreadCount > prevUnreadCountRef.current) loadSubmissions();
    prevUnreadCountRef.current = adminNotifications.unreadCount;
  }, [adminNotifications.unreadCount, tab, showArchived, loadSubmissions]);

  const handleRestore = async (id) => {
    await API.put(`/api/admin/submissions/${id}/restore`);
    loadSubmissions();
  };

  const handleClearOldActivity = async () => {
    const days = parseInt(clearOldDays);
    if (!days || days < 1) { showToast('Enter a valid number of days', 'error'); return; }
    const ok = await confirm(`Permanently delete every activity log entry older than ${days} days? This cannot be undone.`, { title: 'Clear old activity?', confirmText: 'Delete', danger: true });
    if (!ok) return;
    setClearingOld(true);
    try {
      const res = await API.delete('/api/admin/activity/clear-old', { data: { days } });
      showToast(`Deleted ${res.data.deleted} old activity record${res.data.deleted === 1 ? '' : 's'}`);
      setActivityPage(1);
      const p = new URLSearchParams({ page: 1, limit: 50 });
      if (activityCategory) p.append('category', activityCategory);
      API.get(`/api/admin/activity?${p}`).then(r => { setActivity(r.data.actions); setActivityPagination(r.data.pagination); });
    } catch (err) { showToast(err.response?.data?.error || 'Failed to clear old activity', 'error'); }
    finally { setClearingOld(false); }
  };

  // Clears the "updated" marker for one drawer tab of one film — fired by the
  // ReviewDrawer as the admin actually opens each tab (on mount for the
  // landing tab, then on every tab switch). Fire-and-forget; optimistically
  // updates the row so its bold/tint and remaining tab dots reflect the
  // change immediately rather than waiting for the next 30s poll.
  const markTabViewed = useCallback((submissionId, tab) => {
    API.put(`/api/admin/notifications/mark-read-for-submission/${submissionId}`, { tab }).catch(() => {});
    const drop = (arr) => (Array.isArray(arr) ? arr.filter(t => t !== tab) : []);
    setSubmissions(prev => prev.map(s => {
      if (s.id !== submissionId) return s;
      const unread_tabs = drop(s.unread_tabs);
      return { ...s, unread_tabs, has_unread_notification: unread_tabs.length > 0 };
    }));
    setSelected(sel => {
      if (!sel || sel.id !== submissionId) return sel;
      const unread_tabs = drop(sel.unread_tabs);
      return { ...sel, unread_tabs, has_unread_notification: unread_tabs.length > 0 };
    });
  }, []);

  const openSubmission = (sub, drawerTab = 'details') => {
    setPendingDrawerTab(drawerTab);
    setSelected(sub);
    // Per-tab marking is handled inside ReviewDrawer (mount + tab switches).
  };

  // Jumps from a film's drawer to the full submissions list filtered to
  // everything else that same email address has ever submitted — reuses
  // the existing title/email/director search rather than a new endpoint.
  const filterByFilmmaker = (email) => {
    setSelected(null);
    setFilters(f => ({ ...f, search: email }));
    setTab('submissions');
  };

  // Notification bell click-through — the target film may not be in the
  // currently loaded/filtered page, so fall back to a direct fetch.
  const openNotification = async (n) => {
    if (!n.submission_id) return;
    setTab('submissions');
    const existing = submissions.find(s => s.id === n.submission_id);
    if (existing) { openSubmission(existing, n.link_tab || 'details'); return; }
    try {
      const res = await API.get(`/api/admin/submissions/${n.submission_id}`);
      openSubmission(res.data.submission, n.link_tab || 'details');
    } catch { showToast('Could not open that film', 'error'); }
  };

  useEffect(() => { if (tab==='submissions') loadSubmissions(); }, [tab, loadSubmissions]);
  useEffect(() => { if (tab==='analytics') API.get('/api/admin/analytics').then(r => setAnalytics(r.data)); }, [tab]);
  useEffect(() => { if (tab==='content' || tab==='submissions') API.get('/api/admin/seasons').then(r => setSeasons(r.data.seasons)); }, [tab]);
  useEffect(() => { if (tab==='content') API.get('/api/admin/banners').then(r => setBanners(r.data.banners)); }, [tab]);
  useEffect(() => { if (tab==='content') API.get('/api/admin/login-backgrounds').then(r => setLoginBackgrounds(r.data.backgrounds)); }, [tab]);
  useEffect(() => { if (tab==='content') API.get('/api/admin/guidelines').then(r => setGuidelines(r.data.guidelines?.content||'')); }, [tab]);
  const loadRejectionTemplates = () => API.get('/api/admin/rejection-templates').then(r => setRejectionTemplates(r.data.templates || [])).catch(() => {});
  useEffect(() => { if (tab==='content') loadRejectionTemplates(); }, [tab]);

  const loadNotesAndTasks = useCallback(() => {
    if (tab !== 'notes') return;
    setNotesLoading(true);
    Promise.all([
      API.get(`/api/admin/tasks?status=${tasksFilter}`),
      API.get('/api/admin/notes'),
    ]).then(([t, n]) => { setAllTasks(t.data.tasks); setAllNotes(n.data.notes); })
      .catch(() => showToast('Could not load notes & tasks', 'error'))
      .finally(() => setNotesLoading(false));
  }, [tab, tasksFilter, showToast]);

  useEffect(() => { loadNotesAndTasks(); }, [loadNotesAndTasks]);

  // "Needing attention" badge (delivery_status='submitted' — filmmaker has
  // uploaded materials and it's awaiting QC) — fetched once on mount, cheap
  // since it only asks for the count via limit=1, matching the pattern of
  // the Submissions tab's own unread badge.
  useEffect(() => {
    API.get('/api/admin/deliveries?status=submitted&limit=1').then(r => {
      setDeliveriesNeedingAttention(r.data.pagination?.total || 0);
    }).catch(() => {});
  }, []);

  // Reuses the pending-films endpoint built for Reviewer Management (Request
  // 17) — it already returns each film's assignments inline (status per
  // reviewer), which is exactly what's needed to compute "ready for
  // decision" (every assigned reviewer completed, nobody's decided yet) vs
  // "in progress" vs "unassigned" client-side, with no new backend route.
  const loadReviews = useCallback(() => {
    const p = new URLSearchParams({ page: reviewsPage, limit: 50 });
    if (reviewsSearch) p.append('search', reviewsSearch);
    return API.get(`/api/admin/reviewers/pending-films?${p}`).then(r => {
      setReviewsFilms(r.data.films || []);
      if (r.data.pagination) setReviewsPagination(r.data.pagination);
    }).catch(() => showToast('Could not load reviews', 'error'));
  }, [reviewsPage, reviewsSearch, showToast]);
  useEffect(() => { if (tab === 'reviews') loadReviews(); }, [tab, loadReviews]);
  useEffect(() => { setReviewsPage(1); }, [reviewsSearch]);

  // "Ready for decision" badge — same documented-200 trade-off already used
  // elsewhere in this codebase (see allPendingForSelect in Reviewer
  // Management) rather than a dedicated count endpoint for one badge.
  useEffect(() => {
    API.get('/api/admin/reviewers/pending-films?limit=200').then(r => {
      const ready = (r.data.films || []).filter(f => f.assignments.length > 0 && f.assignments.every(a => a.status === 'completed')).length;
      setReviewsReadyCount(ready);
    }).catch(() => {});
  }, []);

  const loadDeliveries = useCallback(() => {
    const p = new URLSearchParams({ page: deliveriesPage, limit: 50 });
    if (deliveriesStatusFilter) p.append('status', deliveriesStatusFilter);
    if (deliveriesSearch.trim()) p.append('search', deliveriesSearch.trim());
    return API.get(`/api/admin/deliveries?${p}`).then(r => {
      setDeliveries(r.data.deliveries || []);
      if (r.data.pagination) setDeliveriesPagination(r.data.pagination);
    }).catch(() => showToast('Could not load deliveries', 'error'));
  }, [deliveriesPage, deliveriesStatusFilter, deliveriesSearch, showToast]);
  useEffect(() => { if (tab === 'deliveries') loadDeliveries(); }, [tab, loadDeliveries]);
  useEffect(() => { setDeliveriesPage(1); }, [deliveriesStatusFilter, deliveriesSearch]);

  const handleToggleTaskFromList = async (taskId) => {
    setAllTasks(t => t.map(x => x.id === taskId ? { ...x, is_done: !x.is_done } : x));
    try { await API.put(`/api/admin/tasks/${taskId}/toggle`); loadNotesAndTasks(); }
    catch { showToast('Could not update task', 'error'); }
  };

  // Jump to a film's drawer from the aggregated Notes & Tasks list — same
  // approach as opening from a notification (openSubmission expects a full
  // row from the currently loaded `submissions` list, which this tab
  // doesn't share, so fetch it standalone).
  // `returnTab` defaults to 'submissions' (the original behavior, for
  // notification/Notes & Tasks jumps) but stays on whichever tab the caller
  // was actually on when it makes more sense to land back there once the
  // drawer closes — e.g. Deliveries, where forcing a switch to Submissions
  // meant re-navigating back to Deliveries before every single film click.
  const openSubmissionById = async (id, drawerTab = 'details', returnTab = 'submissions') => {
    setTab(returnTab);
    try {
      const res = await API.get(`/api/admin/submissions/${id}`);
      setPendingDrawerTab(drawerTab);
      setSelected(res.data.submission);
    } catch { showToast('Could not open that film', 'error'); }
  };
  useEffect(() => { if (tab==='teams' && isSuperAdmin) API.get('/api/admin/team').then(r => setTeam(r.data.admins)); }, [tab, isSuperAdmin]);
  useEffect(() => { if (tab==='dashboard') API.get('/api/admin/dashboard').then(r => setDashboardData(r.data)).catch(() => {}); }, [tab]);

  // Global search — debounced, min 2 chars, cancels a stale in-flight
  // request if the query changes again before it resolves.
  useEffect(() => {
    if (globalSearch.trim().length < 2) { setGlobalSearchResults(null); return; }
    let cancelled = false;
    const handle = setTimeout(() => {
      API.get(`/api/admin/search?q=${encodeURIComponent(globalSearch.trim())}`).then(r => {
        if (!cancelled) setGlobalSearchResults(r.data);
      }).catch(() => {});
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [globalSearch]);
  useEffect(() => {
    if (tab!=='settings' || settingsSubTab!=='activity') return;
    const p = new URLSearchParams({ page: activityPage, limit: 50 });
    if (activityCategory) p.append('category', activityCategory);
    API.get(`/api/admin/activity?${p}`).then(r => {
      setActivity(r.data.actions);
      if (r.data.pagination) setActivityPagination(r.data.pagination);
    });
  }, [tab, settingsSubTab, activityPage, activityCategory]);
  // Reset to page 1 whenever the category filter changes.
  useEffect(() => { setActivityPage(1); }, [activityCategory]);
  useEffect(() => {
    API.get('/api/admin/settings').then(r => {
      setLimit(r.data.settings?.submission_limit||'200');
      setLimitEnabled(r.data.settings?.submission_limit_enabled !== 'false');
      setAutoOpenHours(r.data.settings?.auto_open_hours||'1');
      setAutoOpenEnabled(r.data.settings?.auto_open_enabled !== 'false');
      setRequireStrengths(r.data.settings?.require_feedback_strengths === 'true');
      setRequireImprovements(r.data.settings?.require_feedback_improvements === 'true');
      setRequireSummary(r.data.settings?.require_feedback_summary === 'true');
      setAdvScoreThreshold(r.data.settings?.adv_strong_candidate_threshold || '7.5');
      setAdvMinApprovalScore(r.data.settings?.adv_min_approval_score || '4.5');
      setAdvNotifySubmission(r.data.settings?.notify_submission_confirmation !== 'false');
      setAdvNotifyCorrection(r.data.settings?.notify_correction_email !== 'false');
      setAdvNotifyDeliveryQc(r.data.settings?.notify_delivery_qc_email !== 'false');
      setAdvNotifyRelease(r.data.settings?.notify_release_email !== 'false');
      setAdvLogRetentionMonths(r.data.settings?.reviewer_log_retention_months || '24');
      setAdvSessionExpiryDays(r.data.settings?.session_expiry_days || '7');
      setAdvMagicLinkMinutes(r.data.settings?.magic_link_expiry_minutes || '15');
      setAdvLockoutAttempts(r.data.settings?.reviewer_lockout_attempts || '5');
      setAdvLockoutMinutes(r.data.settings?.reviewer_lockout_minutes || '15');
      setAdvSfConsoleSyncEnabled(r.data.settings?.sf_console_sync_enabled !== 'false');
      if (r.data.settings?.submission_genres) {
        try { const parsed = JSON.parse(r.data.settings.submission_genres); if (Array.isArray(parsed) && parsed.length) setGenreOptions(parsed); } catch {}
      }
    }).catch(()=>{});
    API.get('/api/admin/reviewers').then(r => setQuickAssignReviewers((r.data.reviewers||[]).filter(rv => rv.is_active))).catch(()=>{});
    API.get('/api/admin/reviewers/groups').then(r => setQuickAssignGroups(r.data.groups||[])).catch(()=>{});
  }, []);

  useEffect(() => {
    const closeMenu = () => { setContextMenu(null); setContextMenuAddingTask(false); setContextMenuTaskText(''); };
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  // Keyboard shortcuts — scoped to the Submissions tab, and ignored while
  // typing in any input/textarea/select so letters like J/K/A don't hijack
  // normal typing (e.g. inside a note or a task text box).
  useEffect(() => {
    const handler = (e) => {
      // Sidebar toggle works on every tab (the sidebar itself is always
      // visible), so this check runs before the Submissions-only gate below.
      if (e.altKey && e.key.toLowerCase() === 'b') {
        const el = document.activeElement;
        if (el && (['INPUT','TEXTAREA','SELECT'].includes(el.tagName) || el.isContentEditable)) return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('sf-toggle-sidebar'));
        return;
      }
      if (tab !== 'submissions') return;
      const el = document.activeElement;
      const typing = el && (['INPUT','TEXTAREA','SELECT'].includes(el.tagName) || el.isContentEditable);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's' && selected) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('sf-save-review'));
        return;
      }
      if (typing) {
        if (e.key === 'Escape') el.blur();
        return;
      }

      if (e.key === '/') { e.preventDefault(); setTopSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 0); return; }
      if (e.key === '?') { e.preventDefault(); setShowShortcuts(s => !s); return; }
      if (e.key === 'Escape') {
        if (contextMenu) setContextMenu(null);
        else if (showShortcuts) setShowShortcuts(false);
        else if (selected) setSelected(null);
        return;
      }
      if (selected) {
        if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') { e.preventDefault(); navigateFilm('next'); }
        else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') { e.preventDefault(); navigateFilm('prev'); }
        return;
      }
      if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(i => Math.min(submissions.length - 1, i + 1)); }
      else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(i => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') { if (highlightedIndex >= 0 && submissions[highlightedIndex]) openSubmission(submissions[highlightedIndex]); }
      else if (e.key === 'a' || e.key === 'A') { if (highlightedIndex >= 0) handleArchiveHighlighted(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const handleQuickArchive = async (submissionId, title) => {
    const ok = await confirm(`Archive "${title}"? It will be hidden from the submissions list. A super admin can restore it later.`, { title: 'Archive submission?', confirmText: 'Archive' });
    if (!ok) { setContextMenu(null); return; }
    try {
      await API.put(`/api/admin/submissions/${submissionId}/archive`);
      setQuickAssignMsg('✓ Archived');
      setTimeout(() => setQuickAssignMsg(''), 2500);
      loadSubmissions();
    } catch { setQuickAssignMsg('Archive failed'); setTimeout(() => setQuickAssignMsg(''), 2500); }
    setContextMenu(null);
  };

  const handleQuickResubmission = async (submissionId) => {
    try {
      await API.post(`/api/admin/submissions/${submissionId}/allow-resubmission`);
      setQuickAssignMsg('✓ Resubmission email sent');
      setTimeout(() => setQuickAssignMsg(''), 2500);
    } catch { setQuickAssignMsg('Failed to allow resubmission'); setTimeout(() => setQuickAssignMsg(''), 2500); }
    setContextMenu(null);
  };

  const handleCopyFilmLink = (link) => {
    navigator.clipboard.writeText(link);
    setQuickAssignMsg('✓ Film link copied');
    setTimeout(() => setQuickAssignMsg(''), 2000);
    setContextMenu(null);
  };

  const handleCopyReference = (num) => {
    navigator.clipboard.writeText(String(num).padStart(3,'0'));
    setQuickAssignMsg('✓ Reference number copied');
    setTimeout(() => setQuickAssignMsg(''), 2000);
    setContextMenu(null);
  };

  const handleContextMenuAddTask = async (submissionId) => {
    const text = contextMenuTaskText.trim();
    if (!text) return;
    try {
      await API.post(`/api/admin/submissions/${submissionId}/tasks`, { text });
      setQuickAssignMsg('✓ Task added');
      setTimeout(() => setQuickAssignMsg(''), 2000);
    } catch { setQuickAssignMsg('Could not add task'); setTimeout(() => setQuickAssignMsg(''), 2000); }
    setContextMenuTaskText(''); setContextMenuAddingTask(false); setContextMenu(null);
  };

  // Keyboard-driven film switching while the drawer is open — moves
  // forward/back through the currently loaded/filtered `submissions` list,
  // reopening the drawer on the same tab it was already on (pendingDrawerTab
  // is kept in sync with the drawer's live tab via ReviewDrawer's
  // onTabChange, not just its initial value).
  const navigateFilm = (direction) => {
    if (!selected) return;
    const idx = submissions.findIndex(s => s.id === selected.id);
    if (idx === -1) return;
    const nextIdx = direction === 'next' ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= submissions.length) return;
    openSubmission(submissions[nextIdx], pendingDrawerTab);
  };

  const handleArchiveHighlighted = async () => {
    if (highlightedIndex < 0 || highlightedIndex >= submissions.length) return;
    const sub = submissions[highlightedIndex];
    const ok = await confirm(`Archive "${sub.title}"? It will be hidden from the submissions list. A super admin can restore it later.`, { title: 'Archive submission?', confirmText: 'Archive' });
    if (!ok) return;
    try {
      await API.put(`/api/admin/submissions/${sub.id}/archive`);
      showToast('Archived');
      loadSubmissions();
    } catch { showToast('Archive failed', 'error'); }
  };

  const handleBulkAction = async () => {
    const body = bulkAction === 'approved' ? { status: bulkAction, approval_platform: bulkApprovalPlatform } : { status: bulkAction };
    try {
      const res = selectAllMatching
        ? await API.post('/api/admin/submissions/bulk', { ...body, filters: { search: filters.search, genre: filters.genre, language: filters.language, status: filters.status, date_from: filters.dateFrom, date_to: filters.dateTo, season: filters.season, duration_bucket: filters.duration } })
        : (bulkIds.length ? await API.post('/api/admin/submissions/bulk', { ...body, ids: bulkIds }) : null);
      if (!res) return;
      const { updated, skipped } = res.data;
      if (skipped?.length) {
        showToast(`${updated} updated, ${skipped.length} skipped (${skipped[0].reason}${skipped.length > 1 ? `, +${skipped.length - 1} more` : ''})`, updated > 0 ? 'success' : 'error');
      } else {
        showToast(`${updated} film${updated === 1 ? '' : 's'} updated`);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Bulk action failed', 'error');
    }
    setBulkIds([]); setSelectAllMatching(false); loadSubmissions();
  };

  const handleExport = async (format, dateFrom, dateTo) => {
    setExporting(format);
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.append('date_from', dateFrom);
      if (dateTo) p.append('date_to', dateTo);
      const qs = p.toString();
      const res = await API.get(`/api/admin/export-${format}${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `shortflix-submissions.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { showToast('Export failed', 'error'); }
    finally { setExporting(''); }
  };

  // 'general' is a sentinel for the no-season pool — loginBackgrounds
  // stores that row with season_id=NULL server-side, so the lookup here
  // has to special-case it rather than matching season.id directly.
  const openBgModal = (season) => {
    const existing = season.id === 'general'
      ? loginBackgrounds.find(b => b.season_id === null)
      : loginBackgrounds.find(b => b.season_id === season.id);
    setNewBg({
      season_id: season.id === 'general' ? '' : season.id,
      overlay_opacity: existing ? existing.overlay_opacity : 50,
      focal_point: existing ? existing.focal_point : 'center',
      overlay_style: existing ? existing.overlay_style : 'gradient',
      file: null,
      previewUrl: existing ? existing.image_url : null,
    });
    setBgModalSeasonId(season.id);
  };
  const closeBgModal = () => { setBgModalSeasonId(null); setNewBg({ season_id:'', overlay_opacity:50, focal_point:'center', overlay_style:'gradient', file:null, previewUrl:null }); };

  const genres = [...new Set(submissions.map(s => s.genre).filter(Boolean))];
  const languages = [...new Set(submissions.map(s => s.language).filter(Boolean))];

  const TABS = [
    { id:'dashboard', label:'Dashboard', icon:<Home size={14} /> },
    { id:'submissions', label:'Submissions', icon:<LayoutDashboard size={14} />, badge: adminNotifications.unreadCount },
    { id:'reviews', label:'Reviews', icon:<Star size={14} />, badge: reviewsReadyCount },
    { id:'deliveries', label:'Deliveries', icon:<Truck size={14} />, badge: deliveriesNeedingAttention },
    { id:'analytics', label:'Analytics', icon:<BarChart2 size={14} /> },
    { id:'content', label:'Content', icon:<Megaphone size={14} /> },
    { id:'notes', label:'Notes & Tasks', icon:<ListChecks size={14} /> },
    { id:'teams', label:'Teams', icon:<Users size={14} /> },
  ];

  const CONTENT_TABS = [
    { id:'banners', label:'Banners', icon:<Megaphone size={13} /> },
    { id:'seasons', label:'Seasons', icon:<Calendar size={13} /> },
    { id:'login-bg', label:'Login Background', icon:<Image size={13} /> },
    { id:'guidelines', label:'Guidelines', icon:<FileText size={13} /> },
    { id:'rejection-templates', label:'Rejection Templates', icon:<XCircle size={13} /> },
  ];

  return (
    <div className="admin-shell" style={{ minHeight:'100vh', background:'var(--bg)', display:'flex' }}>
      <AdminSidebar tabs={TABS} active={tab} onChange={setTab} onSettingsClick={() => setTab(t => t==='settings' ? 'dashboard' : 'settings')} settingsActive={tab==='settings'} />

      {/* Hidden while the ReviewDrawer is open — the drawer is right-anchored
          full height with its own ✕ close button in the same top-right
          corner, and the drawer sits at a higher effective stacking context,
          so the two controls visually overlapped. */}
      {!selected && (
        <div className="admin-notification-bell-fixed" style={{ position:'fixed', top:16, right:20, zIndex:250, display:'flex', alignItems:'center', gap:8 }}>
          {tab==='submissions' && (
            topSearchOpen ? (
              <div className="admin-top-search-box" style={{ display:'flex', alignItems:'center', gap:6, background:'var(--bg-elevated)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'0 8px', height:32, minWidth:0 }}>
                <Search size={13} color="var(--text-muted)" style={{ flexShrink:0 }} />
                <input ref={searchInputRef} autoFocus value={filters.search} onChange={e => setFilters(f=>({...f,search:e.target.value}))}
                  onBlur={() => { if (!filters.search.trim()) setTopSearchOpen(false); }}
                  onKeyDown={e => { if (e.key==='Escape') { setFilters(f=>({...f,search:''})); setTopSearchOpen(false); } }}
                  placeholder="Search title, #, email, director…"
                  className="admin-top-search-input"
                  style={{ border:'none', background:'none', outline:'none', fontSize:13, width:200, minWidth:0, padding:'6px 0', color:'var(--text)', fontFamily:'var(--font)' }} />
                <button onClick={() => { setFilters(f=>({...f,search:''})); setTopSearchOpen(false); }} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', flexShrink:0 }}>
                  <X size={13} color="var(--text-muted)" />
                </button>
              </div>
            ) : (
              <button onClick={() => setTopSearchOpen(true)} title="Search submissions (/)"
                style={{ width:32, height:32, borderRadius:'var(--r-sm)', background:'var(--bg-elevated)', border:'1.5px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                <Search size={14} color="var(--text-secondary)" />
              </button>
            )
          )}
          {tab==='submissions' && !showArchived && (
            <button onClick={() => setShowSubmitBehalf(true)} title="Submit for Filmmaker"
              style={{ width:32, height:32, borderRadius:'var(--r-sm)', background:'var(--pink)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
          <button onClick={handleManualRefresh} title="Refresh submissions list" disabled={refreshing}
            style={{ width:32, height:32, borderRadius:'var(--r-sm)', background:'var(--bg-elevated)', border:'1.5px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <RefreshCw size={14} color="var(--text-secondary)" style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
          </button>
          <NotificationBell {...adminNotifications} onItemClick={openNotification} />
        </div>
      )}

      <div style={{ flex:1, minWidth:0 }}>
      {/* Extra top padding clears the fixed Search/+/Refresh/Bell cluster
          (top:16, ~32px tall) so page content — especially the Submissions
          stat cards, which sit near the right edge — never sits underneath
          it, including when the search box is expanded and wider. */}
      <div style={{ maxWidth:1440, margin:'0 auto', padding:'64px 24px 28px' }}>

        {/* ── SUBMISSIONS ── */}
        {tab==='submissions' && (
          <div>
            {!showArchived && (
            <div className="reviewer-stats-grid" style={{ marginBottom:20 }}>
              {[['Total','var(--pink)',stats.total],['Pending','var(--amber)',stats.pending],['Approved','var(--green)',stats.approved],['Rejected','var(--red)',stats.rejected]].map(([l,c,v]) => (
                <div key={l} className="card" style={{ padding:'14px 18px' }}>
                  <div style={{ fontWeight:900, fontSize:28, color:c }}>{v||0}</div>
                  <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>
            )}

            {showArchived && (
              <div style={{ marginBottom:14 }}>
                <button onClick={() => { setShowArchived(false); setBulkIds([]); }}
                  style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  ← Back to Active
                </button>
              </div>
            )}

            {/* Filters — title/number/email/director search moved to the
                expandable search icon in the top-right cluster; everything
                else stays here. */}
            <div className="table-scroll" style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'nowrap', alignItems:'center', paddingBottom:2 }}>
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <input type="date" value={filters.dateFrom} onChange={e => setFilters(f=>({...f,dateFrom:e.target.value}))} title="From date" style={{ fontSize:12, padding:'7px 9px', width:'auto' }} />
                <input type="date" value={filters.dateTo} onChange={e => setFilters(f=>({...f,dateTo:e.target.value}))} title="To date" style={{ fontSize:12, padding:'7px 9px', width:'auto' }} />
                {(filters.dateFrom || filters.dateTo) && (
                  <button onClick={() => setFilters(f=>({...f,dateFrom:'',dateTo:''}))} style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 10px', fontSize:11, cursor:'pointer', fontFamily:'var(--font)', flexShrink:0 }}>Clear dates</button>
                )}
              </div>
              <select value={filters.genre} onChange={e => setFilters(f=>({...f,genre:e.target.value}))} style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0 }}>
                <option value="">All Genres</option>{genres.map(g=><option key={g}>{g}</option>)}
              </select>
              <select value={filters.language} onChange={e => setFilters(f=>({...f,language:e.target.value}))} style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0 }}>
                <option value="">All Languages</option>{languages.map(l=><option key={l}>{l}</option>)}
              </select>
              <select value={filters.status} onChange={e => setFilters(f=>({...f,status:e.target.value}))} style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0 }}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select value={filters.filmmakerDeleted} onChange={e => setFilters(f=>({...f,filmmakerDeleted:e.target.value}))} style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0 }} title="Filter by filmmaker-deleted status">
                <option value="">All</option>
                <option value="true">Deleted by Filmmaker</option>
                <option value="false">Not Deleted</option>
              </select>
              <select value={filters.season} onChange={e => setFilters(f=>({...f,season:e.target.value}))} style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0 }} title="Filter by season">
                <option value="">All</option>
                <option value="general">General (No Season)</option>
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={filters.duration} onChange={e => setFilters(f=>({...f,duration:e.target.value}))} style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0 }} title="Filter by duration">
                <option value="">All</option>
                <option value="under5">Under 5 minutes</option>
                <option value="5to30">5–30 minutes</option>
                <option value="30to60">30–60 minutes</option>
                <option value="over60">More than 1 hour</option>
              </select>
              <select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value, 10))}
                style={{ fontSize:12, padding:'7px 10px', width:'auto', flexShrink:0, marginLeft:'auto' }}
                title="How many entries to show per page">
                <option value={15}>15 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>

            {!showArchived && bulkIds.length > 0 && bulkIds.length < submissions.length && !selectAllMatching && (
              <div style={{ background:'rgba(59,130,246,0.06)', border:'1.5px solid rgba(59,130,246,0.2)', borderRadius:'var(--r-md)', padding:'8px 14px', marginBottom:8, fontSize:12, color:'var(--blue)', fontWeight:600 }}>
                {bulkIds.length} selected on this page — <button onClick={() => setBulkIds(submissions.map(s=>s.id))} style={{ background:'none', border:'none', color:'var(--blue)', fontWeight:800, cursor:'pointer', fontFamily:'var(--font)', padding:0, textDecoration:'underline' }}>select all {submissions.length}</button>
              </div>
            )}
            {!showArchived && bulkIds.length > 0 && bulkIds.length === submissions.length && pagination.total > submissions.length && !selectAllMatching && (
              <div style={{ background:'rgba(59,130,246,0.06)', border:'1.5px solid rgba(59,130,246,0.2)', borderRadius:'var(--r-md)', padding:'8px 14px', marginBottom:8, fontSize:12, color:'var(--blue)', fontWeight:600 }}>
                All {submissions.length} on this page selected — <button onClick={() => setSelectAllMatching(true)} style={{ background:'none', border:'none', color:'var(--blue)', fontWeight:800, cursor:'pointer', fontFamily:'var(--font)', padding:0, textDecoration:'underline' }}>select all {pagination.total} matching this filter</button>
              </div>
            )}
            {!showArchived && (bulkIds.length > 0 || selectAllMatching) && (
              <div style={{ background:'var(--pink-glow)', border:'1.5px solid var(--pink-glow-strong)', borderRadius:'var(--r-md)', padding:'10px 14px', marginBottom:10, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:12, fontWeight:800, color:'var(--pink)' }}>{selectAllMatching ? `${pagination.total} selected (all matching this filter)` : `${bulkIds.length} selected`}</span>
                <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} style={{ background:'var(--bg-card)', border:'1.5px solid var(--border)', color:'var(--text)', borderRadius:'var(--r-sm)', padding:'5px 10px', fontSize:11 }}>
                  <option value="approved">Approve All</option>
                  <option value="rejected">Reject All</option>
                  <option value="pending">Mark Pending</option>
                </select>
                {bulkAction === 'approved' && (
                  <select value={bulkApprovalPlatform} onChange={e => setBulkApprovalPlatform(e.target.value)} title="Applies to every film in this batch" style={{ background:'var(--bg-card)', border:'1.5px solid var(--border)', color:'var(--text)', borderRadius:'var(--r-sm)', padding:'5px 10px', fontSize:11 }}>
                    <option value="app">For: The App</option>
                    <option value="youtube">For: YouTube Only</option>
                  </select>
                )}
                <button onClick={handleBulkAction} className="btn-primary" style={{ padding:'6px 12px', fontSize:11 }}>Apply</button>
                <button onClick={() => { setBulkIds([]); setSelectAllMatching(false); setSelectionMode(false); }} style={{ background:'none', color:'var(--text-muted)', border:'none', fontSize:11, cursor:'pointer', fontFamily:'var(--font)' }}>Clear</button>
              </div>
            )}

            {/* Table */}
            <div className="card" style={{ overflowX:'auto', overflowY:'hidden' }}>
              <div className="admin-grid-header" style={{ gridTemplateColumns:'28px 62px 1fr 100px 116px 110px 110px 130px 140px 70px', padding:'11px 14px', background:'rgba(255,45,139,0.08)', borderBottom:'2px solid var(--pink)', fontSize:10.5, color:'var(--text)', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', alignItems:'center' }}>
                <span>{!showArchived && <input type="checkbox" onChange={e => { setSelectionMode(e.target.checked); setBulkIds(e.target.checked ? submissions.map(s=>s.id):[]); setSelectAllMatching(false); }} checked={(bulkIds.length===submissions.length&&submissions.length>0)||selectAllMatching} />}</span>
                <span>#</span><span>Film</span><span>Date</span>
                <span onClick={() => setSort(s => ({ by: 'duration', dir: s.by === 'duration' && s.dir === 'asc' ? 'desc' : 'asc' }))}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, userSelect: 'none' }} title="Sort by duration">
                  Duration
                  {sort.by === 'duration' && (sort.dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
                </span>
                <span>Genre</span><span>Language</span><span>Stage</span><span>Status</span><span>{showArchived ? 'Archived' : 'Score'}</span>
              </div>
              {loading && <LoadingState label="Loading submissions…" minHeight={160} />}
              {!loading && submissions.length===0 && <EmptyState icon={FileText} title="No submissions found" subtitle="Try adjusting your filters, or check back once filmmakers start submitting." />}
              {submissions.map((sub,i) => {
                const statusStyle = {
                  approved: { border:'var(--green)', bg:'rgba(0,200,150,0.05)' },
                  rejected: { border:'var(--red)', bg:'rgba(255,68,68,0.05)' },
                  pending: { border:'var(--amber)', bg:'transparent' },
                }[sub.status] || { border:'transparent', bg:'transparent' };
                // Gmail-style unread: a pink tint layered on top of the
                // existing status background, composed rather than replaced.
                const rowBg = sub.has_unread_notification ? 'rgba(255,45,139,0.18)' : statusStyle.bg;
                const isHighlighted = i === highlightedIndex;
                return (
                <div key={sub.id} onClick={() => { setHighlightedIndex(i); openSubmission(sub); }}
                  onContextMenu={e => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, submissionId: sub.id, submissionTitle: sub.title }); setContextMenuAddingTask(false); setContextMenuTaskText(''); }}
                  className="admin-grid-row"
                  style={{ gridTemplateColumns:'28px 62px 1fr 100px 116px 110px 110px 130px 140px 70px', padding:'18px 14px', borderBottom:i<submissions.length-1?'1px solid var(--border)':'none', borderLeft:`3px solid ${statusStyle.border}`, background:rowBg, cursor:'pointer', transition:'background 0.12s', alignItems:'center', boxShadow: isHighlighted ? 'inset 0 0 0 1.5px var(--pink)' : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background=rowBg}>
                  <span onClick={e => e.stopPropagation()}>
                    {!showArchived && selectionMode && <input type="checkbox" style={{ accentColor:'var(--pink)' }} checked={selectAllMatching || bulkIds.includes(sub.id)} onChange={e => {
                      if (selectAllMatching) { setSelectAllMatching(false); setBulkIds(submissions.filter(s=>s.id!==sub.id).map(s=>s.id)); return; }
                      setBulkIds(p => e.target.checked?[...p,sub.id]:p.filter(id=>id!==sub.id));
                    }} />}
                  </span>
                  <span data-label="#" style={{ fontSize:11, color:'var(--text-muted)', fontWeight:800, fontFamily:'monospace' }} title="Stable reference number — safe to use in discussions/notes, never changes">
                    {sub.submission_number != null ? String(sub.submission_number).padStart(3,'0') : '—'}
                  </span>
                  <div>
                    <div style={{ fontSize:19, fontWeight: sub.has_unread_notification ? 800 : 700, color: (sub.status==='rejected'||sub.filmmaker_deleted) ? 'var(--text-muted)' : (sub.has_unread_notification ? 'var(--text)' : 'var(--text-secondary)'), marginBottom:3, textDecoration: (sub.status==='rejected'||sub.filmmaker_deleted) ? 'line-through' : 'none', display:'flex', alignItems:'center', gap:6 }}>
                      {sub.has_unread_notification && <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--pink)', flexShrink:0 }} />}
                      {sub.title}
                    </div>
                    <div style={{ fontSize:14, color:'var(--text-secondary)', fontWeight:500 }}>{sub.filmmaker_name} · {timeAgo(sub.created_at)}</div>
                    {sub.filmmaker_deleted && <div style={{ fontSize:12, color:'var(--red)', fontWeight:700, marginTop:2 }}>Deleted by filmmaker · {timeAgo(sub.filmmaker_deleted_at)}</div>}
                    <div style={{ display:'flex', gap:4, marginTop:3, flexWrap:'wrap' }}>
                      {sub.season_name
                        ? <span className="badge badge-pink" style={{ fontSize:9, padding:'1.5px 6px' }}>{sub.season_name}</span>
                        : <span className="badge" style={{ fontSize:9, padding:'1.5px 6px', background:'var(--bg-elevated)', color:'var(--text-muted)', border:'1px solid var(--border)' }}>general</span>}
                      {(sub.format === 'vertical' || sub.format === 'series') && (
                        <span className="badge" style={{ fontSize:9, padding:'1.5px 6px', background:'var(--bg-elevated)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
                          {sub.format}{sub.episode_count ? ` · ${sub.episode_count} eps` : ''}{sub.season_number ? ` · S${sub.season_number}` : ''}
                        </span>
                      )}
                      {sub.admin_notes && <span className="badge badge-amber" style={{ fontSize:9, padding:'1.5px 6px' }}>note</span>}
                      {sub.is_resubmission && <span className="badge badge-purple" style={{ fontSize:9, padding:'1.5px 6px' }}>resubmit</span>}
                      {sub.submitted_by_admin_id && <span className="badge" style={{ fontSize:9, padding:'1.5px 6px', display:'inline-flex', alignItems:'center', gap:2, background:'rgba(255,45,139,0.1)', color:'var(--pink)', border:'1px solid rgba(255,45,139,0.25)' }} title="Entered by an admin on the filmmaker's behalf"><Mail size={8} /> admin submitted</span>}
                      {sub.notes_to_reviewers && <span className="badge badge-blue" style={{ fontSize:9, padding:'1.5px 6px' }}>filmmaker note</span>}
                      {sub.admin_notes_for_reviewers && <span className="badge" style={{ fontSize:9, padding:'1.5px 6px', background:'rgba(59,130,246,0.1)', color:'var(--blue)', border:'1px solid rgba(59,130,246,0.25)' }}>reviewer note</span>}
                    </div>
                  </div>
                  <span data-label="Date" style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>
                    {sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—'}
                  </span>
                  <span data-label="Duration" style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>{sub.duration || '—'}</span>
                  <span data-label="Genre" style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>{sub.genre||'—'}</span>
                  <span data-label="Language" style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>{sub.language||'—'}</span>
                  <span data-label="Stage" style={{ fontSize:11.5, color:'var(--text-secondary)', fontWeight:700, textTransform:'capitalize', letterSpacing:'0.04em' }}>{(sub.review_status||'').replace('_',' ')}</span>
                  <div data-label="Status"><StatusBadge status={sub.status} /></div>
                  {showArchived ? (
                    <div data-label="Archived">
                      <button onClick={e => { e.stopPropagation(); handleRestore(sub.id); }}
                        style={{ background:'rgba(0,200,150,0.08)', border:'1.5px solid rgba(0,200,150,0.25)', color:'var(--green)', borderRadius:'var(--r-sm)', padding:'5px 8px', fontSize:10, fontWeight:800, cursor:'pointer', fontFamily:'var(--font)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                        Restore
                      </button>
                    </div>
                  ) : (
                    <div data-label="Score">
                      <span style={{ fontWeight:900, fontSize:14, color:sub.score_overall?'var(--pink)':'var(--text-muted)' }}>
                        {sub.score_overall ? `★${parseFloat(sub.score_overall).toFixed(1)}` : '—'}
                      </span>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {!showArchived && pagination.total > 0 && (() => {
              const rangeStart = (pagination.page - 1) * pagination.pageSize + 1;
              const rangeEnd = Math.min(pagination.page * pagination.pageSize, pagination.total);
              return (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, marginTop:14 }}>
                  <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page<=1} style={{ background:'none', color: page<=1?'var(--text-muted)':'var(--text)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor: page<=1?'default':'pointer', fontFamily:'var(--font)' }}>← Prev</button>
                  <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:600 }}>Showing {rangeStart}–{rangeEnd} of {pagination.total} entries · Page {pagination.page} of {pagination.totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(pagination.totalPages, p+1))} disabled={page>=pagination.totalPages} style={{ background:'none', color: page>=pagination.totalPages?'var(--text-muted)':'var(--text)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor: page>=pagination.totalPages?'default':'pointer', fontFamily:'var(--font)' }}>Next →</button>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── SETTINGS (super-admin only — gated by the sidebar gear button
             not even existing for anyone else, see AdminSidebar.js) ── */}
        {tab==='settings' && isSuperAdmin && (() => {
          const exportRangeDates = () => {
            if (exportRange === 'all') return {};
            if (exportRange === 'custom') return { from: exportCustomFrom || undefined, to: exportCustomTo || undefined };
            const days = { '1m':30, '3m':90, '6m':180, '1y':365 }[exportRange];
            const to = new Date();
            const from = new Date(); from.setDate(from.getDate() - days);
            const fmt = d => d.toISOString().slice(0,10);
            return { from: fmt(from), to: fmt(to) };
          };
          const doExport = (format) => { const { from, to } = exportRangeDates(); handleExport(format, from, to); };
          return (
          <div style={{ maxWidth:1040 }}>
            <h2 style={{ fontWeight:900, fontSize:26, color:'var(--text)', marginBottom:18, letterSpacing:'-0.01em' }}>Settings</h2>

            <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'1.5px solid var(--border)', paddingBottom:0 }}>
              {[['settings','Settings'],['activity','Activity']].map(([val,label]) => (
                <button key={val} onClick={() => setSettingsSubTab(val)}
                  style={{ background:'none', border:'none', borderBottom: settingsSubTab===val ? '2px solid var(--pink)' : '2px solid transparent', color: settingsSubTab===val ? 'var(--pink)' : 'var(--text-secondary)', fontWeight:800, fontSize:13, padding:'8px 4px', marginBottom:-2, cursor:'pointer', fontFamily:'var(--font)' }}>
                  {label}
                </button>
              ))}
            </div>

            {settingsSubTab==='settings' && (
            <div className="admin-two-col" style={{ gridTemplateColumns:'1fr 360px', gap:20, alignItems:'start' }}>
              <div>
                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:14 }}>Submission Cap</div>
                  <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                    <button onClick={async () => { const n=!limitEnabled; setLimitEnabled(n); await API.put('/api/admin/settings/submission_limit_enabled',{value:String(n)}); }}
                      title={limitEnabled ? 'Turn off the submission cap' : 'Turn on the submission cap'}
                      style={{ width:36, height:20, borderRadius:10, background:limitEnabled?'var(--pink)':'var(--bg-card)', border:'1.5px solid var(--border-strong)', cursor:'pointer', position:'relative', transition:'all 0.2s', flexShrink:0 }}>
                      <span style={{ position:'absolute', top:1, left:limitEnabled?16:1, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s', display:'block' }} />
                    </button>
                    {!limitEnabled ? (
                      <span style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:600 }}>Unlimited — {stats.total||0} submitted, no cap</span>
                    ) : editLimit ? (
                      <>
                        <input value={limit} onChange={e => setLimit(e.target.value)} style={{ width:70, fontSize:13, padding:'5px 8px' }} />
                        <button onClick={async () => { await API.put('/api/admin/settings/submission_limit',{value:limit}); setEditLimit(false); }} className="btn-primary" style={{ padding:'5px 12px', fontSize:11 }}>Save</button>
                      </>
                    ) : (
                      <>
                        <div style={{ flex:1, minWidth:80, height:4, background:'var(--bg-elevated)', borderRadius:2, overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${Math.min(100,((stats.total||0)/parseInt(limit))*100)}%`, background:`${((stats.total||0)/parseInt(limit))>0.9?'var(--red)':((stats.total||0)/parseInt(limit))>0.7?'var(--amber)':'var(--green)'}`, borderRadius:2 }} />
                        </div>
                        <span style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:600 }}>{stats.total||0}/{limit}</span>
                        <button onClick={() => setEditLimit(true)} style={{ background:'none', color:'var(--blue)', fontSize:11, border:'none', cursor:'pointer', fontFamily:'var(--font)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>Edit</button>
                      </>
                    )}
                  </div>
                </div>

                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Submission Genres</div>
                  <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>Options shown on the filmmaker submission form and "Submit on Behalf" below. Add or remove without a code change.</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
                    {genreOptions.map(g => (
                      <span key={g} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'var(--bg-elevated)', border:'1.5px solid var(--border)', borderRadius:99, fontSize:12, fontWeight:600, color:'var(--text-secondary)' }}>
                        {g}
                        <button
                          title={`Remove ${g}`}
                          onClick={async () => {
                            const next = genreOptions.filter(x => x !== g);
                            setGenreOptions(next);
                            await API.put('/api/admin/settings/submission_genres', { value: JSON.stringify(next) });
                          }}
                          style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:0, display:'flex', lineHeight:0 }}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <input
                      value={newGenre}
                      onChange={e => setNewGenre(e.target.value)}
                      onKeyDown={async e => {
                        if (e.key !== 'Enter') return;
                        const name = newGenre.trim();
                        if (!name || genreOptions.includes(name)) return;
                        const next = [...genreOptions, name];
                        setGenreOptions(next);
                        setNewGenre('');
                        await API.put('/api/admin/settings/submission_genres', { value: JSON.stringify(next) });
                      }}
                      placeholder="e.g. AI Film"
                      style={{ flex:1, fontSize:13, padding:'7px 10px' }}
                    />
                    <button
                      className="btn-ghost"
                      disabled={!newGenre.trim() || genreOptions.includes(newGenre.trim())}
                      onClick={async () => {
                        const name = newGenre.trim();
                        if (!name || genreOptions.includes(name)) return;
                        const next = [...genreOptions, name];
                        setGenreOptions(next);
                        setNewGenre('');
                        await API.put('/api/admin/settings/submission_genres', { value: JSON.stringify(next) });
                      }}
                      style={{ padding:'6px 14px', fontSize:12, flexShrink:0 }}
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:14 }}>Auto-Open After</div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <select value={autoOpenHours} onChange={async e => { setAutoOpenHours(e.target.value); await API.put('/api/admin/settings/auto_open_hours',{value:e.target.value}); }} style={{ width:80, fontSize:13, padding:'6px 8px' }}>
                      {['1','2','4','6','12','24'].map(h => <option key={h} value={h}>{h}h</option>)}
                    </select>
                    <button onClick={async () => { const n=!autoOpenEnabled; setAutoOpenEnabled(n); await API.put('/api/admin/settings/auto_open_enabled',{value:String(n)}); }} style={{ width:36, height:20, borderRadius:10, background:autoOpenEnabled?'var(--pink)':'var(--bg-card)', border:'1.5px solid var(--border-strong)', cursor:'pointer', position:'relative', transition:'all 0.2s', flexShrink:0 }}>
                      <span style={{ position:'absolute', top:1, left:autoOpenEnabled?16:1, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s', display:'block' }} />
                    </button>
                    <span style={{ fontSize:11, color:autoOpenEnabled?'var(--green)':'var(--text-muted)', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em' }}>{autoOpenEnabled?'ON':'OFF'}</span>
                  </div>
                  <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:10 }}>New submissions are automatically opened/marked seen after this many hours, even if no admin has clicked into them.</p>
                </div>

                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Reviewer Feedback Requirements</div>
                  <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>By default reviewers can leave any of the 3 written-feedback boxes blank. Check a box below to make that one required before a reviewer can submit — scores and the recommendation are always required regardless.</p>
                  {[
                    ['What works well', requireStrengths, setRequireStrengths, 'require_feedback_strengths'],
                    ['Areas to improve', requireImprovements, setRequireImprovements, 'require_feedback_improvements'],
                    ['Summary note', requireSummary, setRequireSummary, 'require_feedback_summary'],
                  ].map(([label, checked, setChecked, key]) => (
                    <label key={key} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', marginBottom:10 }}>
                      <input type="checkbox" checked={checked} onChange={async e => { const v=e.target.checked; setChecked(v); await API.put(`/api/admin/settings/${key}`,{value:String(v)}); }} style={{ width:16, height:16, accentColor:'var(--pink)', cursor:'pointer' }} />
                      <span style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:600 }}>Require "{label}"</span>
                    </label>
                  ))}
                </div>

                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:14 }}>Export & Archive</div>
                  <div style={{ marginBottom:12 }}>
                    <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.08em' }}>Export range</label>
                    <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                      <select value={exportRange} onChange={e => setExportRange(e.target.value)} style={{ fontSize:13, padding:'7px 10px', width:'auto' }}>
                        <option value="all">All time</option>
                        <option value="1m">Last 1 month</option>
                        <option value="3m">Last 3 months</option>
                        <option value="6m">Last 6 months</option>
                        <option value="1y">Last 1 year</option>
                        <option value="custom">Custom range…</option>
                      </select>
                      {exportRange === 'custom' && (
                        <>
                          <input type="date" value={exportCustomFrom} onChange={e => setExportCustomFrom(e.target.value)} style={{ fontSize:12, padding:'7px 8px', width:'auto' }} />
                          <span style={{ fontSize:12, color:'var(--text-muted)' }}>to</span>
                          <input type="date" value={exportCustomTo} onChange={e => setExportCustomTo(e.target.value)} style={{ fontSize:12, padding:'7px 8px', width:'auto' }} />
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    <button onClick={() => doExport('csv')} disabled={exporting==='csv'} className="btn-ghost" style={{ padding:'8px 14px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                      <Download size={13} /> {exporting==='csv' ? 'Exporting…' : 'Export CSV'}
                    </button>
                    <button onClick={() => doExport('xlsx')} disabled={exporting==='xlsx'} className="btn-ghost" style={{ padding:'8px 14px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                      <Download size={13} /> {exporting==='xlsx' ? 'Exporting…' : 'Export Excel'}
                    </button>
                    <button onClick={() => { setShowArchived(true); setBulkIds([]); setTab('submissions'); }}
                      style={{ background:'none', color:'var(--text-secondary)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', display:'flex', alignItems:'center', gap:6 }}>
                      🗄 View Archived
                    </button>
                  </div>
                </div>

                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Display</div>
                  <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>A personal preference for this browser only — not a team-wide setting, unlike everything in Advanced Settings below.</p>
                  <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                    <button onClick={toggleHighContrast} title={highContrast ? 'Turn off high contrast' : 'Turn on high contrast'}
                      style={{ width:36, height:20, borderRadius:10, background:highContrast?'var(--pink)':'var(--bg-card)', border:'1.5px solid var(--border-strong)', cursor:'pointer', position:'relative', transition:'all 0.2s', flexShrink:0 }}>
                      <span style={{ position:'absolute', top:1, left:highContrast?16:1, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s', display:'block' }} />
                    </button>
                    <div>
                      <div style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>High Contrast (dark mode)</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>Pure black background, brighter white text and borders — for easier reading in dark mode.</div>
                    </div>
                  </div>
                </div>

                {user?.is_primary_super_admin && (
                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Advanced Settings</div>
                  <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:16 }}>Lower-level controls — safe to leave on their defaults. Changes apply going forward only (e.g. a shorter session length doesn't sign anyone out early). Visible to the primary super admin only.</p>

                  <div style={{ marginBottom:18 }}>
                    <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.08em' }}>Strong Candidate score threshold</label>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <input type="number" step="0.1" min="0" max="10" value={advScoreThreshold} onChange={e => setAdvScoreThreshold(e.target.value)} style={{ width:80, fontSize:13, padding:'6px 8px' }} />
                      <button onClick={async () => { await API.put('/api/admin/settings/adv_strong_candidate_threshold',{value:advScoreThreshold}); showToast('Saved'); }} className="btn-ghost" style={{ padding:'6px 12px', fontSize:11 }}>Save</button>
                    </div>
                    <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>Films with a consensus score at or above this show a "⭐ Strong Candidate" badge in the Reviews tab.</p>
                  </div>

                  <div style={{ marginBottom:18 }}>
                    <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.08em' }}>Minimum score to approve</label>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <input type="number" step="0.1" min="0" max="10" value={advMinApprovalScore} onChange={e => setAdvMinApprovalScore(e.target.value)} style={{ width:80, fontSize:13, padding:'6px 8px' }} />
                      <button onClick={async () => { await API.put('/api/admin/settings/adv_min_approval_score',{value:advMinApprovalScore}); showToast('Saved'); }} className="btn-ghost" style={{ padding:'6px 12px', fontSize:11 }}>Save</button>
                    </div>
                    <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>A film's consensus score must be at or above this to be Approved — below it, only "Not Selected" is available. Enforced server-side too, not just in the UI.</p>
                  </div>

                  <div style={{ marginBottom:18 }}>
                    <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.08em' }}>Email Notifications</label>
                    {[
                      ['Submission confirmation', advNotifySubmission, setAdvNotifySubmission, 'notify_submission_confirmation'],
                      ['Correction needed', advNotifyCorrection, setAdvNotifyCorrection, 'notify_correction_email'],
                      ['Delivery / QC updates', advNotifyDeliveryQc, setAdvNotifyDeliveryQc, 'notify_delivery_qc_email'],
                      ['Release scheduled / hold', advNotifyRelease, setAdvNotifyRelease, 'notify_release_email'],
                    ].map(([label, checked, setChecked, key]) => (
                      <label key={key} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', marginBottom:8 }}>
                        <input type="checkbox" checked={checked} onChange={async e => { const v=e.target.checked; setChecked(v); await API.put(`/api/admin/settings/${key}`,{value:String(v)}); }} style={{ width:16, height:16, accentColor:'var(--pink)', cursor:'pointer' }} />
                        <span style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:600 }}>{label}</span>
                      </label>
                    ))}
                    <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Decision (Approve/Not Selected) emails aren't listed here — they already require an explicit super-admin send per film.</p>
                  </div>

                  <div className="adv-settings-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:4 }}>
                    {[
                      ['Reviewer access-log retention (months)', advLogRetentionMonths, setAdvLogRetentionMonths, 'reviewer_log_retention_months'],
                      ['Session length (days)', advSessionExpiryDays, setAdvSessionExpiryDays, 'session_expiry_days'],
                      ['Magic-link expiry (minutes)', advMagicLinkMinutes, setAdvMagicLinkMinutes, 'magic_link_expiry_minutes'],
                      ['Reviewer login lockout — attempts', advLockoutAttempts, setAdvLockoutAttempts, 'reviewer_lockout_attempts'],
                      ['Reviewer login lockout — minutes', advLockoutMinutes, setAdvLockoutMinutes, 'reviewer_lockout_minutes'],
                    ].map(([label, val, setVal, key]) => (
                      <div key={key} style={{ minWidth:0 }}>
                        <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</label>
                        <div style={{ display:'flex', gap:6, minWidth:0 }}>
                          <input type="number" min="1" value={val} onChange={e => setVal(e.target.value)} style={{ width:70, minWidth:0, fontSize:13, padding:'6px 8px' }} />
                          <button onClick={async () => { await API.put(`/api/admin/settings/${key}`,{value:val}); showToast('Saved'); }} className="btn-ghost" style={{ padding:'6px 10px', fontSize:11, flexShrink:0 }}>Save</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:12 }}>Reviewer access-log retention also updates the wording in the reviewer confidentiality agreement (clause 5), so the two never drift out of sync.</p>

                  <div style={{ marginTop:20, paddingTop:18, borderTop:'1px solid var(--border)' }}>
                    <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.08em' }}>SF Console Integration</label>
                    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                      <input type="checkbox" checked={advSfConsoleSyncEnabled}
                        onChange={async e => { const v=e.target.checked; setAdvSfConsoleSyncEnabled(v); await API.put('/api/admin/settings/sf_console_sync_enabled',{value:String(v)}); showToast(v ? 'SF Console sync turned on' : 'SF Console sync turned off'); }}
                        style={{ width:16, height:16, accentColor:'var(--pink)', cursor:'pointer' }} />
                      <span style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:600 }}>Push updates to SF Console</span>
                    </label>
                    <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>Kill switch for genre updates pushed to SF Console when a filmmaker changes it. Turn off if SF Console is misbehaving — doesn't affect anything else in this app, and SF Console can't pull from here either way without its own separate switch also being on.</p>
                  </div>
                </div>
                )}
              </div>

              {/* Right column of the grid is a single track (1fr 360px) —
                  About and Keyboard Shortcuts need to be wrapped in one
                  container to stack together here; as separate top-level
                  grid children they'd wrap into a 3rd implicit row under the
                  LEFT column instead (only 2 columns are defined), which is
                  why Keyboard Shortcuts was invisible below About before. */}
              <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
              <div className="card" style={{ padding:20 }}>
                <div className="section-label" style={{ marginBottom:14 }}>About</div>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                  <img src="/logo.png" alt="Shortflix" style={{ height:20 }} onError={e => { e.target.style.display = 'none'; }} />
                  <span style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>SF Gateway</span>
                  <span className="badge badge-pink" style={{ fontSize:11 }}>v{APP_VERSION}</span>
                </div>
                <div className="section-label" style={{ marginBottom:10, fontSize:10 }}>Version History</div>
                {/* Fixed-height + scroll so this card never grows unbounded
                    as the changelog accumulates entries release over release. */}
                <div style={{ maxHeight:360, overflowY:'auto', paddingRight:6 }}>
                  {CHANGELOG.map(entry => (
                    <div key={entry.version} style={{ marginBottom:14, paddingBottom:14, borderBottom:'1px solid var(--border)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                        <span style={{ fontSize:13, fontWeight:800, color:'var(--text)' }}>v{entry.version}</span>
                        <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>{new Date(entry.date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</span>
                      </div>
                      <ul style={{ margin:0, paddingLeft:18, display:'flex', flexDirection:'column', gap:4 }}>
                        {entry.notes.map((n,i) => <li key={i} style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.5 }}>{n}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding:20 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Keyboard Shortcuts</div>
                <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:14 }}>Active on the Submissions tab (sidebar toggle works everywhere), except while typing in a field.</p>
                {SHORTCUTS_LIST.map(([label, key]) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid var(--border)', fontSize:12.5 }}>
                    <span style={{ color:'var(--text-secondary)' }}>{label}</span>
                    <span style={{ display:'flex', gap:4 }}>
                      {key.split('  ').map(k => <code key={k} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:4, padding:'2px 7px', fontSize:11, fontFamily:'monospace', color:'var(--text)' }}>{k}</code>)}
                    </span>
                  </div>
                ))}
              </div>
              </div>
            </div>
            )}

            {settingsSubTab==='activity' && (
              <div>
                <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:16, fontWeight:400 }}>
                  Who did what, and when. {activityPagination.total} action{activityPagination.total===1?'':'s'} total.
                </p>

                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                  <select value={activityCategory} onChange={e => setActivityCategory(e.target.value)} style={{ fontSize:12, padding:'7px 10px', width:'auto' }}>
                    <option value="">All types</option>
                    {[['submission','Submissions'],['delivery','Delivery/QC'],['agreement','Agreements'],['reviewer_assignment','Reviewer Assignments'],['season','Seasons'],['banner','Banners'],['login_background','Login Backgrounds'],['guidelines','Guidelines'],['settings','Settings'],['team','Admin Team']].map(([v,l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  {activityCategory && (
                    <button onClick={() => setActivityCategory('')} style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>
                      Clear filter
                    </button>
                  )}

                  {user?.is_primary_super_admin && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
                      <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>Clear entries older than</span>
                      <input type="number" min={1} value={clearOldDays} onChange={e => setClearOldDays(e.target.value)} style={{ width:60, fontSize:12, padding:'6px 8px' }} />
                      <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>days</span>
                      <button onClick={handleClearOldActivity} disabled={clearingOld}
                        style={{ background:'rgba(255,68,68,0.08)', border:'1.5px solid rgba(255,68,68,0.25)', color:'var(--red)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>
                        {clearingOld ? 'Clearing...' : 'Clear'}
                      </button>
                    </div>
                  )}
                </div>

                {activity.length === 0 && <div className="card"><EmptyState icon={Clock} title="No activity recorded yet" subtitle={activityCategory ? "No actions of this type yet — try clearing the filter." : "Admin actions like reviews, archives, and team changes will show up here."} /></div>}
                {activity.map(a => (
                  <div key={a.id} className="card" style={{ padding:'12px 16px', marginBottom:6, display:'flex', alignItems:'flex-start', gap:12 }}>
                    <Clock size={14} color="var(--text-muted)" style={{ marginTop:2, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>
                        <strong>{a.actor_name || a.actor_email || 'Unknown'}</strong> {describeAction(a)}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{timeAgo(a.created_at)}</div>
                    </div>
                  </div>
                ))}

                {activityPagination.totalPages > 1 && (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, marginTop:14 }}>
                    <button onClick={() => setActivityPage(p => Math.max(1, p-1))} disabled={activityPage<=1} style={{ background:'none', color: activityPage<=1?'var(--text-muted)':'var(--text)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor: activityPage<=1?'default':'pointer', fontFamily:'var(--font)' }}>← Prev</button>
                    <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:600 }}>Page {activityPagination.page} of {activityPagination.totalPages} · {activityPagination.total} total</span>
                    <button onClick={() => setActivityPage(p => Math.min(activityPagination.totalPages, p+1))} disabled={activityPage>=activityPagination.totalPages} style={{ background:'none', color: activityPage>=activityPagination.totalPages?'var(--text-muted)':'var(--text)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'6px 12px', fontSize:11, fontWeight:700, cursor: activityPage>=activityPagination.totalPages?'default':'pointer', fontFamily:'var(--font)' }}>Next →</button>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })()}

        {/* ── DASHBOARD (landing screen) ── */}
        {tab==='dashboard' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, flexWrap:'wrap', marginBottom:16 }}>
              <div>
                <h2 style={{ fontWeight:900, fontSize:26, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Hey, {user?.name?.split(' ')[0] || 'there'} 👋</h2>
                <p style={{ fontSize:13, color:'var(--text-muted)' }}>Here's what's happening across Shortflix right now.</p>
              </div>
              <div style={{ display:'flex', gap:10, flexShrink:0 }}>
                <button onClick={() => setShowSubmitBehalf(true)} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px' }}>
                  <Plus size={14} /> Submit for Filmmaker
                </button>
                <button onClick={() => setTab('analytics')} className="btn-ghost" style={{ padding:'9px 16px' }}>View Full Analytics →</button>
              </div>
            </div>

            {/* Global search — films, reviewers, and admin team members in
                one place, since previously every search box on the site was
                scoped to just one tab (Submissions, or Reviews, etc.). */}
            <div style={{ position:'relative', maxWidth:480, marginBottom:28 }}>
              <Search size={15} color="var(--text-muted)" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }} />
              <input value={globalSearch}
                onChange={e => { setGlobalSearch(e.target.value); setGlobalSearchOpen(true); }}
                onFocus={() => setGlobalSearchOpen(true)}
                onBlur={() => setTimeout(() => setGlobalSearchOpen(false), 150)}
                placeholder="Search films, reviewers, or admins…"
                style={{ fontSize:14, padding:'11px 14px 11px 38px', width:'100%' }} />
              {globalSearchOpen && globalSearch.trim().length >= 2 && (
                <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, right:0, zIndex:50, background:'var(--bg-card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', boxShadow:'0 10px 30px rgba(0,0,0,0.3)', maxHeight:360, overflowY:'auto' }}>
                  {!globalSearchResults ? (
                    <div style={{ padding:16, fontSize:12, color:'var(--text-muted)' }}>Searching…</div>
                  ) : (globalSearchResults.films.length + globalSearchResults.reviewers.length + globalSearchResults.admins.length === 0) ? (
                    <div style={{ padding:16, fontSize:12, color:'var(--text-muted)' }}>No matches for "{globalSearch}"</div>
                  ) : (
                    <>
                      {globalSearchResults.films.length > 0 && (
                        <div style={{ padding:'8px 0' }}>
                          <div style={{ fontSize:9.5, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', padding:'2px 14px 6px' }}>Films</div>
                          {globalSearchResults.films.map(f => (
                            <button key={f.id} onMouseDown={() => { openSubmissionById(f.id, 'details', 'dashboard'); setGlobalSearch(''); setGlobalSearchOpen(false); }}
                              style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', background:'none', border:'none', padding:'8px 14px', cursor:'pointer', textAlign:'left', fontFamily:'var(--font)' }}
                              onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                              <span style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>{f.title} <span style={{ color:'var(--text-muted)', fontWeight:400 }}>· {f.director_name}</span></span>
                              <span style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', fontWeight:700 }}>{f.status}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {globalSearchResults.reviewers.length > 0 && (
                        <div style={{ padding:'8px 0', borderTop: globalSearchResults.films.length ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontSize:9.5, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', padding:'2px 14px 6px' }}>Reviewers</div>
                          {globalSearchResults.reviewers.map(r => (
                            <button key={r.id} onMouseDown={() => { setTab('teams'); setTeamsSubTab('reviewer'); setGlobalSearch(''); setGlobalSearchOpen(false); }}
                              style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', background:'none', border:'none', padding:'8px 14px', cursor:'pointer', textAlign:'left', fontFamily:'var(--font)' }}
                              onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                              <span style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>{r.name} <span style={{ color:'var(--text-muted)', fontWeight:400 }}>· {r.email}</span></span>
                              {!r.is_active && <span style={{ fontSize:10, color:'var(--red)', fontWeight:700 }}>INACTIVE</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {globalSearchResults.admins.length > 0 && (
                        <div style={{ padding:'8px 0', borderTop: (globalSearchResults.films.length || globalSearchResults.reviewers.length) ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontSize:9.5, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', padding:'2px 14px 6px' }}>Admin Team</div>
                          {globalSearchResults.admins.map(a => (
                            <button key={a.id} onMouseDown={() => { setTab('teams'); setTeamsSubTab('admin'); setGlobalSearch(''); setGlobalSearchOpen(false); }}
                              style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', background:'none', border:'none', padding:'8px 14px', cursor:'pointer', textAlign:'left', fontFamily:'var(--font)' }}
                              onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                              <span style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>{a.name} <span style={{ color:'var(--text-muted)', fontWeight:400 }}>· {a.email}</span></span>
                              <span style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', fontWeight:700 }}>{a.role==='super_admin'?'Super Admin':'Admin'}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {!dashboardData ? <LoadingState label="Loading dashboard…" minHeight={160} /> : (
              <>
                <div className="section-label" style={{ marginBottom:10 }}>Needs Your Attention</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14, marginBottom:28 }}>
                  {[
                    ['Unassigned Films', dashboardData.attention.unassignedFilms, 'var(--amber)', 'reviews', 'No reviewer assigned yet'],
                    ['Ready for Decision', dashboardData.attention.readyForDecision, 'var(--pink)', 'reviews', 'All reviewers finished — awaiting Approve/Not Selected'],
                    ['Deliveries Awaiting QC', dashboardData.attention.deliveriesAwaitingQc, 'var(--blue)', 'deliveries', 'Filmmaker submitted, needs review'],
                  ].map(([label, val, color, jumpTab, hint]) => (
                    <button key={label} onClick={() => setTab(jumpTab)}
                      className="card" style={{ padding:18, textAlign:'left', cursor:'pointer', border:`1.5px solid ${val>0 ? `${color}55` : 'var(--border)'}`, background: val>0 ? `${color}11` : 'var(--bg-card)' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                        <span style={{ fontWeight:900, fontSize:30, color: val>0 ? color : 'var(--text-muted)' }}>{val}</span>
                        <ArrowRight size={15} color="var(--text-muted)" />
                      </div>
                      <div style={{ fontSize:12.5, fontWeight:700, color:'var(--text)', marginBottom:3 }}>{label}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{hint}</div>
                    </button>
                  ))}
                </div>

                <div className="section-label" style={{ marginBottom:10 }}>Overview</div>
                <div className="reviewer-stats-grid" style={{ marginBottom:28 }}>
                  {[
                    ['Total Submissions', dashboardData.stats.total, 'var(--pink)', true],
                    ['Pending', dashboardData.stats.pending, 'var(--amber)'],
                    ['Approved', dashboardData.stats.approved, 'var(--green)'],
                    ['Approval Rate', `${dashboardData.stats.approval_rate}%`, 'var(--blue)'],
                    ['Avg Score', `★ ${dashboardData.avg_score}`, 'var(--text)'],
                  ].map(([label, val, color, showDelta]) => {
                    const delta = showDelta ? dashboardData.weeklyDelta.thisWeek - dashboardData.weeklyDelta.lastWeek : null;
                    return (
                    <div key={label} className="card" style={{ padding:'14px 18px' }}>
                      <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                        <div style={{ fontWeight:900, fontSize:24, color }}>{val}</div>
                        {showDelta && (
                          <span style={{ fontSize:10.5, fontWeight:800, color: delta > 0 ? 'var(--green)' : delta < 0 ? 'var(--red)' : 'var(--text-muted)' }}>
                            {delta > 0 ? '↑' : delta < 0 ? '↓' : '–'} {Math.abs(delta)} this week
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>{label}</div>
                    </div>
                    );
                  })}
                </div>

                <div className="dashboard-chart-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:28 }}>
                  <div className="card" style={{ padding:18 }}>
                    <div className="section-label" style={{ marginBottom:14 }}>Monthly Submissions</div>
                    <MiniBarChart color="var(--pink)" data={dashboardData.monthlyTrend.map(m => ({ label: new Date(m.month+'-02').toLocaleDateString('en-IN',{month:'short'}), count: m.count }))} />
                  </div>
                  <div className="card" style={{ padding:18 }}>
                    <div className="section-label" style={{ marginBottom:14 }}>Score Distribution</div>
                    <MiniBarChart color="var(--blue)" data={dashboardData.scoreDistribution.map(s => ({ label: s.bucket, count: s.count }))} />
                  </div>
                </div>

                <div className="dashboard-chart-grid" style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:16, marginBottom:28, alignItems:'start' }}>
                  <div className="card" style={{ padding:18 }}>
                    <div className="section-label" style={{ marginBottom:14 }}>Pipeline Funnel</div>
                    {(() => {
                      const stages = [
                        ['Submitted', dashboardData.funnel.submitted, 'var(--text-muted)'],
                        ['Film Opened', dashboardData.funnel.opened, 'var(--text-muted)'],
                        ['Under Review', dashboardData.funnel.under_review, 'var(--blue)'],
                        ['Shortlisted', dashboardData.funnel.shortlisted, 'var(--pink)'],
                        ['Decided', dashboardData.funnel.decided, 'var(--amber)'],
                        ['In Delivery', dashboardData.funnel.in_delivery, 'var(--purple)'],
                        ['Published', dashboardData.funnel.published, 'var(--green)'],
                      ];
                      const max = Math.max(1, ...stages.map(s => s[1]));
                      return stages.map(([label, count, color]) => (
                        <div key={label} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:9 }}>
                          <span style={{ fontSize:11.5, color:'var(--text-secondary)', width:90, flexShrink:0 }}>{label}</span>
                          <div style={{ flex:1, height:14, background:'var(--bg-elevated)', borderRadius:4, overflow:'hidden' }}>
                            <div style={{ width:`${(count/max)*100}%`, height:'100%', background:color, borderRadius:4, transition:'width 0.2s', minWidth: count>0?4:0 }} />
                          </div>
                          <span style={{ fontSize:12, fontWeight:800, color:'var(--text)', width:20, textAlign:'right', flexShrink:0 }}>{count}</span>
                        </div>
                      ));
                    })()}
                    <p style={{ fontSize:10.5, color:'var(--text-muted)', marginTop:10 }}>"Decided" counts every film ever approved/rejected — In Delivery/Published are subsets of decided+approved, not additional totals.</p>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                    <div className="card" style={{ padding:'14px 18px' }}>
                      <div style={{ fontWeight:900, fontSize:22, color:'var(--text)' }}>{dashboardData.turnaround.avgDaysToDecision ?? '—'}{dashboardData.turnaround.avgDaysToDecision != null && <span style={{ fontSize:13, fontWeight:600, color:'var(--text-muted)' }}> days</span>}</div>
                      <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>Avg. Submission → Decision</div>
                      <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:4 }}>Approximate — based on last-updated time, not a dedicated decision timestamp.</div>
                    </div>
                    <div className="card" style={{ padding:'14px 18px' }}>
                      <div style={{ fontWeight:900, fontSize:22, color:'var(--text)' }}>{dashboardData.turnaround.avgDaysQc ?? '—'}{dashboardData.turnaround.avgDaysQc != null && <span style={{ fontSize:13, fontWeight:600, color:'var(--text-muted)' }}> days</span>}</div>
                      <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>Avg. Delivery → QC Check</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── REVIEWS ── */}
        {tab==='reviews' && (
          <div>
            <h2 style={{ fontWeight:900, fontSize:26, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Reviews</h2>
            <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:20 }}>Every pending film currently in the reviewer pipeline — which ones are unassigned, in progress, and ready for a decision.</p>

            <input value={reviewsSearch} onChange={e => setReviewsSearch(e.target.value)} placeholder="Search title, genre, language…"
              style={{ fontSize:13, padding:'8px 12px', width:280, marginBottom:14 }} />

            <div className="card" style={{ overflowX:'auto', overflowY:'hidden' }}>
              <div className="admin-grid-header" style={{ gridTemplateColumns:'1fr 160px 160px 190px 130px', padding:'11px 14px', background:'rgba(255,45,139,0.08)', borderBottom:'2px solid var(--pink)', fontSize:10.5, color:'var(--text)', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', alignItems:'center' }}>
                <span>Film</span><span>Genre / Language</span><span>Reviewers</span><span>Status</span><span>Submitted</span>
              </div>
              {reviewsFilms.length===0 && <EmptyState icon={Star} title="Nothing in the review pipeline" subtitle="Pending films needing a reviewer assignment or currently under review show up here." />}
              {reviewsFilms.map(f => {
                const total = f.assignments.length;
                const completed = f.assignments.filter(a => a.status === 'completed').length;
                const unassigned = total === 0;
                const ready = total > 0 && completed === total;
                const color = unassigned ? 'var(--amber)' : ready ? 'var(--pink)' : 'var(--blue)';
                const label = unassigned ? 'Unassigned' : ready ? 'Ready for decision' : `${completed}/${total} completed`;
                // Aging signal — a film sitting untouched for weeks looked
                // identical to one submitted yesterday before this; escalates
                // the "Submitted" timestamp's own color independently of the
                // status color above so the two don't compete for attention.
                const daysOld = (Date.now() - new Date(f.created_at).getTime()) / 86400000;
                const ageColor = daysOld >= 21 ? 'var(--red)' : daysOld >= 7 ? 'var(--amber)' : 'var(--text-muted)';
                return (
                  <div key={f.id} onClick={() => openSubmissionById(f.id, 'reviews', 'reviews')}
                    className="admin-grid-row"
                    style={{ display:'grid', gridTemplateColumns:'1fr 160px 160px 190px 130px', padding:'16px 14px', borderBottom:'1px solid var(--border)', borderLeft:`3px solid ${color}`, background: ready ? 'rgba(255,45,139,0.10)' : 'transparent', cursor:'pointer', alignItems:'center', transition:'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = ready ? 'rgba(255,45,139,0.16)' : 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ready ? 'rgba(255,45,139,0.10)' : 'transparent'}>
                    <span data-label="Film" style={{ fontWeight: ready ? 800 : 600, color:'var(--text)', fontSize:13.5, display:'flex', alignItems:'center', gap:8 }}>
                      {ready && <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--pink)', flexShrink:0 }} />}
                      {f.title}
                    </span>
                    <span data-label="Genre / Language" style={{ fontSize:12.5, color:'var(--text-secondary)' }}>{f.genre} · {f.language}</span>
                    <span data-label="Reviewers" style={{ fontSize:12.5, color:'var(--text-secondary)' }}>
                      {unassigned ? '—' : f.assignments.map(a => a.reviewer_name).join(', ')}
                    </span>
                    <span data-label="Status" style={{ fontSize:12, fontWeight:800, color, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</span>
                    <span data-label="Submitted" style={{ fontSize:11.5, color:ageColor, fontWeight: daysOld>=21 ? 700 : 400 }}>{timeAgo(f.created_at)}</span>
                  </div>
                );
              })}
            </div>

            {reviewsPagination.totalPages > 1 && (
              <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:14, marginTop:16 }}>
                <button onClick={() => setReviewsPage(p => Math.max(1,p-1))} disabled={reviewsPage<=1} className="btn-ghost" style={{ padding:'6px 14px', fontSize:12 }}>← Prev</button>
                <span style={{ fontSize:12, color:'var(--text-muted)' }}>Page {reviewsPage} of {reviewsPagination.totalPages}</span>
                <button onClick={() => setReviewsPage(p => p+1)} disabled={reviewsPage>=reviewsPagination.totalPages} className="btn-ghost" style={{ padding:'6px 14px', fontSize:12 }}>Next →</button>
              </div>
            )}
          </div>
        )}

        {/* ── DELIVERIES ── */}
        {tab==='deliveries' && (
          <div>
            <h2 style={{ fontWeight:900, fontSize:26, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Deliveries</h2>
            <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:20 }}>Every film that has entered the post-approval delivery pipeline, whether or not the licensing agreement was actually signed.</p>

            <input value={deliveriesSearch} onChange={e => setDeliveriesSearch(e.target.value)} placeholder="Search title, filmmaker name, or email…"
              style={{ fontSize:13, padding:'8px 12px', width:280, marginBottom:14, display:'block' }} />

            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
              {[['','All'],['requested','Requested'],['submitted','Submitted — needs QC'],['qc_failed','QC Failed'],['qc_passed','QC Passed'],['published','Published']].map(([val,label]) => (
                <button key={val} onClick={() => setDeliveriesStatusFilter(val)}
                  style={{ background: deliveriesStatusFilter===val ? 'var(--pink-glow)' : 'var(--bg-elevated)', color: deliveriesStatusFilter===val ? 'var(--pink)' : 'var(--text-secondary)', border:`1.5px solid ${deliveriesStatusFilter===val ? 'var(--pink-glow-strong)' : 'var(--border)'}`, borderRadius:'var(--r-sm)', padding:'7px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="card" style={{ overflowX:'auto', overflowY:'hidden' }}>
              <div className="admin-grid-header" style={{ gridTemplateColumns:'1fr 190px 170px 130px 150px 120px', padding:'11px 14px', background:'rgba(255,45,139,0.08)', borderBottom:'2px solid var(--pink)', fontSize:10.5, color:'var(--text)', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', alignItems:'center' }}>
                <span>Film</span><span>Filmmaker</span><span>Delivery Status</span><span>Method</span><span>Agreement</span><span>Updated</span>
              </div>
              {deliveries.length===0 && <EmptyState icon={Truck} title="No deliveries yet" subtitle="Films show up here once an admin unlocks delivery on an approved film." />}
              {deliveries.map(d => {
                const statusMap = {
                  requested: { color:'var(--amber)', label:'Requested' },
                  submitted: { color:'var(--pink)', label:'Submitted' },
                  qc_failed: { color:'var(--red)', label:'QC Failed' },
                  qc_passed: { color:'var(--green)', label:'QC Passed' },
                  published: { color:'var(--blue)', label:'Published' },
                };
                const s = statusMap[d.delivery_status] || { color:'var(--text-muted)', label:d.delivery_status };
                const needsAttention = d.delivery_status === 'submitted';
                const agreementSkipped = d.agreement_status !== 'signed';
                // Aging only escalates while something's actually waiting on
                // you (submitted, not yet QC'd) — an old "Published" or
                // "Requested" timestamp isn't neglect, it's just history.
                const daysWaiting = (Date.now() - new Date(d.updated_at).getTime()) / 86400000;
                const ageColor = needsAttention ? (daysWaiting >= 14 ? 'var(--red)' : daysWaiting >= 3 ? 'var(--amber)' : 'var(--text-muted)') : 'var(--text-muted)';
                return (
                  <div key={d.id} onClick={() => openSubmissionById(d.submission_id, 'delivery', 'deliveries')}
                    className="admin-grid-row"
                    style={{ display:'grid', gridTemplateColumns:'1fr 190px 170px 130px 150px 120px', padding:'16px 14px', borderBottom:'1px solid var(--border)', borderLeft:`3px solid ${s.color}`, background: needsAttention ? 'rgba(255,45,139,0.10)' : 'transparent', cursor:'pointer', alignItems:'center', transition:'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = needsAttention ? 'rgba(255,45,139,0.16)' : 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = needsAttention ? 'rgba(255,45,139,0.10)' : 'transparent'}>
                    <span data-label="Film" style={{ fontWeight: needsAttention ? 800 : 600, color:'var(--text)', fontSize:13.5, display:'flex', alignItems:'center', gap:8 }}>
                      {needsAttention && <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--pink)', flexShrink:0 }} />}
                      {d.title}
                    </span>
                    <span data-label="Filmmaker" style={{ fontSize:12.5, color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.director_name || d.director_email}</span>
                    <span data-label="Delivery Status" style={{ fontSize:12, fontWeight:800, color:s.color, textTransform:'uppercase', letterSpacing:'0.04em' }}>{s.label}</span>
                    <span data-label="Method" style={{ fontSize:12.5, color:'var(--text-secondary)' }}>{d.delivery_method==='drive_link' ? 'Drive Link' : 'Physical'}</span>
                    <span data-label="Agreement">
                      {agreementSkipped
                        ? <span style={{ fontSize:10.5, fontWeight:800, color:'var(--amber)', background:'rgba(255,180,0,0.12)', border:'1px solid rgba(255,180,0,0.35)', borderRadius:20, padding:'3px 9px' }}>⚠ Skipped</span>
                        : <span style={{ fontSize:10.5, fontWeight:800, color:'var(--green)', background:'rgba(0,200,150,0.1)', border:'1px solid rgba(0,200,150,0.3)', borderRadius:20, padding:'3px 9px' }}>Signed</span>}
                    </span>
                    <span data-label="Updated" style={{ fontSize:11.5, color:ageColor, fontWeight: needsAttention && daysWaiting>=14 ? 700 : 400 }}>{timeAgo(d.updated_at)}</span>
                  </div>
                );
              })}
            </div>

            {deliveriesPagination.total > 50 && (
              <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:14, marginTop:16 }}>
                <button onClick={() => setDeliveriesPage(p => Math.max(1,p-1))} disabled={deliveriesPage<=1} className="btn-ghost" style={{ padding:'6px 14px', fontSize:12 }}>← Prev</button>
                <span style={{ fontSize:12, color:'var(--text-muted)' }}>Page {deliveriesPage} of {Math.ceil(deliveriesPagination.total/50)}</span>
                <button onClick={() => setDeliveriesPage(p => p+1)} disabled={deliveriesPage>=Math.ceil(deliveriesPagination.total/50)} className="btn-ghost" style={{ padding:'6px 14px', fontSize:12 }}>Next →</button>
              </div>
            )}
          </div>
        )}

        {/* ── ANALYTICS ── */}
        {tab==='analytics' && analytics && (
          <div>
            <h2 style={{ fontWeight:900, fontSize:26, color:'var(--text)', marginBottom:24, letterSpacing:'-0.01em' }}>Analytics</h2>
            <div className="reviewer-stats-grid" style={{ marginBottom:14 }}>
              {[['Approval Rate',`${analytics.stats.approval_rate}%`,'var(--green)'],['Avg Score',`★ ${analytics.avg_score}`,'var(--pink)'],['Reviewed',analytics.stats.total_reviewed,'var(--blue)'],['Total',analytics.stats.total,'var(--text)'],['Needs Correction',analytics.needs_correction||0,'var(--amber)']].map(([l,v,c]) => (
                <div key={l} className="card" style={{ padding:18 }}>
                  <div style={{ fontWeight:900, fontSize:28, color:c, marginBottom:3 }}>{v}</div>
                  <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>{l}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom:24 }}>
              <button onClick={() => setShowHiddenStats(s => !s)}
                style={{ background:'none', border:'1px solid var(--border)', borderRadius:20, padding:'4px 10px', fontSize:10, fontWeight:700, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font)', display:'inline-flex', alignItems:'center', gap:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                {showHiddenStats ? <EyeOff size={11} /> : <Eye size={11} />}
                {showHiddenStats ? 'Hide' : 'Show'} deleted / archived counts
              </button>
              {showHiddenStats && (
                <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                  <span className="badge" style={{ background:'rgba(255,68,68,0.1)', color:'var(--red)', border:'1px solid rgba(255,68,68,0.25)' }}>
                    {analytics.filmmaker_deleted_count||0} deleted by filmmaker
                  </span>
                  <span className="badge" style={{ background:'var(--bg-elevated)', color:'var(--text-muted)', border:'1px solid var(--border)' }}>
                    {analytics.archived_count||0} archived
                  </span>
                </div>
              )}
            </div>

            <div className="admin-two-col" style={{ gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
              <BarChart title="By Genre" data={analytics.by_genre} keyField="genre" />
              <BarChart title="By Language" data={analytics.by_language} keyField="language" />
            </div>
            <div className="card" style={{ padding:22 }}>
              <div className="section-label" style={{ marginBottom:14 }}>Status Breakdown</div>
              <div style={{ display:'flex', height:24, borderRadius:'var(--r-sm)', overflow:'hidden', gap:2 }}>
                {[['pending','var(--amber)',analytics.stats.pending],['approved','var(--green)',analytics.stats.approved],['rejected','var(--red)',analytics.stats.rejected]].map(([l,c,v]) => {
                  const pct = analytics.stats.total > 0 ? (v/analytics.stats.total*100) : 0;
                  return pct > 0 ? <div key={l} style={{ background:c, width:`${pct}%`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, color:'#fff' }}>{Math.round(pct)}%</div> : null;
                })}
              </div>
              <div style={{ display:'flex', gap:20, marginTop:10 }}>
                {[['Pending','var(--amber)',analytics.stats.pending],['Approved','var(--green)',analytics.stats.approved],['Not Selected','var(--red)',analytics.stats.rejected]].map(([l,c,v]) => (
                  <div key={l} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-secondary)', fontWeight:500 }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:c }} />{l}: <strong style={{ color:c }}>{v}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CONTENT (Banners + Seasons + Guidelines) ── */}
        {tab==='content' && (
          <div>
            <div style={{ marginBottom:22, borderBottom:'1.5px solid var(--border)' }}>
              <TabBar tabs={CONTENT_TABS} active={contentSubTab} onChange={setContentSubTab} />
            </div>

        {/* ── SEASONS ── */}
        {contentSubTab==='seasons' && (
          <div className="admin-two-col" style={{ gridTemplateColumns:'1fr 320px', gap:28 }}>
            <div>
              <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:16, letterSpacing:'-0.01em' }}>Seasons</h2>
              <div className="card" style={{ padding:'11px 16px', marginBottom:12, display:'flex', alignItems:'center', gap:8, background:'rgba(0,200,150,0.04)', borderColor:'rgba(0,200,150,0.2)' }}>
                <span style={{ color:'var(--green)', fontSize:16 }}>●</span>
                <span style={{ fontSize:12, color:'var(--text)', fontWeight:600 }}>Shortflix accepts films outside of seasons year-round</span>
              </div>
              {seasons.map(s => (
                <div key={s.id} className="card" style={{ padding:18, marginBottom:10, borderColor:s.is_active?'rgba(0,200,150,0.3)':'var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                        <span style={{ fontSize:15, fontWeight:800, color:'var(--text)' }}>{s.name}</span>
                        {s.is_active && <span className="badge badge-green">Active</span>}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:500 }}>
                        {s.start_date?new Date(s.start_date).toLocaleDateString():'—'} → {s.end_date?new Date(s.end_date).toLocaleDateString():'—'}
                        <span style={{ marginLeft:10 }}>{s.submission_count} films</span>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      {!s.is_active && <button onClick={async () => { await API.put(`/api/admin/seasons/${s.id}/activate`); API.get('/api/admin/seasons').then(r=>setSeasons(r.data.seasons)); }} className="btn-ghost" style={{ fontSize:11, padding:'5px 10px', color:'var(--green)', borderColor:'rgba(0,200,150,0.3)' }}>Set Active</button>}
                      {s.is_active && <button onClick={async () => { if(await confirm('Deactivate this season? New submissions will go back into the general year-round pool instead of this season.', { title:'Deactivate season?', confirmText:'Deactivate' })){ await API.put(`/api/admin/seasons/${s.id}/deactivate`); API.get('/api/admin/seasons').then(r=>setSeasons(r.data.seasons)); }}} className="btn-ghost" style={{ fontSize:11, padding:'5px 10px', color:'var(--amber)', borderColor:'rgba(255,181,71,0.3)' }}>Deactivate</button>}
                      <button onClick={async () => { if(await confirm('Delete this season? This cannot be undone.', { title:'Delete season?', confirmText:'Delete' })){ await API.delete(`/api/admin/seasons/${s.id}`); setSeasons(p=>p.filter(x=>x.id!==s.id)); }}} style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'5px 8px', fontSize:11, cursor:'pointer', fontFamily:'var(--font)' }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding:22, height:'fit-content' }}>
              <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)', marginBottom:18 }}>Create Season</h3>
              {[['name','Season Name','text','e.g. Season 3 — Jan 2026'],['start_date','Start Date','date',''],['end_date','End Date','date','']].map(([k,l,t,p]) => (
                <div key={k} style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>{l}</label>
                  <input type={t} value={newSeason[k]} onChange={e => setNewSeason(f=>({...f,[k]:e.target.value}))} placeholder={p} />
                </div>
              ))}
              <button onClick={async () => { await API.post('/api/admin/seasons',newSeason); API.get('/api/admin/seasons').then(r=>setSeasons(r.data.seasons)); setNewSeason({name:'',start_date:'',end_date:''}); }} className="btn-primary" style={{ width:'100%' }}>Create Season</button>
            </div>
          </div>
        )}

        {/* ── BANNERS ── */}
        {contentSubTab==='banners' && (
          <div className="admin-two-col" style={{ gridTemplateColumns:'1fr 340px', gap:28 }}>
            <div>
              <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:18, letterSpacing:'-0.01em' }}>Banners</h2>
              {banners.map(b => (
                <div key={b.id} className="card" style={{ padding:18, marginBottom:10, borderColor:b.is_active?'rgba(59,130,246,0.3)':'var(--border)' }}>
                  <div style={{ background:b.bg_color, color:b.text_color, borderRadius:'var(--r-md)', padding:'9px 14px', marginBottom:12, fontSize:13, fontWeight:600 }}>
                    {b.title}{b.subtitle&&<span style={{ opacity:0.8, fontWeight:400 }}> · {b.subtitle}</span>}
                    {b.cta_text&&<span style={{ float:'right', fontWeight:800 }}>{b.cta_text} →</span>}
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    {b.is_active
                      ? <button onClick={async () => { await API.put(`/api/admin/banners/${b.id}/deactivate`); API.get('/api/admin/banners').then(r=>setBanners(r.data.banners)); }} className="btn-ghost" style={{ fontSize:11, color:'var(--amber)', borderColor:'rgba(255,181,71,0.3)' }}>Deactivate</button>
                      : <button onClick={async () => { await API.put(`/api/admin/banners/${b.id}/activate`); API.get('/api/admin/banners').then(r=>setBanners(r.data.banners)); }} className="btn-ghost" style={{ fontSize:11, color:'var(--blue)', borderColor:'rgba(59,130,246,0.3)' }}>Activate</button>}
                    <button onClick={async () => { await API.delete(`/api/admin/banners/${b.id}`); setBanners(p=>p.filter(x=>x.id!==b.id)); }} style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'5px 8px', fontSize:11, cursor:'pointer', fontFamily:'var(--font)' }}>Delete</button>
                    {b.is_active&&<span className="badge badge-green" style={{ marginLeft:'auto' }}>● Live</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding:22, height:'fit-content' }}>
              <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)', marginBottom:14 }}>Create Banner</h3>
              <div style={{ background:newBanner.bg_color, color:newBanner.text_color, borderRadius:'var(--r-md)', padding:'9px 14px', marginBottom:14, fontSize:13, fontWeight:600, minHeight:34 }}>
                {newBanner.title||'Preview'}{newBanner.subtitle&&<span style={{ opacity:0.8, fontWeight:400 }}> · {newBanner.subtitle}</span>}
                {newBanner.cta_text&&<span style={{ float:'right', fontWeight:800 }}>{newBanner.cta_text} →</span>}
              </div>
              {[['title','Title *','text'],['subtitle','Subtitle','text'],['cta_text','Button Text','text'],['cta_link','Button Link','url']].map(([k,l,t]) => (
                <div key={k} style={{ marginBottom:10 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.08em' }}>{l}</label>
                  <input type={t} value={newBanner[k]} onChange={e => setNewBanner(f=>({...f,[k]:e.target.value}))} style={{ fontSize:13 }} />
                </div>
              ))}
              <div style={{ display:'flex', gap:10, marginBottom:14 }}>
                {[['bg_color','BG Color'],['text_color','Text Color']].map(([k,l]) => (
                  <div key={k} style={{ flex:1 }}>
                    <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.08em' }}>{l}</label>
                    <input type="color" value={newBanner[k]} onChange={e => setNewBanner(f=>({...f,[k]:e.target.value}))} style={{ width:'100%', height:34, borderRadius:'var(--r-sm)', border:'1.5px solid var(--border)', cursor:'pointer', background:'none', padding:2 }} />
                  </div>
                ))}
              </div>
              <button onClick={async () => { await API.post('/api/admin/banners',newBanner); API.get('/api/admin/banners').then(r=>setBanners(r.data.banners)); setNewBanner({title:'',subtitle:'',cta_text:'',cta_link:'',bg_color:'#FF2D8B',text_color:'#ffffff'}); }} className="btn-primary" style={{ width:'100%' }}>Create Banner</button>
            </div>
          </div>
        )}

        {/* ── LOGIN BACKGROUND ── */}
        {contentSubTab==='login-bg' && (
          <div style={{ maxWidth:720 }}>
            <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Login Background</h2>
            <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:18, fontWeight:400 }}>Each season can have its own background image on the login page. The image for the currently <strong style={{ color:'var(--text)' }}>active season</strong> is the one filmmakers actually see — the <strong style={{ color:'var(--text)' }}>General Pool</strong> background is the fallback used whenever no season is active. Click the image icon to upload or edit a background.</p>
            {[{ id:'general', name:'General Pool (no season active)', is_active: false, isGeneral: true }, ...seasons].map(s => {
              const bg = s.isGeneral ? loginBackgrounds.find(b => b.season_id === null) : loginBackgrounds.find(b => b.season_id === s.id);
              return (
                <div key={s.id} className="card" style={{ padding:18, marginBottom:10, borderColor:s.is_active?'rgba(0,200,150,0.3)':s.isGeneral?'rgba(255,45,139,0.25)':'var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:14 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      {bg
                        ? <img src={bg.image_url} alt="" style={{ width:64, height:40, objectFit:'cover', borderRadius:'var(--r-sm)', border:'1.5px solid var(--border)' }} />
                        : <div style={{ width:64, height:40, borderRadius:'var(--r-sm)', border:'1.5px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)' }}><Image size={14} /></div>}
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:14, fontWeight:800, color:'var(--text)' }}>{s.name}</span>
                          {s.is_active && <span className="badge badge-green">Active</span>}
                        </div>
                        <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:500 }}>{bg ? `${bg.overlay_style === 'flat' ? 'Flat' : 'Gradient'} overlay ${bg.overlay_opacity}% · ${bg.focal_point}` : 'No background set'}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => openBgModal(s)} title="Upload / edit background" style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--pink)', borderRadius:'var(--r-sm)', width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                        <Image size={14} />
                      </button>
                      {bg && <button onClick={async () => { if(await confirm('Remove this background?', { title:'Remove login background?', confirmText:'Remove' })){ await API.delete(`/api/admin/login-background/${bg.id}`); API.get('/api/admin/login-backgrounds').then(r=>setLoginBackgrounds(r.data.backgrounds)); }}} style={{ background:'none', color:'var(--text-muted)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'5px 8px', fontSize:11, cursor:'pointer', fontFamily:'var(--font)' }}>Delete</button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── LOGIN BACKGROUND UPLOAD MODAL ── */}
        {bgModalSeasonId && (() => {
          const modalSeason = bgModalSeasonId === 'general' ? { name: 'General Pool' } : seasons.find(s => s.id === bgModalSeasonId);
          return (
            <div style={{ position:'fixed', inset:0, background:'var(--overlay)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
              <div className="card" style={{ width:440, maxHeight:'88vh', overflowY:'auto', padding:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
                  <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)' }}>Login Background — {modalSeason?.name}</h3>
                  <button onClick={closeBgModal} style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:14 }}>✕</button>
                </div>

                <div style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>Image *</label>
                  <input type="file" accept="image/*" onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setNewBg(f => ({ ...f, file, previewUrl: URL.createObjectURL(file) }));
                  }} style={{ fontSize:12 }} />
                </div>

                <div style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>Overlay Darkness — {newBg.overlay_opacity}%</label>
                  <input type="range" min={0} max={80} value={newBg.overlay_opacity} onChange={e => setNewBg(f=>({...f,overlay_opacity:Number(e.target.value)}))} style={{ width:'100%' }} />
                </div>

                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>Focal Point</label>
                  <select value={newBg.focal_point} onChange={e => setNewBg(f=>({...f,focal_point:e.target.value}))}>
                    <option value="top">Top</option>
                    <option value="center">Center</option>
                    <option value="bottom">Bottom</option>
                  </select>
                </div>

                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.08em' }}>Overlay Style</label>
                  <select value={newBg.overlay_style} onChange={e => setNewBg(f=>({...f,overlay_style:e.target.value}))}>
                    <option value="gradient">Gradient (recommended — keeps text readable, shows more of the image)</option>
                    <option value="flat">Flat (uniform darkness across the whole image)</option>
                  </select>
                </div>

                {/* Live preview */}
                <div style={{ marginBottom:18 }}>
                  <label style={{ display:'block', fontSize:10, fontWeight:800, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.08em' }}>Preview</label>
                  <div style={{
                    position:'relative', height:140, borderRadius:'var(--r-md)', overflow:'hidden', border:'1.5px solid var(--border)',
                    backgroundImage: newBg.previewUrl ? `url(${newBg.previewUrl})` : 'none',
                    backgroundColor: newBg.previewUrl ? 'transparent' : 'var(--bg-elevated)',
                    backgroundSize:'cover', backgroundPosition: newBg.focal_point,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    {!newBg.previewUrl && <span style={{ color:'var(--text-muted)', fontSize:11 }}>Choose an image to preview</span>}
                    {newBg.previewUrl && (
                      <div style={{
                        position:'absolute', inset:0,
                        background: newBg.overlay_style === 'gradient'
                          ? `linear-gradient(to right, rgba(0,0,0,${newBg.overlay_opacity/100}) 0%, rgba(0,0,0,${(newBg.overlay_opacity/100)*0.3}) 55%, rgba(0,0,0,0) 100%)`
                          : `rgba(0,0,0,${newBg.overlay_opacity/100})`,
                        display:'flex', alignItems:'center', justifyContent: newBg.overlay_style === 'gradient' ? 'flex-start' : 'center',
                      }}>
                        <span style={{ color:'#fff', fontSize:11, fontWeight:700, background:'rgba(0,0,0,0.35)', padding:'4px 10px', borderRadius:6, marginLeft: newBg.overlay_style === 'gradient' ? 12 : 0 }}>Login card would appear here</span>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={closeBgModal} className="btn-ghost" style={{ flex:1 }}>Cancel</button>
                  <button disabled={!newBg.file || bgUploading} onClick={async () => {
                    setBgUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('image', newBg.file);
                      formData.append('season_id', newBg.season_id);
                      formData.append('overlay_opacity', newBg.overlay_opacity);
                      formData.append('focal_point', newBg.focal_point);
                      formData.append('overlay_style', newBg.overlay_style);
                      await API.post('/api/admin/login-background', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      API.get('/api/admin/login-backgrounds').then(r => setLoginBackgrounds(r.data.backgrounds));
                      closeBgModal();
                    } catch (e) { showToast('Upload failed. Please try again.', 'error'); }
                    finally { setBgUploading(false); }
                  }} className="btn-primary" style={{ flex:2 }}>{bgUploading ? 'Uploading…' : 'Save'}</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── GUIDELINES ── */}
        {contentSubTab==='guidelines' && (
          <div style={{ maxWidth:720 }}>
            <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Submission Guidelines</h2>
            <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:20, fontWeight:400 }}>Appears at <strong style={{ color:'var(--text)' }}>/guidelines</strong> — publicly visible.</p>
            <textarea value={guidelines} onChange={e => setGuidelines(e.target.value)} rows={20} style={{ fontSize:13, lineHeight:1.8, resize:'vertical', padding:16 }} />
            <button onClick={async () => {
              try {
                await API.put('/api/admin/guidelines',{content:guidelines});
                showToast('Guidelines saved', 'success');
              } catch {
                showToast('Failed to save guidelines', 'error');
              }
            }} className="btn-primary" style={{ marginTop:12 }}>Save Guidelines</button>
          </div>
        )}

        {contentSubTab==='rejection-templates' && (
          <div>
            <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Rejection Templates</h2>
            <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:20, fontWeight:400, maxWidth:720 }}>
              Canned paragraphs a super admin can drop into the Summary Note when confirming a "Not Selected" decision — useful when reviewer feedback alone is thin, e.g. amateur or first-time submissions. Doesn't replace reviewer feedback, just gives a starting point for the closing note.
            </p>

            <div style={{ display:'flex', gap:24, alignItems:'flex-start', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 380px', minWidth:300 }}>
                {rejectionTemplates.length===0 && <p style={{ fontSize:13, color:'var(--text-muted)' }}>No templates yet — add your first one on the right.</p>}
                {rejectionTemplates.map(t => {
                  const expanded = expandedTemplateId === t.id;
                  return (
                    <div key={t.id} className="card" style={{ padding:0, marginBottom:8, overflow:'hidden' }}>
                      <button onClick={() => setExpandedTemplateId(expanded ? null : t.id)}
                        style={{ width:'100%', background:'none', border:'none', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, cursor:'pointer', textAlign:'left' }}>
                        <span style={{ fontSize:13.5, fontWeight:700, color:'var(--text)' }}>{t.label}</span>
                        <ChevronDown size={15} color="var(--text-muted)" style={{ flexShrink:0, transition:'transform 0.15s', transform: expanded ? 'rotate(180deg)' : 'none' }} />
                      </button>
                      {expanded && (
                        <div style={{ padding:'0 16px 16px' }}>
                          {editingTemplateId === t.id ? (
                            <>
                              <input value={editingTemplateDraft.label} onChange={e => setEditingTemplateDraft(d => ({ ...d, label:e.target.value }))} placeholder="Label" style={{ fontSize:13, marginBottom:8 }} />
                              <textarea value={editingTemplateDraft.body} onChange={e => setEditingTemplateDraft(d => ({ ...d, body:e.target.value }))} rows={4} style={{ fontSize:13, lineHeight:1.6, resize:'vertical', marginBottom:8 }} />
                              <div style={{ display:'flex', gap:8 }}>
                                <button onClick={async () => {
                                  try {
                                    await API.put(`/api/admin/rejection-templates/${t.id}`, editingTemplateDraft);
                                    setEditingTemplateId(null);
                                    loadRejectionTemplates();
                                    showToast('Template saved');
                                  } catch (err) { showToast(err.response?.data?.error || 'Failed to save', 'error'); }
                                }} className="btn-primary" style={{ padding:'7px 16px', fontSize:12 }}>Save</button>
                                <button onClick={() => setEditingTemplateId(null)} className="btn-ghost" style={{ padding:'7px 16px', fontSize:12 }}>Cancel</button>
                              </div>
                            </>
                          ) : (
                            <>
                              <p style={{ fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.6, whiteSpace:'pre-wrap', marginBottom:10 }}>{t.body}</p>
                              <div style={{ display:'flex', gap:6 }}>
                                <button onClick={() => { setEditingTemplateId(t.id); setEditingTemplateDraft({ label:t.label, body:t.body }); }}
                                  style={{ background:'none', color:'var(--blue)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'4px 10px', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>Edit</button>
                                <button onClick={async () => {
                                  const ok = await confirm(`Delete template "${t.label}"?`, { title:'Delete template?', confirmText:'Delete' });
                                  if (!ok) return;
                                  await API.delete(`/api/admin/rejection-templates/${t.id}`);
                                  if (expandedTemplateId === t.id) setExpandedTemplateId(null);
                                  loadRejectionTemplates();
                                }} style={{ background:'none', color:'var(--red)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'4px 10px', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>Delete</button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="card" style={{ padding:20, flex:'1 1 380px', minWidth:300 }}>
                <h3 style={{ fontWeight:800, fontSize:14, color:'var(--text)', marginBottom:12 }}>Add Template</h3>
                <input value={newTemplate.label} onChange={e => setNewTemplate(f => ({ ...f, label:e.target.value }))} placeholder="Label — e.g. 'Amateur — audio/technical issues'" style={{ fontSize:13, marginBottom:10 }} />
                <textarea value={newTemplate.body} onChange={e => setNewTemplate(f => ({ ...f, body:e.target.value }))} rows={8} placeholder="Thank you for submitting… " style={{ fontSize:13, lineHeight:1.6, resize:'vertical', marginBottom:10, width:'100%' }} />
                <button onClick={async () => {
                  if (!newTemplate.label.trim() || !newTemplate.body.trim()) return showToast('Label and body are required', 'error');
                  try {
                    await API.post('/api/admin/rejection-templates', newTemplate);
                    setNewTemplate({ label:'', body:'' });
                    loadRejectionTemplates();
                    showToast('Template added');
                  } catch (err) { showToast(err.response?.data?.error || 'Failed to add', 'error'); }
                }} className="btn-primary" style={{ width:'100%' }}>Add Template</button>
              </div>
            </div>
          </div>
        )}
          </div>
        )}

        {/* ── TEAMS (Admin Team + Reviewer Team, combined) ── */}
        {tab==='teams' && (
          <div>
            <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:18, letterSpacing:'-0.01em' }}>Teams</h2>

            <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'1.5px solid var(--border)', paddingBottom:0 }}>
              {(isSuperAdmin ? [['admin','Admin Team'],['reviewer','Reviewer Team'],['management','Reviewer Management']] : [['reviewer','Reviewer Team'],['management','Reviewer Management']]).map(([val,label]) => (
                <button key={val} onClick={() => setTeamsSubTab(val)}
                  style={{ background:'none', border:'none', borderBottom: teamsSubTab===val ? '2px solid var(--pink)' : '2px solid transparent', color: teamsSubTab===val ? 'var(--pink)' : 'var(--text-secondary)', fontWeight:800, fontSize:13, padding:'8px 4px', marginBottom:-2, cursor:'pointer', fontFamily:'var(--font)' }}>
                  {label}
                </button>
              ))}
            </div>

            {teamsSubTab==='reviewer' && <ReviewerManagementFull mode="roster" />}
            {teamsSubTab==='management' && <ReviewerManagementFull mode="management" />}

            {isSuperAdmin && teamsSubTab==='admin' && (
            <div style={{ display:'flex', gap:24, alignItems:'flex-start', flexWrap:'wrap' }}>
              <div className="admin-team-list" style={{ flex:'1 1 400px', minWidth:320 }}>
                {team.map(m => (
                  <div key={m.id} className="card" style={{ padding:'13px 18px', marginBottom:8, display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background: m.role==='super_admin' ? 'var(--purple)' : 'var(--pink)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0 }}>{(m.name||m.email)[0].toUpperCase()}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'flex', alignItems:'center', gap:6 }}>
                        {m.name||'Admin'}
                        <span className={`badge ${m.role==='super_admin' ? 'badge-purple' : ''}`} style={{ fontSize:9 }}>{m.role==='super_admin' ? 'Super Admin' : 'Admin'}</span>
                        {m.is_primary_super_admin && <span className="badge badge-pink" style={{ fontSize:9 }}>Primary</span>}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:400 }}>{m.email}</div>
                    </div>
                    {m.id===user?.id && <span className="badge badge-pink">You</span>}
                    {!m.is_primary_super_admin && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={async () => {
                          const newRole = m.role==='super_admin' ? 'admin' : 'super_admin';
                          await API.put(`/api/admin/team/${m.id}/role`, { role:newRole });
                          API.get('/api/admin/team').then(r=>setTeam(r.data.admins));
                        }} style={{ background:'none', color:'var(--blue)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'5px 10px', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>
                          {m.role==='super_admin' ? 'Demote' : 'Promote'}
                        </button>
                        <button onClick={async () => {
                          const ok = await confirm(`Remove ${m.name||m.email} from the admin team?`, { title: 'Remove team member?', confirmText: 'Remove' });
                          if (!ok) return;
                          await API.delete(`/api/admin/team/${m.id}`);
                          API.get('/api/admin/team').then(r=>setTeam(r.data.admins));
                        }} style={{ background:'none', color:'var(--red)', border:'1.5px solid var(--border)', borderRadius:'var(--r-sm)', padding:'5px 10px', fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)' }}>
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="card admin-team-form" style={{ padding:22, flex:'0 1 360px', minWidth:300 }}>
                <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)', marginBottom:16 }}>Add Admin</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:12 }}>
                  <input value={newMember.name} onChange={e => setNewMember(f=>({...f,name:e.target.value}))} placeholder="Name" />
                  <input value={newMember.email} onChange={e => setNewMember(f=>({...f,email:e.target.value}))} placeholder="Email" type="email" />
                  <select value={newMember.role||'admin'} onChange={e => setNewMember(f=>({...f,role:e.target.value}))}>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <button onClick={async () => { await API.post('/api/admin/team',newMember); API.get('/api/admin/team').then(r=>setTeam(r.data.admins)); setNewMember({name:'',email:''}); }} className="btn-primary" style={{ width:'100%' }}>Add Admin</button>
              </div>
            </div>
            )}
          </div>
        )}

        {/* ── NOTES & TASKS (aggregated across all films) ── */}
        {tab==='notes' && (
          <div style={{ maxWidth: 800 }}>
            <h2 style={{ fontWeight:900, fontSize:24, color:'var(--text)', marginBottom:6, letterSpacing:'-0.01em' }}>Notes & Tasks</h2>
            <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:20, fontWeight:400 }}>
              Every internal task and note across all films, in one place — click any row to jump straight to that film.
            </p>

            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <div className="section-label" style={{ margin:0 }}>Tasks</div>
              <select value={tasksFilter} onChange={e => setTasksFilter(e.target.value)} style={{ fontSize:11, padding:'5px 8px', width:'auto' }}>
                <option value="open">Open only</option>
                <option value="done">Done only</option>
                <option value="all">All</option>
              </select>
            </div>
            {notesLoading ? (
              <div className="card" style={{ padding:24, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Loading…</div>
            ) : allTasks.length === 0 ? (
              <div className="card" style={{ padding:24, textAlign:'center', color:'var(--text-muted)', fontSize:13, marginBottom:28 }}>
                {tasksFilter === 'open' ? 'No open tasks — nice.' : 'No tasks found.'}
              </div>
            ) : (
              <div style={{ marginBottom:28 }}>
                {allTasks.map(t => (
                  <div key={t.id} className="card" style={{ padding:'12px 16px', marginBottom:6, display:'flex', alignItems:'flex-start', gap:10 }}>
                    <input type="checkbox" checked={t.is_done} onChange={() => handleToggleTaskFromList(t.id)}
                      onClick={e => e.stopPropagation()} style={{ accentColor:'var(--pink)', marginTop:2 }} />
                    <div style={{ flex:1, cursor:'pointer' }} onClick={() => openSubmissionById(t.submission_id, 'details')}>
                      <div style={{ fontSize:13, color: t.is_done ? 'var(--text-muted)' : 'var(--text)', textDecoration: t.is_done ? 'line-through' : 'none', fontWeight:600 }}>
                        <span style={{ color:'var(--pink)', fontWeight:800, fontFamily:'monospace', fontSize:11 }}>{t.submission_number != null ? String(t.submission_number).padStart(3,'0') : '—'}</span>
                        {' – '}{t.submission_title} <span style={{ fontWeight:400, color:'var(--text-secondary)' }}>· {t.text}</span>
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
                        {t.is_done ? `Done by ${t.done_by_name || '—'} · ${timeAgo(t.done_at)}` : `Added by ${t.created_by_name || '—'} · ${timeAgo(t.created_at)}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="section-label" style={{ marginBottom:12 }}>Films with General Notes</div>
            {notesLoading ? null : allNotes.length === 0 ? (
              <div className="card" style={{ padding:24, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No films have notes yet.</div>
            ) : (
              allNotes.map(n => (
                <div key={n.id} className="card" onClick={() => openSubmissionById(n.id, 'details')} style={{ padding:'12px 16px', marginBottom:6, cursor:'pointer' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:4 }}>
                    <span style={{ color:'var(--pink)', fontWeight:800, fontFamily:'monospace', fontSize:11 }}>{n.submission_number != null ? String(n.submission_number).padStart(3,'0') : '—'}</span>
                    {' – '}{n.title}
                  </div>
                  <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.5, whiteSpace:'pre-wrap' }}>{n.admin_notes}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Updated {timeAgo(n.updated_at)}</div>
                </div>
              ))
            )}
          </div>
        )}

      </div>
      </div>

      {showSubmitBehalf && (
        <div style={{ position:'fixed', inset:0, background:'var(--overlay)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div className="card" style={{ width:560, maxHeight:'88vh', overflowY:'auto', padding:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)', display:'flex', alignItems:'center', gap:8 }}><Mail size={15} color="var(--pink)" /> Submit on Behalf of Filmmaker</h3>
              <button onClick={() => setShowSubmitBehalf(false)} style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}><X size={14} /></button>
            </div>
            <p style={{ color:'var(--text-muted)', fontSize:12, marginBottom:18 }}>For filmmakers who emailed their film in instead of using the real form. A confirmation email is sent to the contact email below — enter it exactly as they'll want to receive it.</p>

            <form onSubmit={handleSubmitBehalf}>
              <Field label="Film Title" required><input value={behalfForm.title} onChange={e => setBehalf('title', e.target.value)} /></Field>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}>
                  <Field label="Genre" required>
                    <select value={behalfForm.genre} onChange={e => setBehalf('genre', e.target.value)}>
                      <option value="">Select genre</option>{genreOptions.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </Field>
                </div>
                <div style={{ flex:1 }}>
                  <Field label="Language" required>
                    <select value={behalfForm.language} onChange={e => setBehalf('language', e.target.value)}>
                      <option value="">Select language</option>{SUBMIT_BEHALF_LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
              <Field label="Format" required>
                <div style={{ display:'flex', gap:10 }}>
                  {['horizontal', 'vertical', 'series'].map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setBehalf('format', f)}
                      style={{
                        flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                        border: behalfForm.format === f ? '1.5px solid var(--pink)' : '1.5px solid var(--border)',
                        background: behalfForm.format === f ? 'var(--pink-glow)' : 'var(--bg-card)',
                        color: behalfForm.format === f ? 'var(--pink)' : 'var(--text-muted)',
                        fontWeight: 700, fontSize: 13, textTransform: 'capitalize',
                      }}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </Field>
              {(behalfForm.format === 'series' || behalfForm.format === 'vertical') && (
                <div style={{ display:'flex', gap:10 }}>
                  <div style={{ flex:1 }}>
                    <Field label="Number of Episodes" required>
                      <input type="number" min="1" value={behalfForm.episode_count} onChange={e => setBehalf('episode_count', e.target.value)} placeholder="e.g. 8" />
                    </Field>
                  </div>
                  <div style={{ flex:1 }}>
                    <Field label="Season (optional)">
                      <input type="number" min="1" value={behalfForm.season_number} onChange={e => setBehalf('season_number', e.target.value)} placeholder="e.g. 1" />
                    </Field>
                  </div>
                </div>
              )}
              <Field label="Duration" required>
                <div style={{ display:'flex', gap:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase' }}>Hours</div>
                    <input type="number" min="0" value={behalfForm.duration_hours} onChange={e => setBehalf('duration_hours', e.target.value)} placeholder="0" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase' }}>Minutes</div>
                    <input type="number" min="0" max="59" value={behalfForm.duration_mins} onChange={e => setBehalf('duration_mins', e.target.value)} placeholder="0" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase' }}>Seconds</div>
                    <input type="number" min="0" max="59" value={behalfForm.duration_secs} onChange={e => setBehalf('duration_secs', e.target.value)} placeholder="0" />
                  </div>
                </div>
              </Field>
              <Field label="Synopsis" required><textarea value={behalfForm.synopsis} onChange={e => setBehalf('synopsis', e.target.value)} rows={3} /></Field>
              <Field label="Film Link" required hint="Google Drive, Vimeo, or YouTube"><input value={behalfForm.film_link} onChange={e => setBehalf('film_link', e.target.value)} placeholder="https://drive.google.com/..." /></Field>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}><Field label="Film Link Password"><input value={behalfForm.film_link_password} onChange={e => setBehalf('film_link_password', e.target.value)} /></Field></div>
                <div style={{ flex:1 }}><Field label="Trailer Link"><input value={behalfForm.trailer_link} onChange={e => setBehalf('trailer_link', e.target.value)} /></Field></div>
              </div>
              <Field label="Notes to Reviewers"><textarea value={behalfForm.notes_to_reviewers} onChange={e => setBehalf('notes_to_reviewers', e.target.value)} rows={2} /></Field>

              <hr className="divider" style={{ margin:'16px 0' }} />
              <div style={{ fontSize:11, fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Filmmaker Contact</div>
              <Field label="Director Name" required><input value={behalfForm.director_name} onChange={e => setBehalf('director_name', e.target.value)} /></Field>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}><Field label="Phone Number" required><input value={behalfForm.director_phone} onChange={e => setBehalf('director_phone', e.target.value)} /></Field></div>
                <div style={{ flex:1 }}><Field label="Contact Email" required hint="Confirmation email is sent here" hintMode="icon"><input type="email" value={behalfForm.director_email} onChange={e => setBehalf('director_email', e.target.value)} /></Field></div>
              </div>

              {seasons.some(s => s.is_active) && (
                <label style={{ display:'flex', gap:8, alignItems:'center', cursor:'pointer', marginBottom:16, fontSize:12, color:'var(--text-secondary)', fontWeight:600 }}>
                  <input type="checkbox" checked={behalfForm.is_general_submission} onChange={e => setBehalf('is_general_submission', e.target.checked)} style={{ accentColor:'var(--pink)' }} />
                  Submit to the general pool instead of the active season
                </label>
              )}

              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button type="button" onClick={() => setShowSubmitBehalf(false)} className="btn-ghost" style={{ flex:1 }}>Cancel</button>
                <button type="submit" disabled={behalfSubmitting} className="btn-primary" style={{ flex:2 }}>{behalfSubmitting ? 'Submitting…' : 'Submit & Send Confirmation'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selected && <ReviewDrawer key={selected.id} sub={selected} initialTab={pendingDrawerTab} onClose={() => setSelected(null)} onSave={() => {
          setSelected(null);
          loadSubmissions();
          if (tab === 'deliveries') loadDeliveries();
          if (tab === 'reviews') loadReviews();
          API.get('/api/admin/deliveries?status=submitted&limit=1').then(r => setDeliveriesNeedingAttention(r.data.pagination?.total || 0)).catch(() => {});
          API.get('/api/admin/reviewers/pending-films?limit=200').then(r => {
            const ready = (r.data.films || []).filter(f => f.assignments.length > 0 && f.assignments.every(a => a.status === 'completed')).length;
            setReviewsReadyCount(ready);
          }).catch(() => {});
        }}
        onUpdated={(updated) => {
          setSelected(s => (s && s.id === updated.id) ? { ...s, ...updated } : s);
          setSubmissions(subs => subs.map(s => s.id === updated.id ? { ...s, ...updated } : s));
        }}
        allReviewers={quickAssignReviewers} allGroups={quickAssignGroups} onFilterByFilmmaker={filterByFilmmaker} onTabChange={setPendingDrawerTab} onTabViewed={markTabViewed} />}

      {contextMenu && (() => {
        const sub = submissions.find(s => s.id === contextMenu.submissionId);
        const itemStyle = { display:'block', width:'100%', textAlign:'left', background:'none', border:'none', color:'var(--text)', fontSize:12, padding:'7px 8px', cursor:'pointer', fontFamily:'var(--font)', borderRadius:'var(--r-sm)' };
        const hoverIn = e => e.currentTarget.style.background='var(--bg-hover)';
        const hoverOut = e => e.currentTarget.style.background='none';
        // Flip the menu upward (anchored by its bottom edge to the click
        // point instead of its top) when there isn't enough room below —
        // same idea as the existing left-flip for the reviewer submenu,
        // just for the vertical axis. Also cap maxHeight + scroll as a
        // safety net so it can never run off either edge regardless.
        const spaceBelow = window.innerHeight - contextMenu.y;
        const spaceAbove = contextMenu.y;
        const flipUp = spaceBelow < 420 && spaceAbove > spaceBelow;
        return (
        <div onClick={e => e.stopPropagation()}
          style={{
            position:'fixed', left:contextMenu.x,
            ...(flipUp ? { bottom: spaceBelow, top: 'auto' } : { top: contextMenu.y }),
            maxHeight: (flipUp ? spaceAbove : spaceBelow) - 12, overflowY: 'auto',
            background:'var(--bg-card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', boxShadow:'0 8px 30px rgba(0,0,0,0.3)', zIndex:400, minWidth:220, padding:8,
          }}>
          <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em', padding:'4px 8px 8px' }}>
            "{contextMenu.submissionTitle}"
          </div>

          <button onClick={() => { if (sub) openSubmission(sub); setContextMenu(null); }}
            style={{ ...itemStyle, fontWeight:700 }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
            📂 Open Full Review
          </button>

          {/* Quick-Approve and Quick-Not-Selected both deliberately removed
              from this menu — final decisions now always go through the
              full drawer's decision modal (approving needs the App/YouTube
              picker; keeping both off the quick menu keeps the decision
              path consistent either way). */}
          {sub?.status === 'rejected' && (
            <button onClick={() => handleQuickResubmission(contextMenu.submissionId)} style={{ ...itemStyle, color:'var(--purple)' }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
              🔄 Allow Resubmission
            </button>
          )}

          <div style={{ height:1, background:'var(--border)', margin:'6px 0' }} />

          {!contextMenuAddingTask ? (
            <button onClick={() => setContextMenuAddingTask(true)} style={itemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
              📝 Add Task
            </button>
          ) : (
            <div style={{ padding:'4px 8px' }}>
              <input autoFocus value={contextMenuTaskText} onChange={e => setContextMenuTaskText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleContextMenuAddTask(contextMenu.submissionId); if (e.key === 'Escape') setContextMenuAddingTask(false); }}
                placeholder="Task text, then Enter..." style={{ width:'100%', fontSize:11, padding:'5px 8px', boxSizing:'border-box' }} />
            </div>
          )}
          {sub?.film_link && (
            <button onClick={() => handleCopyFilmLink(sub.film_link)} style={itemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
              🔗 Copy Film Link
            </button>
          )}
          {sub?.submission_number != null && (
            <button onClick={() => handleCopyReference(sub.submission_number)} style={itemStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
              # Copy Reference (#{String(sub.submission_number).padStart(3,'0')})
            </button>
          )}
          <button onClick={() => handleQuickArchive(contextMenu.submissionId, contextMenu.submissionTitle)} style={{ ...itemStyle, color:'var(--text-muted)' }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
            🗄 Archive
          </button>
        </div>
        );
      })()}

      {quickAssignMsg && (
        <div style={{ position:'fixed', bottom:96, right:24, background:'var(--bg-card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)', padding:'10px 16px', fontSize:13, fontWeight:600, color:'var(--text)', boxShadow:'0 8px 30px rgba(0,0,0,0.3)', zIndex:500 }}>
          {quickAssignMsg}
        </div>
      )}

      {/* Submit on Behalf of Filmmaker now triggers from the "+" icon in the
          top-right cluster (near Search/Refresh/Notifications), not a
          floating button — see the admin-notification-bell-fixed block. */}

      {showShortcuts && (
        <div style={{ position:'fixed', inset:0, background:'var(--overlay)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div className="card" style={{ width:360, padding:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ fontWeight:800, fontSize:16, color:'var(--text)' }}>Keyboard Shortcuts</h3>
              <button onClick={() => setShowShortcuts(false)} style={{ background:'var(--bg-elevated)', border:'1.5px solid var(--border)', color:'var(--text-secondary)', borderRadius:'var(--r-sm)', width:26, height:26, cursor:'pointer' }}>✕</button>
            </div>
            {SHORTCUTS_LIST.map(([label, key]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--text-secondary)' }}>{label}</span>
                <span style={{ display:'flex', gap:4 }}>
                  {key.split('  ').map(k => <code key={k} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:4, padding:'2px 8px', fontSize:12, fontFamily:'monospace', color:'var(--text)' }}>{k}</code>)}
                </span>
              </div>
            ))}
            <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:14 }}>Shortcuts are active on the Submissions tab, except while typing in a field.</p>
          </div>
        </div>
      )}
    </div>
  );
}
